<template>
  <div class="baseline-deployment-page">
    <div class="deployment-container">
      <!-- Tab Navigation for Full or Custom Deployment -->
      <div class="tab-header">
        <button
          :class="{ 'active-tab': activeTab === 'onboardnew' }"
          @click="activeTab = 'onboardnew'"
        >
          On-Board Tenant
        </button>
        <button
          :class="{ 'active-tab': activeTab === 'custom' }"
          @click="activeTab = 'custom'"
        >
          Baseline Deployment
        </button>
        <button
          :class="{ 'active-tab': activeTab === 'offboard' }"
          @click="activeTab = 'offboard'"
        >
          Off-Board Tenant
        </button>
      </div>

      <div class="tab-content-window">
        <div v-if="activeTab === 'onboardnew'" class="tab-content">
          <div class="main-content">
            <h2 class="baseline-text">Tenant Onboarding</h2>
            <p class="baseline-text">
              Configure a new Microsoft 365 tenant - validate details, set up the subscription, authenticate as Global Admin, and assign required roles.
            </p>

            <!-- Warning Banner -->
            <div v-if="showPopupWarning" class="popup-warning">
              <span class="warning-icon"><span class="icon-warning"></span></span>
              <div class="warning-text">
                <strong>Allowing Popups</strong>
                <p>
                  The onboarding process will prompt you to authenticate with
                  the Clients Microsoft Tenant, please ensure you allow popups
                  from this site. This can be done by clicking the popup icon in
                  the address bar and selecting 'Always allow popups from this
                  site'.
                </p>
              </div>
              <button class="banner-dismiss" @click="showPopupWarning = false"><span class="icon-close"></span></button>
            </div>

            <div class="admin-access-card">
              <div class="admin-access-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/><circle cx="12" cy="12" r="4"/></svg>
              </div>
              <div class="admin-access-content">
                <strong>Global Administrator Access Required</strong>
                <p>You will need to sign into an account with Global Administrator access to the Microsoft Tenant.</p>
              </div>
            </div>

            <!-- Step 0: Wizard intro / summary -->
            <div v-if="onboardStep === 0" class="wizard-panel">
              <div class="wizard-steps-summary">
                <div class="wizard-step-summary" @click="onboardStep = 1" style="cursor:pointer;">
                  <span class="wizard-step-badge">1</span>
                  <div>
                    <strong>Tenant Details</strong>
                    <p>Enter client code, domain, subscription and region</p>
                  </div>
                </div>
                <div class="wizard-step-summary">
                  <span class="wizard-step-badge">2</span>
                  <div>
                    <strong>Authenticate Global Admin</strong>
                    <p>Sign in as Global Admin to grant permissions</p>
                  </div>
                </div>
                <div class="wizard-step-summary">
                  <span class="wizard-step-badge">3</span>
                  <div>
                    <strong>Assign Roles & Finish</strong>
                    <p>Assign required Azure roles and complete onboarding</p>
                  </div>
                </div>
              </div>
              <div class="wizard-nav">
                <button @click="onboardStep = 1" class="primary-button">Get Started &rarr;</button>
              </div>
            </div>

            <!-- Step 1: Tenant Details -->
            <div v-if="onboardStep === 1" class="wizard-panel">
              <h3 class="wizard-panel-title">Step 1: Tenant Details</h3>
              <div class="form-grid-wrapper">
                <div class="form-group">
                  <label for="new-client-code">Client Code:</label>
                  <input type="text" id="new-client-code" v-model="newClientCode" @input="validateNewClientCode" :class="['onboard-input', { 'is-invalid': newClientCodeError }]" required placeholder="ABC" />
                  <div class="validation-messages">
                    <span v-if="newClientCodeError" class="error-message">{{ newClientCodeError }}</span>
                    <span v-if="newClientCodeSuccess" class="success-message">{{ newClientCodeSuccess }}</span>
                  </div>
                </div>
                <div class="form-group">
                  <label for="new-csp-subscription">Client has an 'Options IT' subscription?</label>
                  <select id="new-csp-subscription" v-model="newHasOptionsCSP" class="onboard-input">
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
                <div class="form-group">
                  <label for="power-support-enrollment">Enroll client into PowerSupport:</label>
                  <select id="power-support-enrollment" v-model="enrollInPowerSupport" class="onboard-input">
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
                <div class="form-group">
                  <label for="new-domain">Domain:</label>
                  <input type="text" id="new-domain" v-model="newDomain" class="onboard-input" required placeholder="example.com" />
                </div>
                <div class="form-group">
                  <label for="azure-region">Region:</label>
                  <select id="azure-region" v-model="selectedRegion" class="onboard-input" required>
                    <option value="" disabled selected>Select Azure Region</option>
                    <option v-for="region in regionMapping" :key="region.azureRegionAPIName" :value="region.azureRegionAPIName">{{ region.displayName }}</option>
                  </select>
                </div>
                <div></div>
                <div v-if="newHasOptionsCSP === 'no'" class="form-group full-width subscription-input">
                  <label for="new-subscription-name">Enter Subscription Name:</label>
                  <input type="text" id="new-subscription-name" v-model="newCustomSubscriptionName" class="onboard-input" required placeholder="e.g CSP-OptionsIT-M365-Business" />
                </div>
              </div>
              <div class="wizard-nav">
                <button @click="onboardStep = 0" class="btn btn-secondary">&larr; Back</button>
                <button @click="resetOnboarding" class="btn btn-secondary">Start Over</button>
                <button @click="onboardStep = 2" class="primary-button" :disabled="!isNewClientInfoValid">Next &rarr;</button>
              </div>
            </div>

            <!-- Step 2: Authenticate Admin -->
            <div v-if="onboardStep === 2" class="wizard-panel">
              <h3 class="wizard-panel-title">Authenticate Global Admin</h3>
              <div class="auth-cta-card">
                <div class="auth-cta-header">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>
                  <div>
                    <strong>Azure AD Admin Consent</strong>
                    <p>Authenticate and grant permissions to manage this tenant</p>
                  </div>
                </div>
                <div class="auth-cta-body">
                  <div class="form-group full-width">
                    <label for="tenant-id">Tenant ID</label>
                    <input type="text" id="tenant-id" v-model="clientTenantId" class="onboard-input" readonly placeholder="Will be automatically populated after authentication" />
                  </div>
                  <div v-if="clientTenantId && globalAdminToken" class="auth-success">
                    <span class="icon-check" style="color:#16a34a;"></span>
                    Authenticated - Tenant ID: {{ clientTenantId }}
                  </div>
                  <button @click="startAdminConsentFlow" class="auth-cta-button" :disabled="!isNewClientInfoValid">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                    Login as Global Admin
                  </button>
                </div>
              </div>
              <div class="wizard-nav">
                <button @click="onboardStep = 1" class="btn btn-secondary">&larr; Back</button>
                <button @click="resetOnboarding" class="btn btn-secondary">Start Over</button>
                <button @click="onboardStep = 3" class="primary-button" :disabled="!step3Valid">Next &rarr;</button>
              </div>
            </div>

            <!-- Step 3: Roles and Finish -->
            <div v-if="onboardStep === 3" class="wizard-panel">
              <h3 class="wizard-panel-title">Assign Roles & Complete</h3>
              <div v-if="globalAdminToken">
                <button @click="assignRequiredRoles" class="primary-button mt-2">
                  Assign Required Roles to "Aegis - Deploy - Monitor - Fix"
                </button>
                <div class="role-status-container mt-4">
                  <h5>Azure Subscription Roles:</h5>
                  <template v-for="(statusStr, idx) in [readerRoleStatus, policyContributorRoleStatus, ContributorRoleStatus]" :key="'subRole'+idx">
                    <div v-if="formatRoleStatus(statusStr).hasStatus" class="role-status" :class="formatRoleStatus(statusStr).success ? 'role-status-success' : 'role-status-error'">
                      <span :class="formatRoleStatus(statusStr).success ? 'icon-check' : 'icon-close'" style="margin-right:0.375rem;"></span>
                      {{ formatRoleStatus(statusStr).text }}
                    </div>
                  </template>
                  <h5 class="mt-3">Azure AD Directory Roles:</h5>
                  <template v-for="(statusStr, idx) in [teamsAdminRoleStatus, globalAdminRoleStatus, exchangeAdminRoleStatus, AuthPolicyAdminStatus, PrivAuthAdminStatus]" :key="'dirRole'+idx">
                    <div v-if="formatRoleStatus(statusStr).hasStatus" class="role-status" :class="formatRoleStatus(statusStr).success ? 'role-status-success' : 'role-status-error'">
                      <span :class="formatRoleStatus(statusStr).success ? 'icon-check' : 'icon-close'" style="margin-right:0.375rem;"></span>
                      {{ formatRoleStatus(statusStr).text }}
                    </div>
                  </template>
                </div>
                <div v-if="allRolesAssigned" class="mt-6">
                  <button @click="triggerOnboardPipeline" class="primary-button">
                    Complete Onboarding Process
                  </button>
                  <div v-if="onboardPipelineStatus.text" class="message status-info mt-4">
                    {{ onboardPipelineStatus.text }}
                  </div>
                </div>
              </div>
              <div v-else class="auth-warning">
                <span class="icon-warning"></span> Please complete Global Admin authentication in the previous step.
              </div>
              <div class="wizard-nav">
                <button @click="onboardStep = 2" class="btn btn-secondary">&larr; Back</button>
                <button @click="resetOnboarding" class="btn btn-secondary">Start Over</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Custom Deployment - REDESIGNED -->
        <div v-if="activeTab === 'custom'" class="tab-content">
          <div class="custom-deployment-container">
            <h2 class="baseline-text">Baseline Deployment Selection</h2>
            <p class="baseline-text">
              Select the required configurations for your Microsoft 365
              environment.
            </p>

            <!-- Pipeline Selection & Export -->
            <div class="settings-row">
              <div class="settings-card pipeline-section">
                <label for="pipeline-select-custom" class="section-header"><h3>Pipeline Selection</h3></label>
                <div class="pipeline-selector">
                  <select
                    v-model="selectedPipelineId"
                    id="pipeline-select-custom"
                    class="pipeline-dropdown"
                  >
                    <option
                      v-for="pipeline in pipelines"
                      :key="pipeline.id"
                      :value="pipeline.id"
                    >
                      {{ pipeline.name }} ({{ formatFolder(pipeline.folder) }})
                    </option>
                  </select>
                </div>
              </div>
              <div class="settings-card export-section">
                <div class="section-header">
                  <h3>Export Configuration Options</h3>
                </div>
                <p class="export-description">
                  Download a complete list of all available security settings and
                  configurations
                </p>

                <!-- Export Options -->
                <div class="export-options">
                  <label class="export-option-checkbox">
                    <input type="checkbox" v-model="includeRawConfig" />
                    <span>Include raw JSON configurations (detailed)</span>
                  </label>
                  <p class="export-note" v-if="includeRawConfig">
                    <span class="icon-warning"></span> Including raw configurations will take longer and produce
                    larger files
                  </p>
                </div>

                <div class="export-buttons">
                  <button
                    @click="exportToPDF"
                    class="btn btn-export"
                    :disabled="exportInProgress"
                  >
                    <span v-if="!exportInProgress"><span class="icon-pdf"></span> Export to PDF</span>
                    <span v-else><span class="icon-spinner"></span> Exporting...</span>
                  </button>
                  <button
                    @click="exportToCSV"
                    class="btn btn-export"
                    :disabled="exportInProgress"
                  >
                    <span v-if="!exportInProgress"><span class="icon-csv"></span> Export to CSV</span>
                    <span v-else><span class="icon-spinner"></span> Exporting...</span>
                  </button>
                </div>

                <div v-if="exportProgress" class="export-progress">
                  {{ exportProgress }}
                </div>
              </div>
            </div>

            <!-- Quick Actions -->
            <div class="quick-actions two-column">
              <div class="quick-action quick-action-greenfield" @click="selectPreset('greenfield')">
                <span class="quick-action-badge badge-green">New Tenant</span>
                <div class="quick-action-title">Greenfield</div>
                <div class="quick-action-desc">Complete baseline deployment for new tenants</div>
                <span class="quick-action-count">{{ greenfieldPresetCount }}</span>
              </div>
              <div class="quick-action quick-action-brownfield" @click="selectPreset('brownfield')">
                <span class="quick-action-badge badge-amber">Existing Tenant</span>
                <div class="quick-action-title">Brownfield</div>
                <div class="quick-action-desc">Safe overlay for tenants with existing configurations</div>
                <span class="quick-action-count">{{ brownfieldPresetCount }}</span>
              </div>
            </div>

            <!-- Service Tabs -->
            <div class="service-tabs">
              <button
                v-for="(options, category) in configurationCategories"
                :key="category"
                :class="[
                  'service-tab',
                  { active: activeServiceTab === category },
                ]"
                @click="activeServiceTab = category"
              >
                {{ formatCategoryName(category) }}
              </button>
            </div>

            <!-- Search and View Toggle -->
            <div v-if="!pipelineLoading" class="search-filter-bar">
              <div class="search-input-wrapper">
                <span class="icon-search search-icon"></span>
                <input type="text" v-model="searchQuery" placeholder="Search configurations..." class="search-input-field" />
                <button v-if="searchQuery" @click="searchQuery = ''" class="search-clear"><span class="icon-close"></span></button>
              </div>
              <div class="filter-toggle-group">
                <button :class="['impact-filter-btn', { active: impactFilter === 'all' }]" @click="impactFilter = 'all'">All</button>
                <button :class="['impact-filter-btn', { active: impactFilter === 'high' }]" @click="impactFilter = 'high'" title="High Impact">High</button>
                <button :class="['impact-filter-btn', { active: impactFilter === 'medium' }]" @click="impactFilter = 'medium'" title="Medium Impact">Med</button>
                <button :class="['impact-filter-btn', { active: impactFilter === 'low' }]" @click="impactFilter = 'low'" title="Low Impact">Low</button>
              </div>
              <div class="view-toggle-group">
                <button :class="['view-toggle-btn', { active: viewMode === 'card' }]" @click="viewMode = 'card'" title="Card view"><span class="icon-card-view"></span></button>
                <button :class="['view-toggle-btn', { active: viewMode === 'table' }]" @click="viewMode = 'table'" title="Table view"><span class="icon-table-view"></span></button>
              </div>
            </div>

            <!-- Category Status -->
            <div v-if="!pipelineLoading && !searchQuery && impactFilter === 'all' && categorySelectedCount > 0" class="category-status">
              <span v-if="categoryAllSelected" class="category-status-all">
                <span class="icon-check" style="color:#16a34a;"></span> All components in this category are selected
              </span>
              <span v-else class="category-status-partial">
                {{ categorySelectedCount }} of {{ (configurationCategories[activeServiceTab] || []).length }} selected in this category
              </span>
            </div>

            <!-- Configuration Cards -->
            <div v-if="viewMode === 'card' && !pipelineLoading" class="config-grid">
              <div
                v-for="option in filteredConfigs"
                :key="option"
                :class="[
                  'config-card',
                  { selected: selectedComponents.includes(option) },
                ]"
              >
                <div class="config-header">
                  <input
                    type="checkbox"
                    :value="option"
                    v-model="selectedComponents"
                    class="config-checkbox"
                  />
                  <div class="config-info">
                    <div class="config-title">{{ option }}</div>
                    <div class="config-description">
                      {{ getConfigDescription(option) }}
                    </div>
                    <div class="config-meta">
                      <span
                        :class="[
                          'impact-badge',
                          'impact-' + getConfigImpact(option).toLowerCase(),
                        ]"
                      >
                        {{ getConfigImpact(option) }} Impact
                      </span>
                      <span class="time-estimate">
                        <span class="icon-time"></span> {{ getEstimatedTime(option) }}
                      </span>
                      <span
                        v-if="getConfigDependencies(option)"
                        class="dependency-indicator"
                      >
                        &#8226; {{ getConfigDependencies(option) }}
                      </span>
                    </div>
                  </div>
                  <button
                    v-if="hasConfigMapping(option)"
                    @click="showConfigInfo(option)"
                    :disabled="loading[option]"
                    class="info-button-card"
                    :title="loading[option] ? 'Loading...' : 'View details'"
                  >
                    <span v-if="loading[option]" class="icon-spinner"></span>
                    <span v-else>ℹ️</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Table View -->
            <div v-if="viewMode === 'table' && !pipelineLoading" class="config-table-container">
              <table class="config-table">
                <thead>
                  <tr>
                    <th class="table-col-checkbox">Select</th>
                    <th>Configuration</th>
                    <th>Impact</th>
                    <th>Time</th>
                    <th>Info</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="option in filteredConfigs" :key="'row-'+option" :class="{ 'selected-row': selectedComponents.includes(option) }">
                    <td><input type="checkbox" :value="option" v-model="selectedComponents" class="config-checkbox" /></td>
                    <td>
                      <div class="table-config-name">{{ option }}</div>
                      <div class="table-config-desc">{{ getConfigDescription(option) }}</div>
                    </td>
                    <td><span :class="['impact-badge', 'impact-' + getConfigImpact(option).toLowerCase()]">{{ getConfigImpact(option) }}</span></td>
                    <td>{{ getEstimatedTime(option) }}</td>
                    <td>
                      <button v-if="hasConfigMapping(option)" @click="showConfigInfo(option)" :disabled="loading[option]" class="info-button-card" :title="loading[option] ? 'Loading...' : 'View details'">
                        <span v-if="loading[option]" class="icon-spinner"></span>
                        <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="info-icon-svg"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-if="filteredConfigs.length === 0" class="config-empty-state">
                <div class="empty-state-icon"><span class="icon-search" style="width:24px;height:24px;color:#94a3b8;"></span></div>
                <div class="empty-state-title">No configurations found</div>
                <div class="empty-state-desc">Try adjusting your search or filter criteria</div>
              </div>
            </div>

            <!-- Selection Summary & Actions -->
            <div class="action-bar">
              <div class="selection-summary">
                <span class="selection-count"
                  >{{ selectedComponents.length }} components</span
                >
                selected • Estimated time:
                <strong>{{ getTotalEstimatedTime() }}</strong>
              </div>
              <div class="action-buttons">
                <button @click="clearSelection" class="btn btn-secondary">
                  Clear All
                </button>
                <button
                  @click="deploySelectedComponents"
                  class="btn btn-primary"
                  :disabled="
                    selectedComponents.length === 0 || !selectedPipelineId
                  "
                  :title="getDeployButtonTooltip()"
                >
                  Deploy Selected Components
                </button>
              </div>
            </div>

            <!-- Status Messages -->
            <div v-if="message.text" class="message" :class="message.type">
              {{ message.text }}
            </div>
            <div v-if="pipelineStatusMessage.text" class="message status-info">
              {{ pipelineStatusMessage.text }}
            </div>

            <!-- Deployed Components -->
            <div
              v-if="deployedComponents.length > 0"
              class="deployed-components-section"
            >
              <h3>Recently Deployed Components:</h3>
              <div class="deployed-grid">
                <div
                  v-for="component in deployedComponents"
                  :key="component"
                  class="deployed-item"
                >
                  {{ component }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Add this section to your existing template where the tabs are defined -->
        <div v-if="activeTab === 'offboard'" class="tab-content">
          <div class="custom-deployment-container">
            <h2 class="baseline-text">Off-Board Tenant Configuration</h2>
            <p class="baseline-text">
              Select configurations to remove from the Microsoft 365 environment
              during tenant off-boarding.
            </p>

            <!-- Pipeline Selection -->
            <div class="pipeline-section-offboard">
              <label for="pipeline-select-offboard" class="section-header"><h3>Pipeline Selection</h3></label>
              <div class="pipeline-selector">
                <select
                  v-model="selectedOffboardPipelineId"
                  id="pipeline-select-offboard"
                  class="pipeline-dropdown"
                >
                  <option
                    v-for="pipeline in pipelines"
                    :key="pipeline.id"
                    :value="pipeline.id"
                  >
                    {{ pipeline.name }} ({{ formatFolder(pipeline.folder) }})
                  </option>
                </select>
              </div>
            </div>

            <!-- Quick Actions -->
            <div class="quick-actions">
              <div
                class="quick-action"
                @click="selectOffboardPreset('minimal')"
              >
                <div class="quick-action-icon"><span class="icon-cleanup"></span></div>
                <div class="quick-action-title">Minimal Cleanup</div>
                <div class="quick-action-desc">
                  Remove only baseline configurations
                </div>
              </div>
              <div
                class="quick-action"
                @click="selectOffboardPreset('standard')"
              >
                <div class="quick-action-icon"><span class="icon-delete"></span></div>
                <div class="quick-action-title">Standard Off-Board</div>
                <div class="quick-action-desc">
                  Complete configuration removal
                </div>
              </div>
              <div
                class="quick-action"
                @click="selectOffboardPreset('complete')"
              >
                <div class="quick-action-icon"><span class="icon-warning"></span></div>
                <div class="quick-action-title">Complete Wipe</div>
                <div class="quick-action-desc">Remove all configurations</div>
              </div>
            </div>

            <!-- Service Tabs -->
            <div class="service-tabs">
              <button
                v-for="(options, category) in offboardCategories"
                :key="category"
                :class="[
                  'service-tab',
                  { active: activeOffboardServiceTab === category },
                ]"
                @click="activeOffboardServiceTab = category"
              >
                {{ formatCategoryName(category) }}
              </button>
            </div>

            <!-- Search and View Toggle -->
            <div class="search-filter-bar">
              <div class="search-input-wrapper">
                <span class="icon-search search-icon"></span>
                <input type="text" v-model="offboardSearchQuery" placeholder="Search configurations..." class="search-input-field" />
                <button v-if="offboardSearchQuery" @click="offboardSearchQuery = ''" class="search-clear"><span class="icon-close"></span></button>
              </div>
              <div class="filter-toggle-group">
                <button :class="['impact-filter-btn', { active: offboardImpactFilter === 'all' }]" @click="offboardImpactFilter = 'all'">All</button>
                <button :class="['impact-filter-btn', { active: offboardImpactFilter === 'high' }]" @click="offboardImpactFilter = 'high'" title="High Impact">High</button>
                <button :class="['impact-filter-btn', { active: offboardImpactFilter === 'medium' }]" @click="offboardImpactFilter = 'medium'" title="Medium Impact">Med</button>
                <button :class="['impact-filter-btn', { active: offboardImpactFilter === 'low' }]" @click="offboardImpactFilter = 'low'" title="Low Impact">Low</button>
              </div>
              <div class="view-toggle-group">
                <button :class="['view-toggle-btn', { active: offboardViewMode === 'card' }]" @click="offboardViewMode = 'card'" title="Card view"><span class="icon-card-view"></span></button>
                <button :class="['view-toggle-btn', { active: offboardViewMode === 'table' }]" @click="offboardViewMode = 'table'" title="Table view"><span class="icon-table-view"></span></button>
              </div>
            </div>

            <!-- Category Status -->
            <div v-if="!offboardSearchQuery && offboardImpactFilter === 'all' && offboardCategorySelectedCount > 0" class="category-status category-status-offboard">
              <span v-if="offboardCategoryAllSelected" class="category-status-all">
                <span class="icon-check" style="color:#16a34a;"></span> All components in this category are selected for removal
              </span>
              <span v-else class="category-status-partial">
                {{ offboardCategorySelectedCount }} of {{ (offboardCategories[activeOffboardServiceTab] || []).length }} selected for removal in this category
              </span>
            </div>

            <!-- Configuration Cards -->
            <div v-if="offboardViewMode === 'card'" class="config-grid">
              <div
                v-for="option in filteredOffboardConfigs"
                :key="option"
                :class="[
                  'config-card',
                  'config-card-offboard',
                  { selected: selectedOffboardComponents.includes(option) },
                ]"
              >
                <div class="config-header">
                  <input
                    type="checkbox"
                    :value="option"
                    v-model="selectedOffboardComponents"
                    class="config-checkbox"
                  />
                  <div class="config-info">
                    <div class="config-title">{{ option }}</div>
                    <div class="config-description">
                      {{ getOffboardConfigDescription(option) }}
                    </div>
                    <div class="config-meta">
                      <span
                        :class="[
                          'impact-badge',
                          'impact-' +
                            getOffboardConfigImpact(option).toLowerCase(),
                        ]"
                      >
                        {{ getOffboardConfigImpact(option) }} Impact
                      </span>
                      <span class="time-estimate">
                        <span class="icon-time"></span> {{ getOffboardEstimatedTime(option) }}
                      </span>
                      <span
                        v-if="getOffboardConfigDependencies(option)"
                        class="dependency-indicator"
                      >
                        &#8226; {{ getOffboardConfigDependencies(option) }}
                      </span>
                    </div>
                  </div>
                  <button
                    v-if="hasOffboardConfigMapping(option)"
                    @click="showOffboardConfigInfo(option)"
                    :disabled="offboardLoading[option]"
                    class="info-button-card"
                    :title="
                      offboardLoading[option] ? 'Loading...' : 'View details'
                    "
                  >
                    <span v-if="offboardLoading[option]" class="icon-spinner"></span>
                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="info-icon-svg"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- Table View -->
            <div v-if="offboardViewMode === 'table'" class="config-table-container">
              <table class="config-table">
                <thead>
                  <tr>
                    <th class="table-col-checkbox">Select</th>
                    <th>Configuration</th>
                    <th>Impact</th>
                    <th>Time</th>
                    <th>Info</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="option in filteredOffboardConfigs" :key="'row-'+option" :class="{ 'selected-row': selectedOffboardComponents.includes(option) }">
                    <td><input type="checkbox" :value="option" v-model="selectedOffboardComponents" class="config-checkbox" /></td>
                    <td>
                      <div class="table-config-name">{{ option }}</div>
                      <div class="table-config-desc">{{ getOffboardConfigDescription(option) }}</div>
                    </td>
                    <td><span :class="['impact-badge', 'impact-' + getOffboardConfigImpact(option).toLowerCase()]">{{ getOffboardConfigImpact(option) }}</span></td>
                    <td>{{ getOffboardEstimatedTime(option) }}</td>
                    <td>
                      <button v-if="hasOffboardConfigMapping(option)" @click="showOffboardConfigInfo(option)" :disabled="offboardLoading[option]" class="info-button-card" :title="offboardLoading[option] ? 'Loading...' : 'View details'">
                        <span v-if="offboardLoading[option]" class="icon-spinner"></span>
                        <span v-else class="icon-info"></span>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-if="filteredOffboardConfigs.length === 0" class="config-empty-state">
                <div class="empty-state-icon"><span class="icon-search" style="width:24px;height:24px;color:#94a3b8;"></span></div>
                <div class="empty-state-title">No configurations found</div>
                <div class="empty-state-desc">Try adjusting your search or filter criteria</div>
              </div>
            </div>

            <!-- Warning Banner -->
            <div class="warning-banner">
              <div class="warning-icon"><span class="icon-warning"></span></div>
              <div class="warning-content">
                <strong>Warning:</strong> Off-boarding will permanently remove
                the selected configurations. Ensure you have proper backups and
                have communicated with all stakeholders before proceeding.
              </div>
            </div>

            <!-- Selection Summary & Actions -->
            <div class="action-bar">
              <div class="selection-summary">
                <span class="selection-count"
                  >{{ selectedOffboardComponents.length }} components</span
                >
                selected for removal • Estimated time:
                <strong>{{ getTotalOffboardEstimatedTime() }}</strong>
              </div>
              <div class="action-buttons">
                <button
                  @click="clearOffboardSelection"
                  class="btn btn-secondary"
                >
                  Clear All
                </button>
                <button
                  @click="confirmOffboard"
                  class="btn btn-danger"
                  :disabled="
                    selectedOffboardComponents.length === 0 ||
                    !selectedOffboardPipelineId
                  "
                >
                  Remove Selected Components
                </button>
              </div>
            </div>

            <!-- Status Messages -->
            <div
              v-if="offboardMessage.text"
              class="message"
              :class="offboardMessage.type"
            >
              {{ offboardMessage.text }}
            </div>
            <div
              v-if="offboardPipelineStatusMessage.text"
              class="message status-info"
            >
              {{ offboardPipelineStatusMessage.text }}
            </div>

            <!-- Recently Removed Components -->
            <div
              v-if="removedComponents.length > 0"
              class="deployed-components-section removed-components"
            >
              <h3>Recently Removed Components:</h3>
              <div class="deployed-grid">
                <div
                  v-for="component in removedComponents"
                  :key="component"
                  class="deployed-item removed-item"
                >
                  {{ component }}
                </div>
              </div>
            </div>
          </div>

          <!-- Confirmation Modal -->
          <div
            v-if="showOffboardConfirmation"
            class="modal-overlay"
            @click="cancelOffboard"
          >
            <div class="confirmation-modal" @click.stop>
              <h3>Confirm Off-Board Process</h3>
              <p>
                You are about to remove
                <strong>{{ selectedOffboardComponents.length }}</strong>
                configuration(s) from the tenant. This action cannot be undone.
              </p>

              <div class="components-to-remove">
                <h4>Components to be removed:</h4>
                <ul>
                  <li
                    v-for="component in selectedOffboardComponents"
                    :key="component"
                  >
                    {{ component }}
                  </li>
                </ul>
              </div>

              <div class="modal-actions">
                <button @click="cancelOffboard" class="btn btn-secondary">
                  Cancel
                </button>
                <button @click="executeOffboard" class="btn btn-danger">
                  Proceed with Removal
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="deploymentMessage.text"
          :class="['message', deploymentMessage.type]"
        >
          {{ deploymentMessage.text }}
        </div>
      </div>
    </div>

    <!-- Configuration Info Modal - MOVED OUTSIDE CATEGORY LOOP -->
    <div v-if="openInfoModal" class="modal-overlay" @click="closeInfoModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>{{ openInfoModal }}</h2>
          <button @click="closeInfoModal" class="close-button"><span class="icon-close"></span></button>
        </div>

        <div v-if="!error[openInfoModal]" class="modal-tabs">
          <button
            :class="{ active: activeModalTab === 'overview' }"
            @click="activeModalTab = 'overview'"
          >
            Overview
          </button>
          <button
            :class="{ active: activeModalTab === 'settings' }"
            @click="activeModalTab = 'settings'"
          >
            Settings Details
          </button>
          <button
            :class="{ active: activeModalTab === 'raw' }"
            @click="activeModalTab = 'raw'"
          >
            Raw Configuration
          </button>
          <button
            :class="['ai-tab', { active: activeModalTab === 'ai' }]"
            @click="activeModalTab = 'ai'"
          >
            Private Mind - AI Explanation
          </button>
        </div>

        <div class="modal-body">
          <!-- Loading State -->
          <div v-if="loading[openInfoModal]" class="loading-state">
            <div class="spinner"><span class="icon-spinner"></span></div>
            <span>Loading configuration from Azure DevOps...</span>
          </div>

          <!-- Error State -->
          <div v-else-if="error[openInfoModal]" class="error-state">
            <span><span class="icon-warning"></span> {{ error[openInfoModal] }}</span>
          </div>

          <!-- Content Tabs -->
          <div v-else-if="configDetails[openInfoModal]">
            <!-- Overview Tab -->
            <div v-if="activeModalTab === 'overview'" class="tab-content-modal">
              <div class="overview-section">
                <h3>Description</h3>
                <p>{{ configDetails[openInfoModal].description }}</p>
              </div>

              <div class="overview-grid">
                <div class="overview-item">
                  <h4>Category</h4>
                  <span class="badge">{{
                    configDetails[openInfoModal].category
                  }}</span>
                </div>
                <div class="overview-item">
                  <h4>Source Path</h4>
                  <code
                    :class="[
                      'path-code',
                      {
                        'path-code-truncated': isPathTooLong(
                          configDetails[openInfoModal].path,
                        ),
                      },
                    ]"
                    :data-full-path="configDetails[openInfoModal].path"
                    :title="configDetails[openInfoModal].path"
                    @click="
                      copyPathToClipboard(configDetails[openInfoModal].path)
                    "
                  >
                    {{ formatPath(configDetails[openInfoModal].path) }}
                  </code>
                </div>
                <div class="overview-item">
                  <h4>Impact Level</h4>
                  <span
                    :class="[
                      'badge',
                      'impact-' +
                        configDetails[openInfoModal].impact.toLowerCase(),
                    ]"
                  >
                    {{ configDetails[openInfoModal].impact }}
                  </span>
                </div>
                <div class="overview-item">
                  <h4>Configuration Type</h4>
                  <span class="badge type-badge">{{
                    configDetails[openInfoModal].type
                      .replace("_", " ")
                      .toUpperCase()
                  }}</span>
                </div>
              </div>
            </div>

            <!-- Settings Tab -->
            <div v-if="activeModalTab === 'settings'" class="tab-content-modal">
              <h3>Configuration Settings</h3>
              <div class="settings-list">
                <div
                  v-for="(setting, index) in configDetails[openInfoModal]
                    .settings"
                  :key="index"
                  class="setting-item"
                >
                  <div class="setting-header">
                    <h4>{{ setting.name }}</h4>
                    <div class="setting-value">
                      <span class="value">{{ setting.value }}</span>
                      <small v-if="setting.rawProperty" class="property">{{
                        setting.rawProperty
                      }}</small>
                    </div>
                  </div>
                  <p class="setting-description">{{ setting.description }}</p>
                </div>
              </div>
            </div>

            <!-- Raw Configuration Tab -->
            <div v-if="activeModalTab === 'raw'" class="tab-content-modal">
              <div class="raw-header">
                <h3>Raw Configuration</h3>
                <div class="raw-header-actions">
                  <button
                    v-if="getExpandableJsonCount(configDetails[openInfoModal]) > 0"
                    @click="toggleAllJsonNodes(configDetails[openInfoModal], true)"
                    class="raw-expand-btn"
                  >Expand All</button>
                  <button
                    v-if="getExpandableJsonCount(configDetails[openInfoModal]) > 0"
                    @click="toggleAllJsonNodes(configDetails[openInfoModal], false)"
                    class="raw-expand-btn"
                  >Collapse All</button>
                  <button
                    @click="copyToClipboard(configDetails[openInfoModal])"
                    class="copy-button"
                  >
                    <span class="icon-copy"></span> Copy
                  </button>
                </div>
              </div>
              <div class="console-json-viewer">
                <template v-for="(node, idx) in flattenJsonTree(configDetails[openInfoModal].rawConfig)" :key="node.path || 'leaf-' + idx">
                  <div
                    v-if="!node.hidden"
                    :style="{ paddingLeft: node.depth * 20 + 'px' }"
                    class="json-tree-row"
                  >
                    <span class="json-toggle" v-if="node.isExpandable">
                      <button
                        @click="toggleJsonNode(node.path)"
                        class="json-toggle-btn"
                        :aria-label="expandedJsonNodes[node.path] ? 'Collapse' : 'Expand'"
                      >{{ expandedJsonNodes[node.path] ? '\u25BC' : '\u25B6' }}</button>
                    </span>
                    <span v-else class="json-toggle json-toggle-spacer"></span>
                    <span class="json-key" v-if="node.key !== null">"{{ node.key }}": </span>
                    <span v-if="node.isExpandable && !expandedJsonNodes[node.path]" class="json-preview">
                      {{ jsonPreviewText(node) }}
                    </span>
                    <span v-if="node.isExpandable && expandedJsonNodes[node.path]" class="json-bracket">{{
                      node.type === 'array' ? '[' : '{'
                    }}</span>
                    <span v-if="!node.isExpandable" :class="'json-value json-' + node.type">{{ node.displayValue }}</span>
                    <span v-if="node.isExpandable && expandedJsonNodes[node.path]" class="json-bracket json-closing">{{
                      node.type === 'array' ? ']' : '}'
                    }}</span>
                  </div>
                </template>
              </div>
            </div>
            <div v-if="activeModalTab === 'ai'" class="tab-content-modal">
              <div class="ai-container">
                <!-- Header -->
                <div class="ai-header">
                  <div class="ai-header-left">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="ai-header-icon">
                      <path d="M12 2a4 4 0 0 1 4 4v2a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z"/>
                      <path d="M8 12h8M8 16h5M3 20c0-4.97 4.03-9 9-9s9 4.03 9 9"/>
                    </svg>
                    <div>
                      <h3>AI-Powered Explanation</h3>
                      <p class="ai-header-sub">Powered by Private Mind glm-5-2-nvfp4</p>
                    </div>
                  </div>
                  <button
                    v-if="!aiExplanations[openInfoModal]"
                    @click="explainConfigWithAI(openInfoModal)"
                    :disabled="aiExplaining[openInfoModal]"
                    class="ai-explain-btn"
                  >
                    <svg v-if="!aiExplaining[openInfoModal]" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                    <span v-if="aiExplaining[openInfoModal]">Analyzing...</span>
                    <span v-else>Explain Configuration</span>
                  </button>
                </div>

                <!-- Loading State -->
                <div v-if="aiExplaining[openInfoModal]" class="ai-loading">
                  <div class="ai-skeleton">
                    <div class="skeleton-block skeleton-title"></div>
                    <div class="skeleton-block skeleton-text"></div>
                    <div class="skeleton-block skeleton-text skeleton-text-short"></div>
                    <div class="skeleton-block skeleton-heading"></div>
                    <div class="skeleton-block skeleton-text"></div>
                    <div class="skeleton-block skeleton-text skeleton-text-medium"></div>
                    <div class="skeleton-block skeleton-heading"></div>
                    <div class="skeleton-block skeleton-text"></div>
                    <div class="skeleton-block skeleton-text skeleton-text-short"></div>
                  </div>
                  <p class="ai-loading-label">Private Mind AI is analyzing this configuration...</p>
                </div>

                <!-- Error State -->
                <div v-else-if="aiErrors[openInfoModal]" class="ai-error">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <p>{{ aiErrors[openInfoModal] }}</p>
                  <button
                    @click="explainConfigWithAI(openInfoModal)"
                    class="ai-retry-btn"
                  >
                    Try Again
                  </button>
                </div>

                <!-- Explanation Content -->
                <div
                  v-else-if="aiExplanations[openInfoModal]"
                  class="ai-explanation"
                >
                  <div v-if="aiCachedFrom[openInfoModal]" class="ai-cached-badge">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    Cached from {{ aiCachedFrom[openInfoModal] }}
                  </div>
                  <div class="ai-explanation-body" v-html="aiExplanations[openInfoModal]"></div>
                  <div class="ai-actions">
                    <button
                      @click="copyToClipboard({ explanation: aiExplanations[openInfoModal] })"
                      class="ai-action-btn ai-copy-btn"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                      Copy
                    </button>
                    <button
                      @click="regenerateExplanation(openInfoModal)"
                      class="ai-action-btn ai-regenerate-btn"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><polyline points="23 20 23 14 17 14"/><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/></svg>
                      Regenerate
                    </button>
                  </div>
                </div>

                <!-- Initial State -->
                <div v-else class="ai-initial-state">
                  <div class="ai-initial-icon">
                    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M12 2a4 4 0 0 1 4 4v2a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z"/>
                      <path d="M8 12h8M8 16h5"/>
                      <path d="M3 20c0-4.97 4.03-9 9-9s9 4.03 9 9"/>
                      <circle cx="19" cy="6" r="3" fill="#818cf8" fill-opacity="0.2"/>
                      <path d="M18.5 4.5l1 1 2-2" stroke="#6366f1" stroke-width="2"/>
                    </svg>
                  </div>
                  <h4>Understand Your Configuration</h4>
                  <p>Get a plain-English breakdown of what this configuration does, its key settings, and why it matters for your organization.</p>
                  <div class="ai-initial-features">
                    <div class="ai-feature-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      <span>Business-friendly explanations</span>
                    </div>
                    <div class="ai-feature-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      <span>Instant retrieval from cache</span>
                    </div>
                    <div class="ai-feature-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      <span>Regenerate anytime for updates</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { msalInstance } from "@/auth.js";
import { msalOnboardInstance } from "@/msalOnboardConfig";
import {
  runFullBaselinePipeline,
  triggerOnboardBuild,
  getPipelineStatus,
  runOffboardPipeline,
  runCustomBaselinePipeline,
  triggerNewOnboardBuild,
  fetchPipelinesWithFolderFilter,
  callAiApi,
} from "@/azure-devops-api.js";
import { mapState } from "vuex";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { getAccessToken } from "@/services/auth-utils.js";
import { registerEntraTenant, getPolicyExplanation, savePolicyExplanation } from "@/services/entra-api.js";
import { azureRegionToCspCode } from "@/services/region-utils.js";

export default {
  components: {},
  data() {
    return {
      activeServiceTab: "Microsoft Intune Configuration Options",
      timeEstimates: {
        "Create Device Compliance Policies": "30 sec",
        "Create Auto Pilot Profiles": "8 sec",
        "Create Managed App Policies": "12 sec",
        "Create iOS Apps": "5 sec",
        "Create Android Apps": "5 sec",
        "Create Deploy Intent Policies": "10 sec",
        "Create Device Management Configuration Policies": "18 sec",
        "Create Device Management - Health Scripts": "6 sec",
        "Create Device Management - Management Scripts": "10 sec",
        "Create Device Clean-Up Rules": "3 sec",
        "Enable Organization Customisation": "2 sec",
        "Set Organization Configuration Compliance": "8 sec",
        "Set Anti Phishing Policy Compliance": "12 sec",
        "Set ATP Policy Compliance - Defender for Office 365": "15 sec",
        "Set SharePoint Configuration Compliance": "10 sec",
        "Create Entra ID Service Accounts": "8 sec",
        "Create MEM Groups": "5 sec",
        "Create Required Entra ID Groups": "6 sec",
        "Create Windows Feature Update Profiles": "6 sec",
      },
      includeRawConfig: false,
      exportInProgress: false,
      exportProgress: "",

      // Off-board specific data
      activeOffboardServiceTab: "Microsoft Intune Configuration Options",
      selectedOffboardComponents: [],
      selectedOffboardPipelineId: null,
      removedComponents: [],
      showOffboardConfirmation: false,
      offboardLoading: {},
      offboardMessage: { text: "", type: "" },
      offboardPipelineStatusMessage: { text: "", type: "" },

      // Add dependency mapping
      dependencies: {
        "Create Auto Pilot Profiles": "Requires Device Registration",
        "Create Conditional Access - Policies": "Requires Named Locations",
        // Add more dependencies as needed
      },
      activeTab: "onboardnew",
      showPopupWarning: true,
      searchQuery: "",
      impactFilter: "all",
      viewMode: "card",
      pipelineLoading: false,
      offboardSearchQuery: "",
      offboardImpactFilter: "all",
      offboardViewMode: "card",
      onboardStep: 0,
      activeModalTab: "overview",
      expandedJsonNodes: {},
      showOnboardForm: true,
      showPipelineList: false,
      selectedComponents: [],
      deployedComponents: [],
      deploymentMessage: { text: "", type: "" },
      pipelines: [],
      selectedPipelineId: null,
      pipelineMessage: { text: "", type: "" },
      clientCode: "",
      domain: "",
      username: "",
      password: "",
      formError: false,
      invalidFields: [],
      message: { text: "", type: "" },
      clientCodeError: "",
      clientCodeSuccess: "",
      clientCodeValidated: false,
      hasOptionsCSP: "yes",
      adminConsentUrl:
        "https://login.microsoftonline.com/common/adminconsent?client_id=9d4e9287-a121-4937-b94d-e7be78d8d334&redirect_uri=https://www.cyaniccloud.com",
      customSubscriptionName: "",
      pipelineStatusMessage: { text: "", type: "" },
      clientTenantId: null,

      configurationMapping: {
        // ==================== Microsoft Entra ID Configuration Options ====================
        "Create Entra ID Service Accounts": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/AAD_RequiredUsers",
          type: "directory",
          description:
            "Creates required service accounts in Azure Active Directory for system operations",
          category: "Azure AD Configuration",
          impact: "High",
        },

        "Create MEM Groups": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/AAD_Groups",
          type: "directory",
          description:
            "Creates Microsoft Endpoint Manager (MEM) groups for device and app management",
          category: "Azure AD Configuration",
          impact: "Medium",
        },

        "Create Required Entra ID Groups": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/AAD_Required_Onboarding_Groups",
          type: "directory",
          description:
            "Creates essential security and administrative groups required for tenant onboarding",
          category: "Azure AD Configuration",
          impact: "High",
        },

        "Set AAD Unified Group Settings": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/AzureADDirectorySetting/Group.Unified.json",
          type: "single_file",
          description:
            "Configures unified group settings for Microsoft 365 groups",
          category: "Azure AD Configuration",
          impact: "Medium",
        },

        "Set AAD Consent Policy Settings": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/AzureADDirectorySetting/Consent Policy Settings.json",
          type: "single_file",
          description:
            "Manages user and admin consent policies for applications",
          category: "Azure AD Configuration",
          impact: "High",
        },

        "Set AAD Password Rule Settings": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/AzureADDirectorySetting/Password Rule Settings.json",
          type: "single_file",
          description:
            "Configures password complexity and security requirements",
          category: "Azure AD Configuration",
          impact: "High",
        },

        "Set AAD User Consent Settings": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/UserConsentSettings",
          type: "directory",
          description: "Controls what applications users can consent to",
          category: "Azure AD Configuration",
          impact: "High",
        },

        "Set AAD Admin Consent Settings": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/AdminConsentSettings",
          type: "directory",
          description:
            "Manages administrative consent workflow for applications",
          category: "Azure AD Configuration",
          impact: "High",
        },

        "Set AAD Admin Center Settings": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/AzureADAdminCenterSettings/AdminCenterSettings.json",
          type: "single_file",
          description:
            "Configures Azure AD admin center display and functionality settings",
          category: "Azure AD Configuration",
          impact: "Low",
        },

        "Create Device Registration Policy Settings": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/DeviceSettings",
          type: "directory",
          description:
            "Manages device registration and join policies for the organization",
          category: "Azure AD Configuration",
          impact: "Medium",
        },

        "Create Conditional Access - Named Locations": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/ConditionalAccess/NamedLocations",
          type: "directory",
          description:
            "Defines trusted network locations for conditional access policies",
          category: "Azure AD Configuration",
          impact: "High",
        },

        "Create Conditional Access - Policies": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/ConditionalAccess/ConditionalAccessPolicies",
          type: "directory",
          description:
            "Creates conditional access policies to control access based on user, device, and location conditions",
          category: "Azure AD Configuration",
          impact: "High",
        },

        "Set Azure AD Policy Baseline": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzurePolicy/DefinitionPolicies/Options Azure Policy Baseline.json",
          type: "single_file",
          description:
            "Applies baseline Azure AD policy definitions for security and compliance",
          category: "Azure AD Configuration",
          impact: "High",
        },

        // ==================== MICROSOFT ENTRA ID SECURITY OPTIONS ====================

        "Enable Email Authentication method": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/AuthMethodConfigurations/Email.json",
          type: "single_file",
          description:
            "Email OTP sends a code to a user's email account which is then used to authenticate. For members of a tenant, email OTP is usable only for Self-Service Password Recovery. It may also be configured to be used for sign-in by guest users. ",
          category: "Entra ID Configuration",
          impact: "High",
        },

        "Enable Federated Identity Credential Authentication method": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/AuthMethodConfigurations/FederatedIdentityCredential.json",
          type: "single_file",
          description:
            "Federated identity credentials allow users to access multiple applications and domains using a single set of credentials, linking their identity across various identity management systems.",
          category: "Entra ID Configuration",
          impact: "High",
        },

        "Enable Fido2 Authentication method": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/AuthMethodConfigurations/Fido2.json",
          type: "single_file",
          description:
            "Passkeys are a phishing-resistant, standards-based passwordless authentication method available from a variety of vendors.  Passkeys are not usable in the Self-Service Password Reset flow.",
          category: "Entra ID Configuration",
          impact: "High",
        },

        "Enable Hardware Oath Authentication method": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/AuthMethodConfigurations/HardwareOath.json",
          type: "single_file",
          description:
            "Hardware OATH tokens are physical devices that use the OATH TOTP standard and a secret key to generate 6-digit codes used to authenticate. This policy control specifically manages the ability to register and use Hardware OATH tokens.",
          category: "Entra ID Configuration",
          impact: "High",
        },

        "Enable Microsoft Authenticator Authentication method": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/AuthMethodConfigurations/MicrosoftAuthenticator.json",
          type: "single_file",
          description:
            "The Microsoft Authenticator app is a flagship authentication method, usable in passwordless or simple push notification approval modes. The app is free to download and use on Android/iOS mobile devices.",
          impact: "High",
        },

        "Enable SMS Authentication method": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/AuthMethodConfigurations/Sms.json",
          type: "single_file",
          description:
            "This authentication method delivers a one-time code via SMS to a user's phone, and the user then inputs that code to sign-in.  SMS is usable for multi-factor authentication and Self-Service Password Reset; it can also be configured to be used as a first factor.",
          impact: "High",
        },

        "Enable Software Oath Authentication method": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/AuthMethodConfigurations/SoftwareOath.json",
          type: "single_file",
          description:
            "Software OATH tokens are applications that use the OATH TOTP standard and a secret key to generate 6-digit codes used to authenticate. This policy control manages the ability to register and use any authenticator app including Microsoft Authenticator to manage software OATH tokens. Software OATH tokens can be used for multi-factor authentication and self-service password reset flows.  Software OATH token is not usable as a first-factor authentication method.",
          impact: "High",
        },

        "Enable Temporary Access Pass Authentication method": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/AuthMethodConfigurations/TemporaryAccessPass.json",
          type: "single_file",
          description:
            "Temporary Access Pass, or TAP, is a time-limited or limited-use passcode that can be used by users for bootstrapping new accounts, account recovery, or when other auth methods are unavailable. TAP is issuable only by administrators, and is seen by the system as strong authentication. It is not usable for Self Service Password Reset.",
          impact: "High",
        },

        "Enable Voice Authentication method": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/AuthMethodConfigurations/Voice.json",
          type: "single_file",
          description:
            "This authentication method places a phone call to a user which the user must then approve using the telephone keypad.  Voice call is not usable as a first-factor authentication method.",
          impact: "High",
        },

        "Enable X509 Certificate Authentication method": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/AuthMethodConfigurations/X509Certificate.json",
          type: "single_file",
          description:
            "Certificate-based authentication is a passwordless, phising-resistant authentication method that uses x.509 certificates and an enterprise public key infrastructure (PKI) for authentication.",
          impact: "High",
        },

        "Enable QR Code Pin Authentication method": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/AuthMethodConfigurations/QRCodePin.json",
          type: "single_file",
          description:
            "QR code authentication is a simple and fast authentication method for frontline workers. To use it, you need both a QR code and PIN.",
          impact: "High",
        },

        // ==================== MICROSOFT INTUNE CONFIGURATION OPTIONS ====================
        "Create Device Compliance Policies": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Intune/CompliancePolicies",
          type: "directory",
          description:
            "Creates device compliance policies to ensure only secure and managed devices can access corporate resources",
          category: "Microsoft Intune Configuration",
          impact: "High",
        },

        "Create Android Device Owner Enrollment Profiles": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Intune/AndroidDeviceOwnerProfiles",
          type: "directory",
          description:
            "Creates enrollment profiles for Android devices in device owner mode",
          category: "Microsoft Intune Configuration",
          impact: "Medium",
        },

        "Create Auto Pilot Profiles": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Intune/AutoPilotProfiles",
          type: "directory",
          description:
            "Creates Windows Autopilot deployment profiles for automated device setup",
          category: "Microsoft Intune Configuration",
          impact: "Medium",
        },

        "Create Managed App Policies": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Intune/ManageAppPolicies",
          type: "directory",
          description:
            "Creates mobile application management (MAM) policies to protect corporate data in apps",
          category: "Microsoft Intune Configuration",
          impact: "High",
        },

        "Create iOS Apps": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Intune/MobileApps/iOS/packageIds",
          type: "directory",
          description:
            "Configures iOS applications for deployment through Intune",
          category: "Microsoft Intune Configuration",
          impact: "Medium",
        },

        "Create Android Apps": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Intune/MobileApps/Android/packageIds/GooglePlayPackageIds.json",
          type: "single_file",
          description:
            "Configures Android applications from Google Play Store for deployment through Intune",
          category: "Microsoft Intune Configuration",
          impact: "Medium",
        },

        "Create Deploy Intent Policies": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Intune/DeviceManagement/IntentPolicies",
          type: "directory",
          description:
            "Creates security baseline and configuration intent policies for devices",
          category: "Microsoft Intune Configuration",
          impact: "High",
        },

        "Create Device Management Configuration Policies": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Intune/DeviceManagement/ConfigurationPolicies",
          type: "directory",
          description:
            "Creates device configuration policies to manage device settings and features",
          category: "Microsoft Intune Configuration",
          impact: "High",
        },

        "Create Device Management Device Configurations Policies": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Intune/DeviceManagement/DeviceConfigurations",
          type: "directory",
          description:
            "Creates device configuration policies for comprehensive device management",
          category: "Microsoft Intune Configuration",
          impact: "High",
        },

        "Create Device Management - Health Scripts": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Intune/DeviceManagement/DeviceHealthScripts",
          type: "directory",
          description:
            "Deploys PowerShell scripts to monitor and maintain device health",
          category: "Microsoft Intune Configuration",
          impact: "Medium",
        },

        "Create Windows Feature Update Profiles": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Intune/DeviceManagement/WindowsFeatureUpdateProfiles/Baseline - Windows Update - 25H2.json",
          type: "single_file",
          description:
            "Deploys Windows Feature Update profiles for managed devices",
          category: "Microsoft Intune Configuration",
          impact: "Medium",
        },

        "Create Device Management - Management Scripts": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Intune/DeviceManagement/DeviceManagementScripts",
          type: "directory",
          description:
            "Deploys PowerShell scripts for device management tasks and configurations",
          category: "Microsoft Intune Configuration",
          impact: "Medium",
        },

        "Create Device Clean-Up Rules": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Intune/DeviceManagement/DeviceCleanUpRules/Days.json",
          type: "single_file",
          description:
            "Configures automatic cleanup rules for inactive devices",
          category: "Microsoft Intune Configuration",
          impact: "Medium",
        },

        // ==================== EXCHANGE ONLINE CONFIGURATION OPTIONS ====================
        "Enable Organization Customisation": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          type: "function",
          functionName: "Check-And-Enable-OrganizationCustomization",
          command: "Enable-OrganizationCustomization",
          description:
            "Enables organization customization by running the Enable-OrganizationCustomization PowerShell command",
          category: "Exchange Online Configuration",
          impact: "Medium",
        },

        "Set Organization Configuration Compliance": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureAD/OrganisationConfiguration/OrganisationalConfig.json",
          type: "single_file",
          description:
            "Configures essential security and compliance settings for your Exchange Online organization",
          category: "Exchange Online Configuration",
          impact: "High",
        },

        "Set Anti Phishing Policy Compliance": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Exchange/AntiPhishPolicy/Office365 AntiPhish Default.json",
          type: "single_file",
          description:
            "Implements advanced anti-phishing protection to safeguard against email-based threats and impersonation attacks",
          category: "Exchange Online Configuration",
          impact: "High",
        },

        "Set ATP Policy Compliance - Defender for Office 365": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Defender/Office_365/Default.json",
          type: "single_file",
          description:
            "Configures Advanced Threat Protection policies for comprehensive email security using Microsoft Defender for Office 365",
          category: "Exchange Online Configuration",
          impact: "High",
        },

        "Set Remote Domain Compliance": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Exchange/RemoteDomains/Default.json",
          type: "single_file",
          description:
            "Manages remote domain settings and external email policies to control email flow and security",
          category: "Exchange Online Configuration",
          impact: "Medium",
        },

        "Set Sharing Policy Compliance": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Exchange/SharingPolicy/Default Sharing Policy.json",
          type: "single_file",
          description:
            "Controls calendar and contact sharing with external organizations and users",
          category: "Exchange Online Configuration",
          impact: "Medium",
        },

        "Set Malware Filter Policy Compliance": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Exchange/MalwareFilterPolicies/Default.json",
          type: "single_file",
          description:
            "Configures malware detection and filtering policies to protect against malicious attachments",
          category: "Exchange Online Configuration",
          impact: "High",
        },

        "Set Hosted Outbound Spam Filter Policy Compliance": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Exchange/SpamFilterPolicies/Default.json",
          type: "single_file",
          description:
            "Configures outbound spam filtering policies to prevent the organization from being blacklisted",
          category: "Exchange Online Configuration",
          impact: "High",
        },

        "Set OWA Mailbox Policy Compliance": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Exchange/OwaMailboxPolicies/OwaMailboxPolicy-Default.json",
          type: "single_file",
          description:
            "Configures Outlook Web App (OWA) mailbox policies to control user access to features and functionality",
          category: "Exchange Online Configuration",
          impact: "Medium",
        },

        "Set Transport Rule Compliance": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          type: "multiple_files",
          paths: [
            "/M365Config/Resources/Exchange/TransportRules/Baseline - Bypass Spam Filtering from Options WAN IPs.json",
            "/M365Config/Resources/Exchange/TransportRules/Baseline - Client External Rules Forwarding Block control.json",
            "/M365Config/Resources/Exchange/TransportRules/Baseline - External Email Banner Warning.json",
          ],
          description:
            "Configures transport rules for email security, compliance, and external email handling",
          category: "Exchange Online Configuration",
          impact: "High",
        },

        "Set Admin Audit Log Enabled": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Exchange/AdminAuditLogConfig",
          type: "directory",
          description:
            "Enables and configures administrator audit logging for Exchange Online compliance tracking",
          category: "Exchange Online Configuration",
          impact: "High",
        },

        "Set Safe Links Policy": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Exchange/SafeLinksPolicy",
          type: "directory",
          description:
            "Configures Safe Links policies to protect against malicious URLs in emails and documents",
          category: "Exchange Online Configuration",
          impact: "High",
        },

        "Import Hosted Connection Filter Policy - US": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Exchange/HostedConnectionFilterPolicy/US/Default.json",
          type: "single_file",
          description:
            "Imports connection filter policy optimized for US-based IP ranges and spam sources",
          category: "Exchange Online Configuration",
          impact: "Medium",
        },

        "Import Hosted Connection Filter Policy - EU": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Exchange/HostedConnectionFilterPolicy/EU/Default.json",
          type: "single_file",
          description:
            "Imports connection filter policy optimized for EU-based IP ranges and spam sources",
          category: "Exchange Online Configuration",
          impact: "Medium",
        },

        "Create 'Inbound O365 Mail Lockdown EU' Connector": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Exchange/InboundConnectors/EU/Inbound O365 Mail Lockdown EU.json",
          type: "single_file",
          description:
            "Creates inbound connector for secure email routing from EU-based mail security appliances",
          category: "Exchange Online Configuration",
          impact: "High",
        },

        "Create 'Inbound O365 Mail Lockdown US' Connector": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Exchange/InboundConnectors/US/Inbound O365 Mail Lockdown US.json",
          type: "single_file",
          description:
            "Creates inbound connector for secure email routing from US-based mail security appliances",
          category: "Exchange Online Configuration",
          impact: "High",
        },

        // ==================== SHAREPOINT CONFIGURATION OPTIONS ====================
        "Set SharePoint Configuration Compliance": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/SharePoint/TenantSettings/SharePointTenantSettings.json",
          type: "single_file",
          description:
            "Configures SharePoint Online tenant settings for security, sharing, and compliance requirements",
          category: "SharePoint Configuration",
          impact: "High",
        },

        // ==================== TENANT ORGANISATIONAL CONFIGURATION OPTIONS ====================
        "Set Microsoft Forms Organisational Settings": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Forms/MicrosoftFormSettings/AdminFormSettings.json",
          type: "single_file",
          description:
            "Configures Microsoft Forms organizational settings and security policies",
          category: "Tenant Organisational Configuration",
          impact: "Medium",
        },

        "Set Microsoft Teams Client Configuration Settings": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Teams/TeamsClientConfiguration/Global.json",
          type: "single_file",
          description:
            "Configures Microsoft Teams client settings and user experience policies",
          category: "Tenant Organisational Configuration",
          impact: "Medium",
        },

        "Set Microsoft Teams Tenant Federation Configuration Settings": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/Teams/TenantFederationSettings/Global.json",
          type: "single_file",
          description:
            "Configures Microsoft Teams federation settings for external communication and collaboration",
          category: "Tenant Organisational Configuration",
          impact: "High",
        },

        // ==================== NETSKOPE CONFIGURATION OPTIONS ====================

          "Import Netskope Url Lists": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/Netskope/Baseline/URL_Lists",
          type: "directory",
          description:
            "Creates and deploys Netskope URL lists",
          category: "Netskope Configuration Options",
          impact: "Medium",
        },

        "Import Netskope Custom Categories": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/Netskope/Baseline/CustomCategories",
          type: "directory",
          description:
            "Creates and deploys Netskope Custom Categories",
          category: "Netskope Configuration Options",
          impact: "Medium",
        },



        // ==================== AZURE MAINTENANCE CONFIGURATIONS ====================
        "Create Phase 0, 1, 2 Maintenance Configurations": {
          repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
          path: "/M365Config/Resources/AzureMaintenance/Configurations",
          type: "directory",
          description:
            "Creates phased maintenance configurations for Azure resource update management",
          category: "Azure Maintenance Configurations",
          impact: "Medium",
        },
      },

      // Off-board configuration mapping (for details)
      offboardConfigurationMapping: {
        // Microsoft Intune Configuration Options
        "Remove Device Compliance Policies": {
          description:
            "Removes all baseline device compliance policies from the tenant",
          impact: "High",
          estimatedTime: "3-5 minutes",
          dependencies: "Ensure devices have alternative compliance policies",
        },
        "Remove Android Device Owner Enrollment Profiles": {
          description: "Removes Android device owner enrollment profiles",
          impact: "Medium",
          estimatedTime: "2-3 minutes",
          dependencies: "Verify no new Android devices pending enrollment",
        },
        "Remove Auto Pilot Profiles": {
          description: "Removes Windows Autopilot deployment profiles",
          impact: "Medium",
          estimatedTime: "2-3 minutes",
          dependencies: "Ensure no devices are currently using Autopilot",
        },
        "Remove Managed App Policies": {
          description: "Removes mobile application management (MAM) policies",
          impact: "High",
          estimatedTime: "3-5 minutes",
          dependencies: "Verify data protection requirements are met",
        },
        "Remove Deploy Intent Policies": {
          description:
            "Removes security baseline and configuration intent policies",
          impact: "High",
          estimatedTime: "3-5 minutes",
          dependencies: "Ensure alternative security policies exist",
        },
        "Remove Device Management Configuration Policies": {
          description: "Removes device configuration policies",
          impact: "High",
          estimatedTime: "5-7 minutes",
          dependencies: "Verify device management requirements",
        },
        "Remove Device Management Device Configurations Policies": {
          description: "Removes all device configuration profiles",
          impact: "High",
          estimatedTime: "5-7 minutes",
          dependencies: "Ensure devices have necessary settings",
        },
        "Remove Device Management - Health Scripts": {
          description: "Removes PowerShell health monitoring scripts",
          impact: "Medium",
          estimatedTime: "2-3 minutes",
          dependencies: "Alternative monitoring solutions should be in place",
        },
        "Remove Windows Feature Update Profiles": {
          description: "Removes Windows Feature Update profiles",
          impact: "Medium",
          estimatedTime: "2-3 minutes",
          dependencies: "Verify feature update schedules are not active",
        },
        "Remove Device Management - Management Scripts": {
          description: "Removes PowerShell management scripts",
          impact: "Medium",
          estimatedTime: "2-3 minutes",
          dependencies: "Verify script functionality is not critical",
        },
        "Remove Conditional Access - Policies": {
          description: "Removes baseline conditional access policies",
          impact: "High",
          estimatedTime: "5-7 minutes",
          dependencies: "Ensure security requirements are maintained",
        },
        "Remove Conditional Access - Named Locations": {
          description: "Removes trusted network locations",
          impact: "High",
          estimatedTime: "2-3 minutes",
          dependencies: "Update dependent conditional access policies",
        },
        "Disable Device Clean-Up Rules": {
          description: "Disables automatic inactive device cleanup",
          impact: "Low",
          estimatedTime: "1-2 minutes",
          dependencies: "Implement manual device management process",
        },

        // Exchange Online Configuration Options

        "Revert Sharing Policy to Default": {
          description: "Removes custom calendar/contact sharing restrictions",
          impact: "Medium",
          estimatedTime: "2-3 minutes",
          dependencies: "Review sharing requirements",
        },
        "Revert Malware Filter Policy to Default": {
          description: "Removes custom malware filtering rules",
          impact: "High",
          estimatedTime: "2-3 minutes",
          dependencies: "Ensure malware protection remains",
        },
        "Revert Hosted Outbound Spam Filter Policy to Default": {
          description: "Removes outbound spam filtering customizations",
          impact: "High",
          estimatedTime: "2-3 minutes",
          dependencies: "Monitor for spam issues",
        },
        "Revert OWA Mailbox Policy to Default": {
          description: "Removes OWA access restrictions",
          impact: "Medium",
          estimatedTime: "2-3 minutes",
          dependencies: "Review OWA security requirements",
        },
        "Remove Transport Rules": {
          description: "Removes all baseline transport rules",
          impact: "High",
          estimatedTime: "3-5 minutes",
          dependencies: "Document rule functionality",
        },
        "Disable Admin Audit Logging": {
          description: "Disables administrator audit logging",
          impact: "High",
          estimatedTime: "1-2 minutes",
          dependencies: "Ensure compliance requirements are met",
        },
        "Remove Safe Links Policy": {
          description: "Removes URL protection policies",
          impact: "High",
          estimatedTime: "2-3 minutes",
          dependencies: "Alternative link protection needed",
        },
        "Remove Hosted Connection Filter Policy - US": {
          description: "Removes US-specific connection filtering",
          impact: "Medium",
          estimatedTime: "2-3 minutes",
          dependencies: "Review IP allowlist requirements",
        },
        "Remove Hosted Connection Filter Policy - EU": {
          description: "Removes EU-specific connection filtering",
          impact: "Medium",
          estimatedTime: "2-3 minutes",
          dependencies: "Review IP allowlist requirements",
        },
        "Remove 'Inbound O365 Mail Lockdown EU' Connector": {
          description: "Removes secure mail routing for EU",
          impact: "High",
          estimatedTime: "2-3 minutes",
          dependencies: "Ensure mail flow continuity",
        },
        "Remove 'Inbound O365 Mail Lockdown US' Connector": {
          description: "Removes secure mail routing for US",
          impact: "High",
          estimatedTime: "2-3 minutes",
          dependencies: "Ensure mail flow continuity",
        },

        // Microsoft Entra ID Configuration Options
        "Remove MEM Groups": {
          description: "Removes all MEM management groups",
          impact: "Medium",
          estimatedTime: "3-5 minutes",
          dependencies: "Ensure devices are managed alternatively",
        },
        "Remove Required Entra ID Groups": {
          description: "Removes baseline security groups",
          impact: "High",
          estimatedTime: "3-5 minutes",
          dependencies: "Review group membership requirements",
        },

        // Tenant Organisational Configuration Options
        "Revert Microsoft Forms Organisational Settings": {
          description: "Reverts Forms settings to default",
          impact: "Low",
          estimatedTime: "1-2 minutes",
          dependencies: "Review Forms usage policies",
        },
        "Revert Microsoft Teams Client Configuration": {
          description: "Reverts Teams client settings",
          impact: "Medium",
          estimatedTime: "2-3 minutes",
          dependencies: "Review Teams policies",
        },
        "Revert Microsoft Teams Tenant Federation Configuration": {
          description: "Removes Teams external access restrictions",
          impact: "High",
          estimatedTime: "2-3 minutes",
          dependencies: "Review external collaboration needs",
        },

        // Azure AD Policy Definition Configuration Options
        "Remove Azure AD Policy Baseline": {
          description: "Removes all baseline Azure policies",
          impact: "High",
          estimatedTime: "3-5 minutes",
          dependencies: "Ensure compliance requirements",
        },

        // Azure Maintenance Configurations Options
        "Remove Phase 0, 1, 2 Maintenance Configurations": {
          description: "Removes phased update configurations",
          impact: "Medium",
          estimatedTime: "3-5 minutes",
          dependencies: "Plan for update management",
        },

        // Entra ID - User Account Removal
        "Remove Entra ID Service Accounts": {
          description:
            "Removes all service accounts created during onboarding e.g JIT Admin, Universal Print",
          impact: "High",
          estimatedTime: "5-7 minutes",
          dependencies: "Critical - verify service dependencies",
        },
      },

      // New fields for On-Board Tenant (New) tab
      newClientCode: "",
      newClientCodeError: "",
      newClientCodeSuccess: "",
      newClientCodeValidated: false,
      newHasOptionsCSP: "yes",
      newCustomSubscriptionName: "",
      newDomain: "",
      enrollInPowerSupport: "yes",

      // Global Admin section
      globalAdminToken: null,
      onboardAccount: null,

      // Role assignment status fields
      readerRoleStatus: "",
      policyContributorRoleStatus: "",
      ContributorRoleStatus: "",
      teamsAdminRoleStatus: "",
      globalAdminRoleStatus: "",
      exchangeAdminRoleStatus: "",
      AuthPolicyAdminStatus: "",
      PrivAuthAdminStatus: "",
      allRolesAssigned: false,

      // Pipeline status for the new onboarding process
      onboardPipelineStatus: { text: "", type: "" },

      configurationCategories: {
        "Microsoft Intune Configuration Options": [
          "Create Device Compliance Policies",
          "Create Android Device Owner Enrollment Profiles",
          "Create Auto Pilot Profiles",
          "Create Managed App Policies",
          "Create iOS Apps",
          "Create Android Apps",
          "Create Deploy Intent Policies",
          "Create Device Management Configuration Policies",
          "Create Device Management Device Configurations Policies",
          "Create Device Management - Health Scripts",
          "Create Device Management - Management Scripts",
          "Create Windows Feature Update Profiles",
          "Create Conditional Access - Policies",
          "Create Conditional Access - Named Locations",
          "Create Device Clean-Up Rules",
          "Create Device Registration Policy Settings",
        ],
        "Exchange Online Configuration Options": [
          "Enable Organization Customisation",
          "Set Organization Configuration Compliance",
          "Set Anti Phishing Policy Compliance",
          "Set ATP Policy Compliance - Defender for Office 365",
          "Set Remote Domain Compliance",
          "Set Sharing Policy Compliance",
          "Set Malware Filter Policy Compliance",
          "Set Hosted Outbound Spam Filter Policy Compliance",
          "Set OWA Mailbox Policy Compliance",
          "Set Transport Rule Compliance",
          "Set Admin Audit Log Enabled",
          "Set Safe Links Policy",
          "Import Hosted Connection Filter Policy - US",
          "Import Hosted Connection Filter Policy - EU",
          "Create 'Inbound O365 Mail Lockdown EU' Connector",
          "Create 'Inbound O365 Mail Lockdown US' Connector",
        ],
        "SharePoint Configuration Options": [
          "Set SharePoint Configuration Compliance",
        ],
        "Microsoft Entra ID Configuration Options": [
          "Create MEM Groups",
          "Create Required Entra ID Groups",
          "Set AAD Unified Group Settings",
          "Set AAD Consent Policy Settings",
          "Set AAD Password Rule Settings",
          "Set AAD User Consent Settings",
          "Set AAD Admin Consent Settings",
          "Set AAD Admin Center Settings",
        ],
        "Microsoft Entra ID Security Options": [
          "Enable Email Authentication method",
          "Enable Federated Identity Credential Authentication method",
          "Enable Fido2 Authentication method",
          "Enable Hardware Oath Authentication method",
          "Enable Microsoft Authenticator Authentication method",
          "Enable SMS Authentication method",
          "Enable Software Oath Authentication method",
          "Enable Temporary Access Pass Authentication method",
          "Enable Voice Authentication method",
          "Enable X509 Certificate Authentication method",
          "Enable QR Code Pin Authentication method",
        ],
          "Netskope Configuration Options": [
          "Import Netskope Url Lists",
          "Import Netskope Custom Categories",
        ],
        "Tenant Organisational Configuration Options": [
          "Set Microsoft Forms Organisational Settings",
          "Set Microsoft Teams Client Configuration Settings",
          "Set Microsoft Teams Tenant Federation Configuration Settings",
        ],
        "Azure AD Policy Definition Configuration Options": [
          "Set Azure AD Policy Baseline",
        ],
        "Azure Maintenance Configurations Options": [
          "Create Phase 0, 1, 2 Maintenance Configurations",
        ],
        "Entra ID - User Account Creation": [
          "Create Entra ID Service Accounts",
        ],
      },

      offboardCategories: {
        "Microsoft Intune Configuration Options": [
          "Remove Device Compliance Policies",
          "Remove Android Device Owner Enrollment Profiles",
          "Remove Auto Pilot Profiles",
          "Remove Managed App Policies",
          "Remove Deploy Intent Policies",
          "Remove Device Management Configuration Policies",
          "Remove Device Management Device Configurations Policies",
          "Remove Device Management - Health Scripts",
          "Remove Device Management - Management Scripts",
          "Remove Windows Feature Update Profiles",
          "Remove Conditional Access - Policies",
          "Remove Conditional Access - Named Locations",
          "Disable Device Clean-Up Rules",
        ],
        "Exchange Online Configuration Options": ["Remove Transport Rules"],
        "Microsoft Entra ID Configuration Options": [
          "Remove MEM Groups",
          "Remove Required Entra ID Groups",
        ],
        "Entra ID - User Account Removal": ["Remove Entra ID Service Accounts"],
      },

      // Complete Azure regions mapping
      regionMapping: [
        {
          azureRegionAPIName: "eastus",
          displayName: "East US",
          shortCode: "eus",
          timeZone: "Eastern Standard Time",
          physicalLocation: "Virginia",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "US",
        },
        {
          azureRegionAPIName: "eastus2",
          displayName: "East US 2",
          shortCode: "eus2",
          timeZone: "Eastern Standard Time",
          physicalLocation: "Virginia",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "US",
        },
        {
          azureRegionAPIName: "centralus",
          displayName: "Central US",
          shortCode: "cus",
          timeZone: "Central Standard Time",
          physicalLocation: "Iowa",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "US",
        },
        {
          azureRegionAPIName: "northcentralus",
          displayName: "North Central US",
          shortCode: "ncus",
          timeZone: "Central Standard Time",
          physicalLocation: "Illinois",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "US",
        },
        {
          azureRegionAPIName: "southcentralus",
          displayName: "South Central US",
          shortCode: "scus",
          timeZone: "Central Standard Time",
          physicalLocation: "Texas",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "US",
        },
        {
          azureRegionAPIName: "westcentralus",
          displayName: "West Central US",
          shortCode: "wcus",
          timeZone: "Mountain Standard Time",
          physicalLocation: "Wyoming",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "US",
        },
        {
          azureRegionAPIName: "westus",
          displayName: "West US",
          shortCode: "wus",
          timeZone: "Pacific Standard Time",
          physicalLocation: "California",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "US",
        },
        {
          azureRegionAPIName: "westus2",
          displayName: "West US 2",
          shortCode: "wus2",
          timeZone: "Pacific Standard Time",
          physicalLocation: "Washington",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "US",
        },
        {
          azureRegionAPIName: "westus3",
          displayName: "West US 3",
          shortCode: "wus3",
          timeZone: "Pacific Standard Time",
          physicalLocation: "Phoenix",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "US",
        },
        {
          azureRegionAPIName: "canadaeast",
          displayName: "Canada East",
          shortCode: "cae",
          timeZone: "Eastern Standard Time",
          physicalLocation: "Quebec",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "Canada",
        },
        {
          azureRegionAPIName: "canadacentral",
          displayName: "Canada Central",
          shortCode: "cac",
          timeZone: "Eastern Standard Time",
          physicalLocation: "Toronto",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Canada",
        },
        {
          azureRegionAPIName: "brazilsouth",
          displayName: "Brazil South",
          shortCode: "brs",
          timeZone: "E. South America Standard Time",
          physicalLocation: "Sao Paulo State",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "South America",
        },
        {
          azureRegionAPIName: "brazilsoutheast",
          displayName: "Brazil Southeast",
          shortCode: "brse",
          timeZone: "E. South America Standard Time",
          physicalLocation: "Rio",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "South America",
        },
        {
          azureRegionAPIName: "northeurope",
          displayName: "North Europe",
          shortCode: "neu",
          timeZone: "GMT Standard Time",
          physicalLocation: "Ireland",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Europe",
        },
        {
          azureRegionAPIName: "westeurope",
          displayName: "West Europe",
          shortCode: "weu",
          timeZone: "W. Europe Standard Time",
          physicalLocation: "Netherlands",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Europe",
        },
        {
          azureRegionAPIName: "uksouth",
          displayName: "UK South",
          shortCode: "uks",
          timeZone: "GMT Standard Time",
          physicalLocation: "London",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Europe",
        },
        {
          azureRegionAPIName: "ukwest",
          displayName: "UK West",
          shortCode: "ukw",
          timeZone: "GMT Standard Time",
          physicalLocation: "Cardiff",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "Europe",
        },
        {
          azureRegionAPIName: "francecentral",
          displayName: "France Central",
          shortCode: "frc",
          timeZone: "Romance Standard Time",
          physicalLocation: "Paris",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Europe",
        },
        {
          azureRegionAPIName: "francesouth",
          displayName: "France South",
          shortCode: "frs",
          timeZone: "Romance Standard Time",
          physicalLocation: "Marseille",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "Europe",
        },
        {
          azureRegionAPIName: "switzerlandnorth",
          displayName: "Switzerland North",
          shortCode: "swn",
          timeZone: "W. Europe Standard Time",
          physicalLocation: "Zurich",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Europe",
        },
        {
          azureRegionAPIName: "switzerlandwest",
          displayName: "Switzerland West",
          shortCode: "sww",
          timeZone: "W. Europe Standard Time",
          physicalLocation: "Geneva",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "Europe",
        },
        {
          azureRegionAPIName: "germanywestcentral",
          displayName: "Germany West Central",
          shortCode: "gewc",
          timeZone: "W. Europe Standard Time",
          physicalLocation: "Frankfurt",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Europe",
        },
        {
          azureRegionAPIName: "germanynorth",
          displayName: "Germany North",
          shortCode: "gen",
          timeZone: "W. Europe Standard Time",
          physicalLocation: "Berlin",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "Europe",
        },
        {
          azureRegionAPIName: "norwayeast",
          displayName: "Norway East",
          shortCode: "noe",
          timeZone: "W. Europe Standard Time",
          physicalLocation: "Norway",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Europe",
        },
        {
          azureRegionAPIName: "norwaywest",
          displayName: "Norway West",
          shortCode: "now",
          timeZone: "W. Europe Standard Time",
          physicalLocation: "Norway",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "Europe",
        },
        {
          azureRegionAPIName: "swedencentral",
          displayName: "Sweden Central",
          shortCode: "swc",
          timeZone: "W. Europe Standard Time",
          physicalLocation: "Gävle",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Europe",
        },
        {
          azureRegionAPIName: "swedensouth",
          displayName: "Sweden South",
          shortCode: "sws",
          timeZone: "W. Europe Standard Time",
          physicalLocation: "Sweden",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "Europe",
        },
        {
          azureRegionAPIName: "italynorth",
          displayName: "Italy North",
          shortCode: "itn",
          timeZone: "W. Europe Standard Time",
          physicalLocation: "Milan",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Europe",
        },
        {
          azureRegionAPIName: "polandcentral",
          displayName: "Poland Central",
          shortCode: "plc",
          timeZone: "Central European Standard Time",
          physicalLocation: "Warsaw",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Europe",
        },
        {
          azureRegionAPIName: "spaincentral",
          displayName: "Spain Central",
          shortCode: "spc",
          timeZone: "Romance Standard Time",
          physicalLocation: "Madrid",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Europe",
        },
        {
          azureRegionAPIName: "uaenorth",
          displayName: "UAE North",
          shortCode: "uaen",
          timeZone: "Arabian Standard Time",
          physicalLocation: "Dubai",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Middle East",
        },
        {
          azureRegionAPIName: "uaecentral",
          displayName: "UAE Central",
          shortCode: "uaec",
          timeZone: "Arabian Standard Time",
          physicalLocation: "Abu Dhabi",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "Middle East",
        },
        {
          azureRegionAPIName: "israelcentral",
          displayName: "Israel Central",
          shortCode: "ilc",
          timeZone: "Israel Standard Time",
          physicalLocation: "Israel",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Middle East",
        },
        {
          azureRegionAPIName: "qatarcentral",
          displayName: "Qatar Central",
          shortCode: "qac",
          timeZone: "Arabian Standard Time",
          physicalLocation: "Doha",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Middle East",
        },
        {
          azureRegionAPIName: "southafricanorth",
          displayName: "South Africa North",
          shortCode: "san",
          timeZone: "South Africa Standard Time",
          physicalLocation: "Johannesburg",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Africa",
        },
        {
          azureRegionAPIName: "southafricawest",
          displayName: "South Africa West",
          shortCode: "saw",
          timeZone: "South Africa Standard Time",
          physicalLocation: "Cape Town",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "Africa",
        },
        {
          azureRegionAPIName: "eastasia",
          displayName: "East Asia",
          shortCode: "ea",
          timeZone: "China Standard Time",
          physicalLocation: "Hong Kong",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Asia Pacific",
        },
        {
          azureRegionAPIName: "southeastasia",
          displayName: "Southeast Asia",
          shortCode: "sea",
          timeZone: "Singapore Standard Time",
          physicalLocation: "Singapore",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Asia Pacific",
        },
        {
          azureRegionAPIName: "japaneast",
          displayName: "Japan East",
          shortCode: "jpe",
          timeZone: "Tokyo Standard Time",
          physicalLocation: "Tokyo, Saitama",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Asia Pacific",
        },
        {
          azureRegionAPIName: "japanwest",
          displayName: "Japan West",
          shortCode: "jpw",
          timeZone: "Tokyo Standard Time",
          physicalLocation: "Osaka",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Asia Pacific",
        },
        {
          azureRegionAPIName: "koreacentral",
          displayName: "Korea Central",
          shortCode: "krc",
          timeZone: "Korea Standard Time",
          physicalLocation: "Seoul",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Asia Pacific",
        },
        {
          azureRegionAPIName: "koreasouth",
          displayName: "Korea South",
          shortCode: "krs",
          timeZone: "Korea Standard Time",
          physicalLocation: "Busan",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "Asia Pacific",
        },
        {
          azureRegionAPIName: "australiaeast",
          displayName: "Australia East",
          shortCode: "aue",
          timeZone: "AUS Eastern Standard Time",
          physicalLocation: "New South Wales",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Asia Pacific",
        },
        {
          azureRegionAPIName: "australiasoutheast",
          displayName: "Australia Southeast",
          shortCode: "ause",
          timeZone: "AUS Eastern Standard Time",
          physicalLocation: "Victoria",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "Asia Pacific",
        },
        {
          azureRegionAPIName: "australiacentral",
          displayName: "Australia Central",
          shortCode: "auc",
          timeZone: "Cen. Australia Standard Time",
          physicalLocation: "Canberra",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "Asia Pacific",
        },
        {
          azureRegionAPIName: "australiacentral2",
          displayName: "Australia Central 2",
          shortCode: "auc2",
          timeZone: "Cen. Australia Standard Time",
          physicalLocation: "Canberra",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "Asia Pacific",
        },
        {
          azureRegionAPIName: "centralindia",
          displayName: "Central India",
          shortCode: "inc",
          timeZone: "India Standard Time",
          physicalLocation: "Pune",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Asia Pacific",
        },
        {
          azureRegionAPIName: "southindia",
          displayName: "South India",
          shortCode: "ins",
          timeZone: "India Standard Time",
          physicalLocation: "Chennai",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "Asia Pacific",
        },
        {
          azureRegionAPIName: "westindia",
          displayName: "West India",
          shortCode: "inw",
          timeZone: "India Standard Time",
          physicalLocation: "Mumbai",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "Asia Pacific",
        },
        {
          azureRegionAPIName: "indonesiacentral",
          displayName: "Indonesia Central",
          shortCode: "idc",
          timeZone: "SE Asia Standard Time",
          physicalLocation: "Jakarta",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Asia Pacific",
        },
        {
          azureRegionAPIName: "newzealandnorth",
          displayName: "New Zealand North",
          shortCode: "nzn",
          timeZone: "New Zealand Standard Time",
          physicalLocation: "Auckland",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Asia Pacific",
        },
        {
          azureRegionAPIName: "mexicocentral",
          displayName: "Mexico Central",
          shortCode: "mxc",
          timeZone: "Central Standard Time (Mexico)",
          physicalLocation: "Querétaro State",
          regionType: "Physical",
          regionCategory: "Recommended",
          geographyGroup: "Mexico",
        },
        {
          azureRegionAPIName: "centraluseuap",
          displayName: "Central US EUAP",
          shortCode: "cuseuap",
          timeZone: "Central Standard Time",
          physicalLocation: "Iowa",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "US",
        },
        {
          azureRegionAPIName: "eastus2euap",
          displayName: "East US 2 EUAP",
          shortCode: "eus2euap",
          timeZone: "Eastern Standard Time",
          physicalLocation: "Virginia",
          regionType: "Physical",
          regionCategory: "Other",
          geographyGroup: "US",
        },
      ],
      selectedRegion: null,

      openInfoModal: null,
      configDetails: {},
      loading: {},
      error: {},

      aiExplaining: {},
      aiExplanations: {},
      aiErrors: {},
      aiCachedFrom: {},
    };
  },
  computed: {
    ...mapState(["isLoggedIn"]),

    greenfieldPresetCount() { return Object.values(this.configurationCategories).flat().length; },
    brownfieldPresetCount() { return 16; },
    categoryAllSelected() {
      const options = this.configurationCategories[this.activeServiceTab] || [];
      if (options.length === 0) return false;
      return options.every(o => this.selectedComponents.includes(o));
    },
    offboardCategoryAllSelected() {
      const options = this.offboardCategories[this.activeOffboardServiceTab] || [];
      if (options.length === 0) return false;
      return options.every(o => this.selectedOffboardComponents.includes(o));
    },
    categorySelectedCount() {
      const options = this.configurationCategories[this.activeServiceTab] || [];
      return options.filter(o => this.selectedComponents.includes(o)).length;
    },
    offboardCategorySelectedCount() {
      const options = this.offboardCategories[this.activeOffboardServiceTab] || [];
      return options.filter(o => this.selectedOffboardComponents.includes(o)).length;
    },

    isFormValid() {
      return (
        this.clientCodeValidated &&
        this.clientCode.trim() !== "" &&
        this.domain.trim() !== "" &&
        this.username.trim() !== "" &&
        this.password.trim() !== "" &&
        (this.hasOptionsCSP === "yes" ||
          this.customSubscriptionName.trim() !== "")
      );
    },

    // Validation for the new client info fields
    isNewClientInfoValid() {
      return (
        this.newClientCodeValidated &&
        this.newClientCode.trim() !== "" &&
        this.newDomain.trim() !== "" &&
        this.selectedRegion &&
        (this.newHasOptionsCSP === "yes" ||
          this.newCustomSubscriptionName.trim() !== "")
      );
    },

    step3Valid() {
      return !!(this.clientTenantId && this.globalAdminToken);
    },

    filteredConfigs() {
      let options = this.configurationCategories[this.activeServiceTab] || [];
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        options = options.filter(o => o.toLowerCase().includes(q) || this.getConfigDescription(o).toLowerCase().includes(q));
      }
      if (this.impactFilter !== 'all') {
        options = options.filter(o => this.getConfigImpact(o).toLowerCase() === this.impactFilter.toLowerCase());
      }
      return options;
    },

    filteredOffboardConfigs() {
      let options = this.offboardCategories[this.activeOffboardServiceTab] || [];
      if (this.offboardSearchQuery) {
        const q = this.offboardSearchQuery.toLowerCase();
        options = options.filter(o => o.toLowerCase().includes(q) || this.getOffboardConfigDescription(o).toLowerCase().includes(q));
      }
      if (this.offboardImpactFilter !== 'all') {
        options = options.filter(o => this.getOffboardConfigImpact(o).toLowerCase() === this.offboardImpactFilter.toLowerCase());
      }
      return options;
    },
  },

  methods: {
    saveFilterState(tab) {
      if (tab === 'custom') {
        sessionStorage.setItem('tenantbaseline_filter_search', this.searchQuery);
        sessionStorage.setItem('tenantbaseline_filter_impact', this.impactFilter);
        sessionStorage.setItem('tenantbaseline_filter_category', this.activeServiceTab);
        sessionStorage.setItem('tenantbaseline_filter_view', this.viewMode);
      } else if (tab === 'offboard') {
        sessionStorage.setItem('tenantbaseline_filter_offsearch', this.offboardSearchQuery);
        sessionStorage.setItem('tenantbaseline_filter_offimpact', this.offboardImpactFilter);
        sessionStorage.setItem('tenantbaseline_filter_offcategory', this.activeOffboardServiceTab);
        sessionStorage.setItem('tenantbaseline_filter_offview', this.offboardViewMode);
      }
    },
    restoreFilterState(tab) {
      if (tab === 'custom') {
        this.searchQuery = sessionStorage.getItem('tenantbaseline_filter_search') || '';
        this.impactFilter = sessionStorage.getItem('tenantbaseline_filter_impact') || 'all';
        const savedCategory = sessionStorage.getItem('tenantbaseline_filter_category');
        if (savedCategory && this.configurationCategories[savedCategory]) this.activeServiceTab = savedCategory;
        this.viewMode = sessionStorage.getItem('tenantbaseline_filter_view') || 'card';
      } else if (tab === 'offboard') {
        this.offboardSearchQuery = sessionStorage.getItem('tenantbaseline_filter_offsearch') || '';
        this.offboardImpactFilter = sessionStorage.getItem('tenantbaseline_filter_offimpact') || 'all';
        const savedCategory = sessionStorage.getItem('tenantbaseline_filter_offcategory');
        if (savedCategory && this.offboardCategories[savedCategory]) this.activeOffboardServiceTab = savedCategory;
        this.offboardViewMode = sessionStorage.getItem('tenantbaseline_filter_offview') || 'card';
      }
    },
    formatRoleStatus(status) {
      if (!status) return { text: '', success: false, hasStatus: false };
      const success = status.startsWith("✅");
      const text = (success ? status.substring(2) : status.startsWith("❌") ? status.substring(2) : status).trim();
      return { text, success, hasStatus: true };
    },
    nextStep() {
      if (this.onboardStep < 3) this.onboardStep++;
    },
    prevStep() {
      if (this.onboardStep > 0) this.onboardStep--;
    },
    // Removes everything the onboarding flow stashes in sessionStorage,
    // including the onboarding MSAL cache (separate client ID from the main app session).
    clearOnboardingSession() {
      [
        "newClientCode",
        "newHasOptionsCSP",
        "newCustomSubscriptionName",
        "newDomain",
        "enrollInPowerSupport",
        "selectedRegion",
        "onboardStep",
        "clientTenantId",
        "preValidatedTenantId",
        "consentStartTime",
      ].forEach((key) => sessionStorage.removeItem(key));

      const onboardClientId = "9d4e9287-a121-4937-b94d-e7be78d8d334";
      Object.keys(sessionStorage)
        .filter((key) => key.includes(onboardClientId))
        .forEach((key) => sessionStorage.removeItem(key));
    },
    // Full reset: clear session state AND component state back to the intro panel
    resetOnboarding() {
      if (
        !confirm(
          "Clear the current onboarding session and start from the beginning?",
        )
      ) {
        return;
      }

      this.clearOnboardingSession();

      this.newClientCode = "";
      this.newClientCodeError = "";
      this.newClientCodeSuccess = "";
      this.newClientCodeValidated = false;
      this.newHasOptionsCSP = "yes";
      this.newCustomSubscriptionName = "";
      this.newDomain = "";
      this.enrollInPowerSupport = "yes";
      this.selectedRegion = null;
      this.clientTenantId = null;
      this.globalAdminToken = null;
      this.onboardAccount = null;
      this.readerRoleStatus = "";
      this.policyContributorRoleStatus = "";
      this.ContributorRoleStatus = "";
      this.teamsAdminRoleStatus = "";
      this.globalAdminRoleStatus = "";
      this.exchangeAdminRoleStatus = "";
      this.AuthPolicyAdminStatus = "";
      this.PrivAuthAdminStatus = "";
      this.allRolesAssigned = false;
      this.onboardPipelineStatus = { text: "", type: "" };
      this.onboardStep = 0;
    },
    selectOffboardPreset(preset) {
      switch (preset) {
        case "minimal":
          // Only remove baseline configurations
          this.selectedOffboardComponents = [
            "Remove Conditional Access - Policies",
            "Remove Conditional Access - Named Locations",
            "Remove Transport Rules",
            "Remove Safe Links Policy",
            "Remove Required Entra ID Groups",
          ];
          break;
        case "standard":
          // Standard off-boarding
          this.selectedOffboardComponents = [
            ...this.offboardCategories[
              "Microsoft Intune Configuration Options"
            ],
            ...this.offboardCategories[
              "Exchange Online Configuration Options"
            ].filter(
              (item) => item.startsWith("Remove") || item.startsWith("Revert"),
            ),
            "Remove MEM Groups",
            "Remove Required Entra ID Groups",
            "Remove Azure AD Policy Baseline",
          ];
          break;
        case "complete":
          // Complete removal of all configurations
          this.selectedOffboardComponents = Object.values(
            this.offboardCategories,
          ).flat();
          break;
      }
    },
    async prepareConfigurationsForExport() {
      const configurations = {};
      const entries = Object.entries(this.configurationMapping);
      const total = entries.length;
      let processed = 0;
      let usedCache = 0;
      let fetched = 0;

      try {
        const accessToken = await this.getAccessToken();

        for (const [componentName, mapping] of entries) {
          processed++;

          // Check if we already have this config loaded in cache
          if (this.configDetails[componentName]) {
            // Use the complete cached configuration - no need to fetch!
            usedCache++;
            this.exportProgress = `Using cached data... ${usedCache}/${processed} (${
              total - processed
            } remaining)`;

            configurations[componentName] = {
              ...mapping,
              rawConfig: JSON.stringify(
                this.configDetails[componentName].rawConfig,
                null,
                2,
              ),
            };
          } else {
            // Only fetch if not in cache
            fetched++;
            this.exportProgress = `Fetching... ${fetched} new | ${usedCache} cached (${processed}/${total})`;

            try {
              let configDetails = null;

              switch (mapping.type) {
                case "single_file":
                  configDetails = await this.loadSingleFileConfig(
                    accessToken,
                    mapping,
                  );
                  break;

                case "directory":
                  configDetails = await this.loadDirectoryConfig(
                    accessToken,
                    mapping,
                  );
                  break;

                case "multiple_files":
                  configDetails = await this.loadMultipleFilesConfig(
                    accessToken,
                    mapping,
                  );
                  break;

                case "function":
                  configDetails = await this.loadFunctionConfig(mapping);
                  break;
              }

              if (configDetails) {
                // ✅ CRITICAL FIX: Cache it for future use!
                this.configDetails[componentName] = configDetails;

                configurations[componentName] = {
                  ...mapping,
                  rawConfig: JSON.stringify(configDetails.rawConfig, null, 2),
                };
              } else {
                configurations[componentName] = {
                  ...mapping,
                  rawConfig: "Configuration not available",
                };
              }
            } catch (error) {
              console.warn(
                `Failed to load config for ${componentName}:`,
                error,
              );
              configurations[componentName] = {
                ...mapping,
                rawConfig: `Error loading configuration: ${error.message}`,
              };
            }
          }
        }

        this.exportProgress = `Complete! Used ${usedCache} cached, fetched ${fetched} new configurations`;

        return configurations;
      } catch (error) {
        console.error("Error preparing configurations:", error);
        throw error;
      }
    },

    async exportToPDF() {
      this.exportInProgress = true;
      this.exportProgress = "Preparing export...";

      try {
        let configurationsToExport;

        if (this.includeRawConfig) {
          // Check how many are already cached
          const cachedCount = Object.keys(this.configDetails).length;
          const totalCount = Object.keys(this.configurationMapping).length;

          if (cachedCount > 0) {
            this.exportProgress = `Found ${cachedCount}/${totalCount} already loaded. Processing...`;
          }

          configurationsToExport = await this.prepareConfigurationsForExport();
        } else {
          // For basic export, just use the mapping
          configurationsToExport = this.configurationMapping;
        }

        this.exportProgress = "Generating PDF...";

        const doc = new jsPDF("l", "mm", "a4");
        let currentY = 15;

        // Title page
        doc.setFontSize(24);
        doc.setTextColor(30, 41, 59);
        doc.text("Microsoft 365 Security Configuration Options", 14, currentY);

        currentY += 10;
        doc.setFontSize(10);
        doc.setTextColor(100, 116, 139);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 14, currentY);
        doc.text(
          `Total Configurations: ${Object.keys(configurationsToExport).length}`,
          14,
          currentY + 5,
        );

        if (this.includeRawConfig) {
          const cachedCount = Object.keys(this.configDetails).length;
          doc.text(
            `Raw JSON Configurations (${cachedCount} from cache)`,
            14,
            currentY + 10,
          );
        }

        currentY += 20;

        // Group by category
        const categorizedData = {};
        Object.entries(configurationsToExport).forEach(
          ([componentName, config]) => {
            const category = config.category || "Other";
            if (!categorizedData[category]) {
              categorizedData[category] = [];
            }
            categorizedData[category].push({ name: componentName, config });
          },
        );

        // Create summary table
        const summaryTableData = [];
        Object.entries(categorizedData).forEach(([category, items]) => {
          summaryTableData.push([
            {
              content: category,
              colSpan: 4,
              styles: {
                fillColor: [59, 130, 246],
                textColor: 255,
                fontStyle: "bold",
              },
            },
          ]);

          items.forEach(({ name, config }) => {
            const row = [
              name,
              config.description,
              config.impact,
              this.formatPathForExport(config.path),
            ];
            summaryTableData.push(row);
          });
        });

        doc.autoTable({
          startY: currentY,
          head: [["Component Name", "Description", "Impact", "Path"]],
          body: summaryTableData,
          theme: "grid",
          headStyles: {
            fillColor: [59, 130, 246],
            textColor: 255,
            fontSize: 9,
            fontStyle: "bold",
          },
          bodyStyles: {
            fontSize: 8,
            textColor: [30, 41, 59],
          },
          alternateRowStyles: {
            fillColor: [248, 250, 252],
          },
          columnStyles: {
            0: { cellWidth: 60 },
            1: { cellWidth: 90 },
            2: { cellWidth: 20 },
            3: { cellWidth: 75 },
          },
          styles: {
            overflow: "linebreak",
            cellPadding: 2,
            lineColor: [226, 232, 240],
            lineWidth: 0.1,
          },
          didParseCell: (data) => {
            if (data.column.index === 2 && data.section === "body") {
              const impact = data.cell.text[0];
              if (impact === "High") {
                data.cell.styles.textColor = [239, 68, 68];
                data.cell.styles.fontStyle = "bold";
              } else if (impact === "Medium") {
                data.cell.styles.textColor = [249, 115, 22];
                data.cell.styles.fontStyle = "bold";
              } else if (impact === "Low") {
                data.cell.styles.textColor = [34, 197, 94];
                data.cell.styles.fontStyle = "bold";
              }
            }
          },
        });

        // Add detailed configuration pages if requested
        // Add detailed configuration pages if requested
        if (this.includeRawConfig) {
          doc.addPage();

          Object.entries(categorizedData).forEach(([category, items]) => {
            items.forEach(({ name, config }) => {
              // Check if we need a new page
              if (
                doc.internal.pageSize.getHeight() - doc.lastAutoTable.finalY <
                60 // <-- Added the < operator
              ) {
                doc.addPage();
              }

              const startY = doc.lastAutoTable
                ? doc.lastAutoTable.finalY + 10
                : 20;

              // Configuration header
              doc.setFillColor(59, 130, 246);
              doc.rect(
                14,
                startY,
                doc.internal.pageSize.getWidth() - 28,
                8,
                "F",
              );
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(11);
              doc.setFont(undefined, "bold");
              doc.text(name, 16, startY + 5.5);

              // Configuration details
              doc.autoTable({
                startY: startY + 10,
                body: [
                  ["Category", category],
                  ["Description", config.description],
                  ["Impact Level", config.impact],
                  ["Type", config.type],
                  ["Path", this.formatPathForExport(config.path)],
                ],
                theme: "plain",
                styles: {
                  fontSize: 8,
                  cellPadding: 2,
                },
                columnStyles: {
                  0: {
                    cellWidth: 30,
                    fontStyle: "bold",
                    textColor: [100, 116, 139],
                  },
                  1: { cellWidth: "auto" },
                },
              });

              // Raw configuration
              if (config.rawConfig) {
                const configStartY = doc.lastAutoTable.finalY + 5;

                doc.setFontSize(9);
                doc.setFont(undefined, "bold");
                doc.setTextColor(30, 41, 59);
                doc.text("Raw Configuration:", 14, configStartY);

                // Format the JSON
                const jsonLines = config.rawConfig.split("\n");
                doc.setFont("courier", "normal");
                doc.setFontSize(7);
                doc.setTextColor(51, 51, 51);

                let jsonY = configStartY + 5;
                const lineHeight = 3.5;

                jsonLines.forEach((line) => {
                  // Check if we need a new page
                  if (jsonY > doc.internal.pageSize.getHeight() - 20) {
                    doc.addPage();
                    jsonY = 20;
                  }

                  // Truncate very long lines
                  const displayLine =
                    line.length > 120 ? line.substring(0, 120) + "..." : line;
                  doc.text(displayLine, 14, jsonY);
                  jsonY += lineHeight;
                });

                // Update lastAutoTable position for next iteration
                doc.lastAutoTable = { finalY: jsonY };
              }
            });
          });
        }

        // Add page numbers
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.setTextColor(148, 163, 184);
          doc.text(
            `Page ${i} of ${pageCount}`,
            doc.internal.pageSize.getWidth() / 2,
            doc.internal.pageSize.getHeight() - 10,
            { align: "center" },
          );
        }

        // Save the PDF
        const filename = this.includeRawConfig
          ? "M365_Security_Configuration_Options_Detailed.pdf"
          : "M365_Security_Configuration_Options.pdf";
        doc.save(filename);

        const cachedCount = Object.keys(this.configDetails).length;
        this.message = {
          text: `PDF exported successfully!${
            this.includeRawConfig
              ? ` (${cachedCount} configurations were already loaded)`
              : ""
          }`,
          type: "success",
        };
      } catch (error) {
        console.error("Error exporting to PDF:", error);
        this.message = {
          text: "Failed to export PDF: " + error.message,
          type: "error",
        };
      } finally {
        this.exportInProgress = false;
        this.exportProgress = "";
      }
    },

    async exportToCSV() {
      this.exportInProgress = true;
      this.exportProgress = "Preparing export...";

      try {
        let configurationsToExport;

        if (this.includeRawConfig) {
          const cachedCount = Object.keys(this.configDetails).length;
          const totalCount = Object.keys(this.configurationMapping).length;

          if (cachedCount > 0) {
            this.exportProgress = `Found ${cachedCount}/${totalCount} already loaded. Processing...`;
          }

          configurationsToExport = await this.prepareConfigurationsForExport();
        } else {
          configurationsToExport = this.configurationMapping;
        }

        this.exportProgress = "Generating CSV...";

        // CSV headers
        let csvContent = this.includeRawConfig
          ? "Category,Component Name,Description,Impact,Type,Path,Raw Configuration\n"
          : "Category,Component Name,Description,Impact,Type,Path\n";

        // Group by category
        const categorizedData = {};
        Object.entries(configurationsToExport).forEach(
          ([componentName, config]) => {
            const category = config.category || "Other";
            if (!categorizedData[category]) {
              categorizedData[category] = [];
            }

            categorizedData[category].push({
              component: componentName,
              description: config.description,
              impact: config.impact,
              type: config.type,
              path: this.formatPathForExport(config.path),
              rawConfig: config.rawConfig || "",
            });
          },
        );

        // Add data by category
        Object.entries(categorizedData).forEach(([category, items]) => {
          items.forEach((item) => {
            const row = [
              this.escapeCSV(category),
              this.escapeCSV(item.component),
              this.escapeCSV(item.description),
              this.escapeCSV(item.impact),
              this.escapeCSV(item.type),
              this.escapeCSV(item.path),
            ];

            if (this.includeRawConfig) {
              row.push(this.escapeCSV(item.rawConfig));
            }

            csvContent += row.join(",") + "\n";
          });
        });

        // Create blob and download
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);

        const filename = this.includeRawConfig
          ? "M365_Security_Configuration_Options_Detailed.csv"
          : "M365_Security_Configuration_Options.csv";

        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        const cachedCount = Object.keys(this.configDetails).length;
        this.message = {
          text: `CSV exported successfully!${
            this.includeRawConfig
              ? ` (${cachedCount} configurations were already loaded)`
              : ""
          }`,
          type: "success",
        };
      } catch (error) {
        console.error("Error exporting to CSV:", error);
        this.message = {
          text: "Failed to export CSV: " + error.message,
          type: "error",
        };
      } finally {
        this.exportInProgress = false;
        this.exportProgress = "";
      }
    },

    async explainConfigWithAI(configName) {
      this.aiExplaining[configName] = true;
      this.aiErrors[configName] = null;
      this.aiExplanations[configName] = "";
      this.aiCachedFrom[configName] = null;

      try {
        const configDetails = this.configDetails[configName];

        if (!configDetails || !configDetails.rawConfig) {
          throw new Error("No configuration data available to explain");
        }

        const jsonContent = JSON.stringify(configDetails.rawConfig, null, 2);

        // Compute content hash for cache lookup
        const contentHash = await this.computeContentHash(jsonContent);

        // Check backend cache first
        try {
          const cached = await getPolicyExplanation(configName);
          if (cached && cached.explanation) {
            this.aiExplanations[configName] = cached.explanation;
            this.aiCachedFrom[configName] = new Date(cached.generatedAt).toLocaleString();
            this.aiExplaining[configName] = false;
            return;
          }
        } catch (cacheError) {
          // Cache miss or network error — fall through to live AI call
          if (cacheError.status !== 404) {
            console.warn("Policy explanation cache lookup failed:", cacheError);
          }
        }

        // Live AI call
        const self = this;
        let lastUpdate = 0;
        const updateInterval = 50;

        const explanation = await callAiApi(
          jsonContent,
          {
            model: "glm-5-2-nvfp4",
            maxTokens: 1048576,
            stream: false,
          },
          (accumulatedText) => {
            const now = Date.now();
            if (now - lastUpdate > updateInterval) {
              self.aiExplanations[configName] = accumulatedText;
              lastUpdate = now;
            }
          },
        );

        this.aiExplanations[configName] = explanation;

        // Save to backend cache for future use
        const mapping = this.configurationMapping[configName];
        if (mapping) {
          try {
            await savePolicyExplanation(configName, {
              repoId: mapping.repoId,
              repoPath: mapping.path || (mapping.paths ? mapping.paths.join('; ') : ""),
              contentHash: contentHash,
              explanation: explanation,
              model: "glm-5-2-nvfp4"
            });
          } catch (saveError) {
            console.warn("Failed to cache policy explanation:", saveError);
          }
        } else {
          console.warn("No configurationMapping found for '" + configName + "' — explanation not cached.");
        }
      } catch (error) {
        console.error("Failed to get AI explanation:", error);
        this.aiErrors[configName] =
          error.message || "Failed to get AI explanation. Please try again.";
      } finally {
        this.aiExplaining[configName] = false;
      }
    },

    async computeContentHash(jsonStr) {
      const msgUint8 = new TextEncoder().encode(jsonStr);
      const hashBuffer = await crypto.subtle.digest("SHA-256", msgUint8);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
    },

    async regenerateExplanation(configName) {
      this.aiExplaining[configName] = true;
      this.aiErrors[configName] = null;
      this.aiCachedFrom[configName] = null;

      try {
        const configDetails = this.configDetails[configName];
        if (!configDetails || !configDetails.rawConfig) {
          throw new Error("No configuration data available to explain");
        }

        const jsonContent = JSON.stringify(configDetails.rawConfig, null, 2);
        const contentHash = await this.computeContentHash(jsonContent);

        const self = this;
        let lastUpdate = 0;
        const updateInterval = 50;

        const explanation = await callAiApi(
          jsonContent,
          { model: "glm-5-2-nvfp4", maxTokens: 1048576, stream: false },
          (accumulatedText) => {
            const now = Date.now();
            if (now - lastUpdate > updateInterval) {
              self.aiExplanations[configName] = accumulatedText;
              lastUpdate = now;
            }
          },
        );

        this.aiExplanations[configName] = explanation;

        const mapping = this.configurationMapping[configName];
        if (mapping) {
          try {
            await savePolicyExplanation(configName, {
              repoId: mapping.repoId,
              repoPath: mapping.path || (mapping.paths ? mapping.paths.join('; ') : ""),
              contentHash: contentHash,
              explanation: explanation,
              model: "glm-5-2-nvfp4"
            });
          } catch (saveError) {
            console.warn("Failed to cache policy explanation:", saveError);
          }
        }
      } catch (error) {
        console.error("Failed to regenerate AI explanation:", error);
        this.aiErrors[configName] =
          error.message || "Failed to generate explanation. Please try again.";
      } finally {
        this.aiExplaining[configName] = false;
      }
    },

    getDeployButtonTooltip() {
      if (this.selectedComponents.length === 0 && !this.selectedPipelineId) {
        return "Select at least one component and a pipeline to deploy";
      }
      if (this.selectedComponents.length === 0) {
        return "Select at least one component to deploy ";
      }
      if (!this.selectedPipelineId) {
        return "Select a pipeline to deploy";
      }
      return "Deploy selected components to the tenant";
    },

    formatPathForExport(path) {
      if (!path) return "Multiple paths";
      if (typeof path === "string") return path;
      if (Array.isArray(path)) return path.join("; ");
      return String(path);
    },

    escapeCSV(value) {
      if (value === null || value === undefined) return "";

      const stringValue = String(value);

      if (
        stringValue.includes(",") ||
        stringValue.includes("\n") ||
        stringValue.includes('"')
      ) {
        return '"' + stringValue.replace(/"/g, '""') + '"';
      }

      return stringValue;
    },

    clearOffboardSelection() {
      this.selectedOffboardComponents = [];
    },

    // Get off-board configuration description
    getOffboardConfigDescription(option) {
      return (
        this.offboardConfigurationMapping[option]?.description ||
        "Remove or revert this configuration to default settings"
      );
    },

    // Get off-board configuration impact
    getOffboardConfigImpact(option) {
      return this.offboardConfigurationMapping[option]?.impact || "Medium";
    },

    // Get off-board estimated time
    getOffboardEstimatedTime(option) {
      return (
        this.offboardConfigurationMapping[option]?.estimatedTime ||
        "2-3 minutes"
      );
    },

    // Get off-board configuration dependencies
    getOffboardConfigDependencies(option) {
      return this.offboardConfigurationMapping[option]?.dependencies || null;
    },

    // Calculate total off-board estimated time
    getTotalOffboardEstimatedTime() {
      const totalMinutes = this.selectedOffboardComponents.reduce(
        (total, component) => {
          const time = this.getOffboardEstimatedTime(component);
          const match = time.match(/(\d+)-(\d+)/);
          if (match) {
            return total + parseInt(match[2]); // Use upper estimate
          }
          return total + 3; // Default 3 minutes
        },
        0,
      );

      if (totalMinutes < 60) {
        return `${totalMinutes} minutes`;
      } else {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours} hour${hours > 1 ? "s" : ""} ${minutes} minutes`;
      }
    },

    // Check if off-board configuration has mapping
    hasOffboardConfigMapping(option) {
      // You can extend this to check for actual file mappings like in the deployment tab
      return this.offboardConfigurationMapping[option] !== undefined;
    },

    // Show off-board configuration info
    async showOffboardConfigInfo(option) {
      // Similar to your existing showConfigInfo but for off-boarding
      this.$set(this.offboardLoading, option, true);

      try {
        // Here you would fetch the configuration details
        // For now, showing the mapping info
        const config = this.offboardConfigurationMapping[option];

        if (config) {
          alert(`
Configuration: ${option}
Description: ${config.description}
Impact: ${config.impact}
Estimated Time: ${config.estimatedTime}
Dependencies: ${config.dependencies}
      `);
        }
      } catch (error) {
        console.error("Error loading off-board config info:", error);
        alert("Failed to load configuration details");
      } finally {
        this.$set(this.offboardLoading, option, false);
      }
    },

    confirmOffboard() {
      if (this.selectedOffboardComponents.length === 0) {
        this.offboardMessage = {
          text: "Please select at least one component to remove",
          type: "error",
        };
        return;
      }

      if (!this.selectedOffboardPipelineId) {
        this.offboardMessage = {
          text: "Please select a pipeline",
          type: "error",
        };
        return;
      }

      // Show confirmation modal
      this.showOffboardConfirmation = true;
    },

    // Cancel off-board
    cancelOffboard() {
      this.showOffboardConfirmation = false;
    },

    // Execute off-board process
    async executeOffboard() {
      this.showOffboardConfirmation = false;

      if (!this.selectedOffboardPipelineId) return;

      try {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length) {
          const accessTokenRequest = {
            account: accounts[0],
            scopes: ["499b84ac-1321-427f-aa17-267ca6975798/user_impersonation"],
          };
          const response = await msalInstance.acquireTokenSilent(
            accessTokenRequest,
          );
          const accessToken = response.accessToken;

          // Retrieve the selected pipeline
          const selectedPipeline = this.pipelines.find(
            (p) => p.id === this.selectedOffboardPipelineId,
          );
          if (!selectedPipeline) {
            throw new Error("Selected pipeline not found.");
          }
          const projectId = selectedPipeline._links.self.href.split("/")[4];

          // Convert selectedOffboardComponents array into a JSON string for the pipeline run
          const componentsJson = JSON.stringify({
            components: this.selectedOffboardComponents,
          });

          // Trigger the pipeline and get the run ID
          const runId = await runOffboardPipeline(
            accessToken,
            projectId,
            this.selectedOffboardPipelineId,
            componentsJson,
          );

          // Check if the pipeline run ID exists
          if (runId) {
            this.offboardMessage = {
              text: "Off-board process has started successfully. Components are being removed...",
              type: "success",
            };

            // Add selected components to the removedComponents array
            this.removedComponents = [...this.selectedOffboardComponents];

            // Clear selection
            this.selectedOffboardComponents = [];

            // Start polling the pipeline status
            this.pollOffboardPipelineStatus(
              accessToken,
              projectId,
              this.selectedOffboardPipelineId,
              runId,
            );
          } else {
            this.offboardMessage = {
              text: "Failed to trigger the off-board pipeline.",
              type: "error",
            };
          }
        } else {
          throw new Error("No accounts available for authentication.");
        }
      } catch (error) {
        console.error("Error during off-board process:", error);
        this.offboardMessage = {
          text: "Error during off-board process: " + error.message,
          type: "error",
        };
      }
    },

    async pollOffboardPipelineStatus(
      accessToken,
      projectId,
      pipelineId,
      runId,
    ) {
      const pollInterval = 10000; // Poll every 10 seconds

      const poll = async () => {
        try {
          const status = await getPipelineStatus(
            accessToken,
            projectId,
            pipelineId,
            runId,
          );

          if (status) {
            this.offboardPipelineStatusMessage = {
              text: `Off-board Pipeline Run ID: ${runId} - Status: ${status.state}`,
              type: "info",
            };

            // Check if the pipeline has completed
            if (status.state === "completed") {
              if (status.result === "succeeded") {
                this.offboardPipelineStatusMessage = {
                  text: `Off-board process completed successfully! All selected components have been removed.`,
                  type: "success",
                };
              } else {
                this.offboardPipelineStatusMessage = {
                  text: `Off-board process completed with result: ${status.result}`,
                  type: status.result === "failed" ? "error" : "warning",
                };
              }
              return; // Stop polling
            }

            // Continue polling if still in progress
            if (
              status.state === "inProgress" ||
              status.state === "notStarted"
            ) {
              setTimeout(poll, pollInterval);
            }
          }
        } catch (error) {
          console.error("Error polling off-board pipeline status:", error);
          this.offboardPipelineStatusMessage = {
            text: "Error checking pipeline status: " + error.message,
            type: "error",
          };
        }
      };

      poll(); // Start polling
    },

    // Validate client code in the original form
    validateClientCode() {
      const clientCodePattern = /^[A-Z]{3}$/;
      if (!clientCodePattern.test(this.clientCode)) {
        this.clientCodeError =
          "Client code must be exactly 3 uppercase letters.";
        this.clientCodeSuccess = "";
        this.clientCodeValidated = false;
      } else {
        this.clientCodeError = "";
        this.clientCodeSuccess = `Client Code is valid!`;
        this.clientCodeValidated = true;
      }
    },

    formatCategoryName(category) {
      // Shorten category names for tabs
      const nameMap = {
        "Microsoft Intune Configuration Options": "Microsoft Intune",
        "Exchange Online Configuration Options": "Exchange Online",
        "SharePoint Configuration Options": "SharePoint",
        "Microsoft Entra ID Configuration Options": "Entra ID Config",
        "Microsoft Entra ID Security Options": "Entra ID Security",
        "Tenant Organisational Configuration Options": "Teams & Forms",
        "Azure AD Policy Definition Configuration Options": "AD Policies",
        "Azure Maintenance Configurations Options": "Maintenance",
        "Entra ID - User Account Creation": "User Accounts",
        "Netskope Configuration Options": "Netskope",
      };
      return nameMap[category] || category;
    },

    getConfigDescription(option) {
      const mapping = this.configurationMapping[option];
      return mapping
        ? mapping.description
        : "Configuration component for Microsoft 365 baseline deployment";
    },

    getEstimatedTime(option) {
      return this.timeEstimates[option] || "5 min";
    },

    getConfigDependencies(option) {
      return this.dependencies[option] || null;
    },

    getTotalEstimatedTime() {
      let totalSeconds = 0;
      this.selectedComponents.forEach((component) => {
        const timeStr = this.getEstimatedTime(component);

        // Extract number and check if it's seconds or minutes
        const match = timeStr.match(/(\d+)\s*(sec|min)/i);
        if (match) {
          const value = parseInt(match[1]);
          const unit = match[2].toLowerCase();

          if (unit.startsWith("sec")) {
            totalSeconds += value;
          } else if (unit.startsWith("min")) {
            totalSeconds += value * 60;
          }
        } else {
          // Fallback: assume 5 seconds if no match
          totalSeconds += 5;
        }
      });

      if (totalSeconds >= 60) {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
      }
      return `${totalSeconds} seconds`;
    },

    selectPreset(type) {
      const brownfieldComponents = [
        "Create Device Compliance Policies",
        "Create Android Device Owner Enrollment Profiles",
        "Create Auto Pilot Profiles",
        "Create Device Management Configuration Policies",
        "Create Deploy Intent Policies",
        "Create Device Management - Health Scripts",
        "Create Device Management Device Configurations Policies",
        "Create Device Management - Management Scripts",
        "Create Windows Feature Update Profiles",
        "Create Conditional Access - Policies",
        "Create Conditional Access - Named Locations",
        "Create Device Clean-Up Rules",
        "Create Device Registration Policy Settings",
        "Create MEM Groups",
        "Create Phase 0, 1, 2 Maintenance Configurations",
        "Create Entra ID Service Accounts",
        "Create Managed App Policies",
      ];

      switch (type) {
        case "greenfield":
          this.selectAll(true);
          break;
        case "brownfield":
          this.selectedComponents = [...brownfieldComponents];
          break;
      }
    },

    clearSelection() {
      this.selectedComponents = [];
    },

    isPathTooLong(path) {
      return path && path.length > 60; // Adjust threshold as needed
    },

    formatPath(path) {
      if (!path) return "Multiple paths";
      if (path.length <= 60) return path;

      // Keep meaningful parts - show beginning and end
      const parts = path.split("/");
      if (parts.length > 4) {
        // Show first 2 and last 2 parts
        const start = parts.slice(0, 2).join("/");
        const end = parts.slice(-2).join("/");
        return `${start}/.../${end}`;
      }

      // Simple truncation with ellipsis
      return path.substring(0, 55) + "...";
    },

    copyPathToClipboard(path) {
      if (path) {
        navigator.clipboard
          .writeText(path)
          .then(() => {
            console.log("Path copied to clipboard");
            // Optional: You could add a toast notification here
          })
          .catch((err) => {
            console.error("Failed to copy path:", err);
          });
      }
    },

    showConfigInfo(configName) {
      this.loadConfigDetails(configName);
      this.openInfoModal = configName;
    },

    closeInfoModal() {
      this.openInfoModal = null;
      this.activeModalTab = "overview"; // Reset to overview tab
    },

    getAccessToken,

    async loadSingleFileConfig(accessToken, mapping) {
      const { fetchJsonFile } = await import("@/azure-devops-api.js");
      const fileData = await fetchJsonFile(
        accessToken,
        mapping.repoId,
        mapping.path,
      );
      const rawConfig = JSON.parse(fileData.content);

      return {
        description: mapping.description,
        category: mapping.category,
        path: mapping.path,
        type: mapping.type,
        impact: mapping.impact,
        settings: this.processRawConfigToSettings(rawConfig, mapping),
        rawConfig: rawConfig,
      };
    },

    async loadDirectoryConfig(accessToken, mapping) {
      try {
        const { fetchDirectoryContents, fetchJsonFile } = await import(
          "@/azure-devops-api.js"
        );

        let allSettings = [];
        let combinedConfig = {};
        let fileCount = 0;

        try {
          const directoryContents = await fetchDirectoryContents(
            accessToken,
            mapping.repoId,
            mapping.path,
          );

          const jsonFiles = directoryContents.filter(
            (item) =>
              item.gitObjectType === "blob" && item.path.endsWith(".json"),
          );

          fileCount = jsonFiles.length;

          // Fetch all files in parallel
          const fileResults = await Promise.allSettled(
            jsonFiles.map(async (file) => {
              const fileData = await fetchJsonFile(
                accessToken,
                mapping.repoId,
                file.path,
              );
              const config = JSON.parse(fileData.content);
              const fileName = file.path.split("/").pop().replace(".json", "");
              return { config, fileName, path: file.path };
            }),
          );

          for (const result of fileResults) {
            if (result.status === "fulfilled") {
              const { config, fileName } = result.value;
              combinedConfig[fileName] = config;
              const fileSettings = this.processRawConfigToSettings(
                config,
                mapping,
                fileName,
              );
              allSettings.push(...fileSettings);
            } else {
              console.warn(`Failed to load file:`, result.reason);
              allSettings.push({
                name: `File: Unknown`,
                value: "Failed to load",
                description: `Error loading this configuration file: ${result.reason?.message}`,
                rawProperty: "error",
              });
            }
          }

          allSettings.unshift({
            name: "Directory Summary",
            value: `${fileCount} configuration files`,
            description: `This directory contains ${fileCount} JSON configuration files`,
            rawProperty: "summary",
          });
        } catch (directoryError) {
          console.warn(
            `Could not fetch directory contents for ${mapping.path}:`,
            directoryError,
          );

          allSettings = [
            {
              name: "Configuration Type",
              value: "Directory with multiple configuration files",
              description:
                "This configuration consists of multiple files in a directory structure",
              rawProperty: "directory",
            },
            {
              name: "Location",
              value: mapping.path,
              description:
                "Path to the configuration directory in the repository",
              rawProperty: "path",
            },
            {
              name: "Access Status",
              value: "Directory contents not accessible",
              description:
                "Unable to fetch the list of files in this directory. This might be due to permissions or the directory structure.",
              rawProperty: "access_error",
            },
          ];

          combinedConfig = {
            type: "directory",
            path: mapping.path,
            description: mapping.description,
            error: "Could not access directory contents",
          };
        }

        return {
          description: mapping.description,
          category: mapping.category,
          path: mapping.path,
          type: mapping.type,
          impact: mapping.impact,
          settings: allSettings,
          rawConfig: combinedConfig,
        };
      } catch (error) {
        console.error(`Error in loadDirectoryConfig:`, error);

        return {
          description: mapping.description,
          category: mapping.category,
          path: mapping.path,
          type: mapping.type,
          impact: mapping.impact,
          settings: [
            {
              name: "Configuration Type",
              value: "Directory with multiple configuration files",
              description:
                "This configuration consists of multiple files in a directory structure",
              rawProperty: "directory",
            },
            {
              name: "Location",
              value: mapping.path,
              description:
                "Path to the configuration directory in the repository",
              rawProperty: "path",
            },
            {
              name: "Loading Error",
              value: "Failed to load directory details",
              description: error.message,
              rawProperty: "error",
            },
          ],
          rawConfig: {
            type: "directory",
            path: mapping.path,
            description: mapping.description,
            error: error.message,
          },
        };
      }
    },

    async loadMultipleFilesConfig(accessToken, mapping) {
      const { fetchJsonFile } = await import("@/azure-devops-api.js");
      let combinedConfig = {};
      let allSettings = [];

      // Load all files in the paths array
      for (let i = 0; i < mapping.paths.length; i++) {
        const path = mapping.paths[i];
        try {
          const fileData = await fetchJsonFile(
            accessToken,
            mapping.repoId,
            path,
          );
          const config = JSON.parse(fileData.content);

          // Add to combined config with file name as key
          const fileName = path.split("/").pop().replace(".json", "");
          combinedConfig[fileName] = config;

          // Add settings from this file
          const fileSettings = this.processRawConfigToSettings(
            config,
            mapping,
            fileName,
          );
          allSettings.push(...fileSettings);
        } catch (error) {
          console.warn(`Failed to load ${path}:`, error);
          allSettings.push({
            name: `File: ${path.split("/").pop()}`,
            value: "Failed to load",
            description: `Error loading this configuration file: ${error.message}`,
            rawProperty: "error",
          });
        }
      }

      return {
        description: mapping.description,
        category: mapping.category,
        path: "Multiple files",
        type: mapping.type,
        impact: mapping.impact,
        settings: allSettings,
        rawConfig: combinedConfig,
      };
    },

    async loadFunctionConfig(mapping) {
      // For function-based configurations, return info about the function
      return {
        description: mapping.description,
        category: mapping.category,
        path: "PowerShell Function",
        type: mapping.type,
        impact: mapping.impact,
        settings: [
          {
            name: "Function Name",
            value: mapping.functionName,
            description: "PowerShell function that will be executed",
            rawProperty: "functionName",
          },
          {
            name: "Command",
            value: mapping.command,
            description: "PowerShell command that will be run",
            rawProperty: "command",
          },
          {
            name: "Execution Type",
            value: "PowerShell Function Call",
            description:
              "This configuration runs a PowerShell function rather than applying a JSON configuration",
            rawProperty: "type",
          },
        ],
        rawConfig: {
          type: "function",
          functionName: mapping.functionName,
          command: mapping.command,
          description: mapping.description,
        },
      };
    },

    processRawConfigToSettings(rawConfig, mapping, fileName = "") {
      const settings = [];

      // Add file name if provided (for multiple files)
      if (fileName) {
        settings.push({
          name: "Source File",
          value: fileName,
          description: "Configuration file containing these settings",
          rawProperty: "fileName",
        });
      }

      // Process different types of configurations
      if (typeof rawConfig === "object" && rawConfig !== null) {
        // Take first 6 properties as key settings
        const entries = Object.entries(rawConfig).slice(0, 6);

        entries.forEach(([key, value]) => {
          settings.push({
            name: key.replace(/([A-Z])/g, " $1").trim(),
            value: this.formatConfigValue(value),
            description: `Configuration setting: ${key}`,
            rawProperty: key,
          });
        });

        // If there are more properties, add a summary
        if (Object.keys(rawConfig).length > 6) {
          settings.push({
            name: "Additional Settings",
            value: `${Object.keys(rawConfig).length - 6} more settings`,
            description:
              "Additional configuration properties not shown in summary",
            rawProperty: "additionalCount",
          });
        }
      }

      // If no settings found, add a default message
      if (settings.length === 0) {
        settings.push({
          name: "Configuration",
          value: "No detailed settings available",
          description:
            "This configuration does not expose individual settings for preview",
          rawProperty: "none",
        });
      }

      return settings;
    },

    formatConfigValue(value) {
      if (typeof value === "boolean") {
        return value ? "Enabled" : "Disabled";
      }
      if (Array.isArray(value)) {
        return value.length > 0
          ? `Array (${value.length} items)`
          : "Empty Array";
      }
      if (value === null || value === "") {
        return "Not Set";
      }
      if (typeof value === "object") {
        return "Object Configuration";
      }
      if (typeof value === "string" && value.length > 50) {
        return value.substring(0, 50) + "...";
      }
      return String(value);
    },

    formatRawConfig(configDetails) {
      if (!configDetails) return "";

      if (configDetails.type === "function") {
        return JSON.stringify(
          {
            type: configDetails.type,
            functionName: configDetails.rawConfig.functionName,
            command: configDetails.rawConfig.command,
            description: configDetails.description,
            category: configDetails.category,
            impact: configDetails.impact,
          },
          null,
          2,
        );
      }

      return JSON.stringify(configDetails.rawConfig || {}, null, 2);
    },

    copyToClipboard(obj) {
      const text = JSON.stringify(obj, null, 2);
      navigator.clipboard.writeText(text).then(() => {
        // You could show a toast notification here
        console.log("Configuration copied to clipboard");
      });
    },

    flattenJsonTree(obj, depth = 0, parentPath = '', hidden = false) {
      if (obj === null || obj === undefined) {
        return [{ key: null, type: 'null', displayValue: 'null', depth, path: parentPath, isExpandable: false, hidden }];
      }
      if (typeof obj !== 'object') {
        let type = typeof obj;
        let displayValue = JSON.stringify(obj);
        if (type === 'string') displayValue = '"' + obj + '"';
        return [{ key: null, type, displayValue, depth, path: parentPath, isExpandable: false, hidden }];
      }

      const isArray = Array.isArray(obj);
      const entries = isArray ? obj.map((v, i) => [String(i), v]) : Object.entries(obj);
      const childCount = entries.length;
      const path = parentPath || 'root';
      const isExpanded = this.expandedJsonNodes[path] !== undefined ? this.expandedJsonNodes[path] : (depth < 1);

      if (!isExpanded && parentPath) {
        this.expandedJsonNodes[path] = false;
      }

      const nodes = [{
        key: null,
        type: isArray ? 'array' : 'object',
        displayValue: '',
        depth,
        path,
        isExpandable: true,
        childCount,
        hidden,
      }];

      if (isExpanded) {
        entries.forEach(([k, v]) => {
          const childPath = path + '.' + k;
          if (v !== null && v !== undefined && typeof v === 'object') {
            const childNodes = this.flattenJsonTree(v, depth + 1, childPath, false);
            if (childNodes.length > 0 && childNodes[0].key === null && childNodes[0].isExpandable) {
              childNodes[0].key = isArray ? String(k) : k;
            }
            nodes.push(...childNodes);
          } else {
            let valType = v === null ? 'null' : typeof v;
            let displayValue = v === null ? 'null' : (typeof v === 'string' ? '"' + v + '"' : JSON.stringify(v));
            nodes.push({
              key: isArray ? String(k) : k,
              type: valType,
              displayValue,
              depth: depth + 1,
              path: childPath,
              isExpandable: false,
              hidden: false,
            });
          }
        });
      }

      return nodes;
    },

    toggleJsonNode(path) {
      this.expandedJsonNodes[path] = !this.expandedJsonNodes[path];
    },

    jsonPreviewText(node) {
      const label = node.type === 'array' ? 'Array' : 'Object';
      const bracket = node.type === 'array' ? '[' + node.childCount + ']' : '{' + node.childCount + '}';
      return label + ' ' + bracket;
    },

    toggleAllJsonNodes(configDetails, expand) {
      if (!configDetails || !configDetails.rawConfig) return;
      const toggleRecursive = (obj, path) => {
        if (obj === null || typeof obj !== 'object') return;
        this.expandedJsonNodes[path] = expand;
        if (obj === undefined || obj === null) return;
        const isArray = Array.isArray(obj);
        const entries = isArray ? obj.map((v, i) => [String(i), v]) : Object.entries(obj);
        entries.forEach(([k, v]) => {
          if (v !== null && typeof v === 'object') {
            toggleRecursive(v, path + '.' + k);
          }
        });
      };
      toggleRecursive(configDetails.rawConfig, 'root');
    },

    getExpandableJsonCount(configDetails) {
      if (!configDetails || !configDetails.rawConfig || typeof configDetails.rawConfig !== 'object') return 0;
      let count = 0;
      const traverse = (obj) => {
        if (obj === null || typeof obj !== 'object') return;
        count++;
        const isArray = Array.isArray(obj);
        const entries = isArray ? obj.map((v, i) => [String(i), v]) : Object.entries(obj);
        entries.forEach(([, v]) => {
          if (v !== null && typeof v === 'object') traverse(v);
        });
      };
      traverse(configDetails.rawConfig);
      return count;
    },

    hasConfigMapping(componentName) {
      return !!this.configurationMapping[componentName];
    },

    getConfigType(componentName) {
      return this.configurationMapping[componentName]?.type;
    },

    getConfigImpact(componentName) {
      return this.configurationMapping[componentName]?.impact || "Medium";
    },

    // Validate client code in the new form
    validateNewClientCode() {
      const clientCodePattern = /^[A-Z]{3}$/;
      if (!clientCodePattern.test(this.newClientCode)) {
        this.newClientCodeError =
          "Client code must be exactly 3 uppercase letters.";
        this.newClientCodeSuccess = "";
        this.newClientCodeValidated = false;
      } else {
        this.newClientCodeError = "";
        this.newClientCodeSuccess = `Client Code is valid!`;
        this.newClientCodeValidated = true;
      }
    },

    async loadConfigDetails(componentName) {
      if (this.configDetails[componentName] || this.loading[componentName])
        return;

      // OLD: this.$set(this.loading, componentName, true);
      this.loading[componentName] = true;

      // OLD: this.$set(this.error, componentName, null);
      this.error[componentName] = null;

      try {
        const mapping = this.configurationMapping[componentName];
        if (!mapping) {
          throw new Error("Configuration mapping not found");
        }

        // Get access token
        const accessToken = await this.getAccessToken();
        let processedDetails;

        switch (mapping.type) {
          case "single_file":
            processedDetails = await this.loadSingleFileConfig(
              accessToken,
              mapping,
            );
            break;

          case "directory":
            processedDetails = await this.loadDirectoryConfig(
              accessToken,
              mapping,
            );
            break;

          case "multiple_files":
            processedDetails = await this.loadMultipleFilesConfig(
              accessToken,
              mapping,
            );
            break;

          case "function":
            processedDetails = await this.loadFunctionConfig(mapping);
            break;

          default:
            throw new Error(`Unknown configuration type: ${mapping.type}`);
        }

        this.configDetails[componentName] = processedDetails;
      } catch (err) {
        console.error(`Error loading config for ${componentName}:`, err);
        this.error[
          componentName
        ] = `Failed to load configuration: ${err.message}`;
      } finally {
        this.loading[componentName] = false;
      }
    },

    async validateTenantBeforeConsent() {
      try {
        const tenantId = await this.resolveTenantIdFromDomain(this.newDomain);

        if (tenantId) {
          console.log("✅ Pre-validated tenant ID:", tenantId);
          // Store this for later use
          sessionStorage.setItem("preValidatedTenantId", tenantId);
          return tenantId;
        }

        return null;
      } catch (error) {
        console.error("Pre-consent validation failed:", error);
        return null;
      }
    },
    // Enhanced startAdminConsentFlow with diagnostic capabilities
    async startAdminConsentFlow() {
      if (!this.isNewClientInfoValid) {
        alert(
          "Please complete all required fields with valid information before proceeding.",
        );
        return;
      }

      // Pre-validate the tenant
      const preValidatedTenantId = await this.validateTenantBeforeConsent();

      // Store client information
      sessionStorage.setItem("newClientCode", this.newClientCode);
      sessionStorage.setItem("newHasOptionsCSP", this.newHasOptionsCSP);
      sessionStorage.setItem(
        "newCustomSubscriptionName",
        this.newCustomSubscriptionName,
      );
      sessionStorage.setItem("newDomain", this.newDomain);
      sessionStorage.setItem("enrollInPowerSupport", this.enrollInPowerSupport);
      sessionStorage.setItem("selectedRegion", this.selectedRegion);

      const clientId = "9d4e9287-a121-4937-b94d-e7be78d8d334";
      const redirectUri = `${window.location.origin}/tenant-baseline-options`;
      const timestamp = new Date().getTime();

      // If we have a pre-validated tenant ID, use tenant-specific consent
      let adminConsentUrl;
      if (preValidatedTenantId) {
        // Use tenant-specific admin consent for more reliable results
        adminConsentUrl = `https://login.microsoftonline.com/${preValidatedTenantId}/adminconsent?client_id=${clientId}&redirect_uri=${redirectUri}&state=${timestamp}`;
        console.log(
          "Using tenant-specific admin consent with pre-validated tenant ID",
        );
      } else {
        // Fall back to common endpoint
        adminConsentUrl = `https://login.microsoftonline.com/common/adminconsent?client_id=${clientId}&redirect_uri=${redirectUri}&state=${timestamp}`;
        console.log("Using common admin consent endpoint");
      }

      console.log("Starting admin consent:", {
        clientId,
        redirectUri,
        timestamp,
        domain: this.newDomain,
        preValidatedTenantId,
        url: adminConsentUrl,
      });

      window.location.href = adminConsentUrl;
    },

    // Add method to check if service principal actually exists
    async checkServicePrincipalExists(domain) {
      try {
        console.log("Checking if service principal exists for domain:", domain);

        // This is a diagnostic check - we'll try to resolve the tenant and check
        const tenantId = await this.resolveTenantIdFromDomain(domain);

        if (!tenantId) {
          console.log("Could not resolve tenant ID from domain");
          return { exists: false, error: "Could not resolve tenant ID" };
        }

        console.log("Resolved tenant ID:", tenantId);

        // Try to check if we can get basic tenant info (this doesn't require the app to exist)
        const tenantInfoUrl = `https://login.microsoftonline.com/${tenantId}/.well-known/openid-configuration`;
        const response = await fetch(tenantInfoUrl);

        if (response.ok) {
          const config = await response.json();
          console.log("Tenant info retrieved successfully:", config.issuer);
          return { exists: false, tenantId, tenantAccessible: true };
        } else {
          console.log("Could not access tenant info");
          return { exists: false, error: "Tenant not accessible" };
        }
      } catch (error) {
        console.error("Error checking service principal:", error);
        return { exists: false, error: error.message };
      }
    },

    async resolveTenantIdFromDomain(domain) {
      try {
        console.log("Resolving tenant ID for domain:", domain);

        // Try multiple domain formats (avoid appending .onmicrosoft.com to an already-full domain)
        const trimmedDomain = domain.trim();
        const baseName = trimmedDomain
          .replace(/\.onmicrosoft\.com$/i, "")
          .replace(/\.(com|net|org|co|io)$/i, "");
        const domainVariants = [
          ...new Set([trimmedDomain, `${baseName}.onmicrosoft.com`]),
        ];

        for (const domainVariant of domainVariants) {
          try {
            const response = await fetch(
              `https://login.microsoftonline.com/${domainVariant}/v2.0/.well-known/openid-configuration`,
              {
                method: "GET",
                mode: "cors",
              },
            );

            if (response.ok) {
              const config = await response.json();
              const issuer = config.issuer;
              const tenantId = issuer.split("/")[3];
              console.log(
                `✅ Resolved tenant ID ${tenantId} for domain variant: ${domainVariant}`,
              );
              return tenantId;
            }
          } catch (variantError) {
            console.log(
              `❌ Failed to resolve tenant for ${domainVariant}:`,
              variantError.message,
            );
          }
        }

        throw new Error("Could not resolve tenant ID from any domain variant");
      } catch (error) {
        console.error("Error resolving tenant ID:", error);
        return null;
      }
    },

    // Verifies a GUID is a real Entra tenant via the v2 discovery endpoint.
    // Used to guard against scraping trace/correlation IDs out of error text.
    async isValidTenantId(tenantId) {
      try {
        const response = await fetch(
          `https://login.microsoftonline.com/${tenantId}/v2.0/.well-known/openid-configuration`,
          { method: "GET", mode: "cors" },
        );
        return response.ok;
      } catch {
        return false;
      }
    },

    // Enhanced error handler with more diagnostic info
    async handleAdminConsentError(error, errorDescription, state) {
      console.error("Admin consent error details:", {
        error,
        errorDescription,
        state,
        timestamp: new Date().toISOString(),
        domain: this.newDomain,
      });

      const startTime = sessionStorage.getItem("consentStartTime");
      if (startTime) {
        const duration = new Date().getTime() - parseInt(startTime);
        console.log("Consent flow duration:", duration, "ms");
      }

      if (
        error === "access_denied" &&
        errorDescription.includes("service principal name is already present")
      ) {
        // Extract tenant ID from error description
        const tenantMatch = errorDescription.match(/tenant\s+([a-f0-9-]{36})/i);
        const extractedTenantId = tenantMatch ? tenantMatch[1] : null;

        console.log("Extracted tenant ID from error:", extractedTenantId);

        // Check if this matches what we expect
        const expectedTenantId = await this.resolveTenantIdFromDomain(
          this.newDomain,
        );
        console.log("Expected tenant ID from domain:", expectedTenantId);

        if (
          extractedTenantId &&
          expectedTenantId &&
          extractedTenantId === expectedTenantId
        ) {
          console.log("Tenant IDs match - proceeding with extracted tenant ID");
          this.clientTenantId = extractedTenantId;
          sessionStorage.setItem("clientTenantId", extractedTenantId);

          // Try to continue with authentication
          try {
            await this.promptGlobalAdminLogin();
          } catch (authError) {
            console.error(
              "Authentication failed after consent error:",
              authError,
            );
            this.showConsentErrorOptions();
          }
        } else {
          console.warn("Tenant ID mismatch or missing - showing options");
          this.showConsentErrorOptions(extractedTenantId, expectedTenantId);
        }
      } else {
        console.error("Unexpected admin consent error:", error);
        alert(
          `Admin consent failed: ${error}\n\n${
            errorDescription || "Please try again or contact support."
          }\n\nCheck the browser console for more details.`,
        );
      }
    },

    // Method to show consent error recovery options
    showConsentErrorOptions(extractedTenantId = null, expectedTenantId = null) {
      const options = [
        "1. Try again with force consent",
        "2. Try tenant-specific consent",
        "3. Try alternative OAuth flow",
        "4. Manual tenant ID entry",
        "5. Cancel and contact support",
      ];

      const choice = prompt(
        `Admin consent failed due to existing service principal.\n\n` +
          `Extracted Tenant ID: ${extractedTenantId || "None"}\n` +
          `Expected Tenant ID: ${expectedTenantId || "None"}\n\n` +
          `Choose recovery option:\n${options.join("\n")}\n\n` +
          `Enter 1-5:`,
      );

      switch (choice) {
        case "1":
          sessionStorage.setItem("consentMethod", "force_consent");
          this.startAdminConsentFlow();
          break;
        case "2":
          sessionStorage.setItem("consentMethod", "tenant_specific");
          this.startAdminConsentFlow();
          break;
        case "3":
          sessionStorage.setItem("consentMethod", "oauth_flow");
          this.startAdminConsentFlow();
          break;
        case "4":
          this.manualTenantIdEntry();
          break;
        case "5":
        default:
          alert(
            "Please contact support with the error details from the browser console.",
          );
          break;
      }
    },

    // Method for manual tenant ID entry
    manualTenantIdEntry() {
      const manualTenantId = prompt(
        "Please provide your Tenant ID to continue.\n\n" +
          "You can find this in:\n" +
          "Azure Portal > Azure Active Directory > Overview > Tenant ID",
      );

      if (manualTenantId && manualTenantId.match(/^[a-f0-9-]{36}$/i)) {
        this.clientTenantId = manualTenantId;
        sessionStorage.setItem("clientTenantId", manualTenantId);
        this.promptGlobalAdminLogin();
      } else if (manualTenantId) {
        alert("Invalid Tenant ID format. Please try again.");
        this.manualTenantIdEntry();
      }
    },

    async discoverTenantViaEndpoint(domain) {
      try {
        // Microsoft provides a tenant discovery endpoint
        const discoveryUrl = `https://login.microsoftonline.com/${domain}/v2.0/.well-known/openid-configuration`;

        const response = await fetch(discoveryUrl, {
          method: "GET",
          mode: "cors",
        });

        if (response.ok) {
          const config = await response.json();
          // Extract tenant ID from the issuer URL
          const issuer = config.issuer;
          const tenantId = issuer.split("/")[3];
          return tenantId;
        }

        // Try alternative format (skip if the domain already has the suffix)
        if (!/\.onmicrosoft\.com$/i.test(domain)) {
          const altUrl = `https://login.microsoftonline.com/${domain}.onmicrosoft.com/v2.0/.well-known/openid-configuration`;
          const altResponse = await fetch(altUrl, {
            method: "GET",
            mode: "cors",
          });

          if (altResponse.ok) {
            const altConfig = await altResponse.json();
            const issuer = altConfig.issuer;
            const tenantId = issuer.split("/")[3];
            return tenantId;
          }
        }

        return null;
      } catch (error) {
        console.error("Tenant endpoint discovery failed:", error);
        return null;
      }
    },

    // Helper method: Extract tenant from current authentication context
    async extractTenantFromCurrentAuth() {
      try {
        // If user is already authenticated with MSAL, we can extract tenant info
        const accounts = msalOnboardInstance.getAllAccounts();

        if (accounts.length > 0) {
          const account = accounts[0];
          console.log("Current MSAL account:", account);

          // Extract tenant ID from the account
          if (account.tenantId) {
            return account.tenantId;
          }

          // Extract from home account ID (format: objectId.tenantId)
          if (account.homeAccountId && account.homeAccountId.includes(".")) {
            const tenantId = account.homeAccountId.split(".")[1];
            if (tenantId && tenantId.match(/^[a-f0-9-]{36}$/i)) {
              return tenantId;
            }
          }
        }

        return null;
      } catch (error) {
        console.error("Failed to extract tenant from current auth:", error);
        return null;
      }
    },

    async discoverTenantFromOAuth() {
      try {
        console.log("Attempting OAuth discovery for tenant ID");

        // Use Microsoft's tenant discovery API with the domain
        if (this.newDomain) {
          const tenantId = await this.discoverTenantViaEndpoint(this.newDomain);
          if (tenantId) {
            console.log("✅ Discovered tenant ID via endpoint:", tenantId);
            return tenantId;
          }
        }

        // Fallback: Try to extract from current user's context if they're already authenticated
        const tenantId = await this.extractTenantFromCurrentAuth();
        if (tenantId) {
          console.log("✅ Extracted tenant ID from current auth:", tenantId);
          return tenantId;
        }

        console.log("❌ OAuth discovery methods exhausted");
        return null;
      } catch (error) {
        console.error("OAuth discovery failed:", error);
        return null;
      }
    },

    // Enhanced URL parameter handler
    async handleUrlParameters() {
      const urlParams = new URLSearchParams(window.location.search);
      const tenantId = urlParams.get("tenant");
      const error = urlParams.get("error");
      const errorDescription = urlParams.get("error_description");
      const adminConsent = urlParams.get("admin_consent");
      const state = urlParams.get("state");

      console.log("Admin consent result:", {
        tenantId,
        error,
        adminConsent,
        errorDescription: errorDescription
          ? decodeURIComponent(errorDescription)
          : null,
        state,
      });

      // Clear URL parameters
      if (tenantId || error || adminConsent) {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );
      }

      let extractedTenantId = null;
      let consentWorked = false;

      // Method 1: Direct tenant ID from URL
      if (tenantId) {
        extractedTenantId = tenantId;
        consentWorked = true;
        console.log("✅ Direct tenant ID from URL:", tenantId);
      }

      // Method 2: If adminConsent=True, try to resolve tenant from domain
      if (adminConsent === "True" && !extractedTenantId) {
        console.log("✅ AdminConsent=True - attempting domain resolution");
        extractedTenantId = await this.resolveTenantIdFromDomain(
          this.newDomain,
        );
        if (extractedTenantId) {
          consentWorked = true;
          console.log("✅ Resolved tenant ID from domain:", extractedTenantId);
        }
      }

      // Method 3: Extract a tenant-qualified tenant ID from the error message
      // (e.g. AADSTS650051 "... tenant <guid>"). Bare UUID matching is NOT trusted:
      // error text also carries trace/correlation IDs (e.g. AADSTS90092), which
      // previously got mistaken for tenant IDs.
      if (!extractedTenantId && errorDescription) {
        const decodedError = decodeURIComponent(errorDescription);
        const tenantMatch = decodedError.match(
          /tenant\s+'?([a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12})'?/i,
        );

        if (tenantMatch) {
          const candidate = tenantMatch[1];
          if (await this.isValidTenantId(candidate)) {
            extractedTenantId = candidate;
            consentWorked = true;
            console.log(
              "✅ Extracted & validated tenant ID from error:",
              extractedTenantId,
            );
          } else {
            console.warn(
              "Ignoring tenant-qualified UUID from error - failed tenant validation:",
              candidate,
            );
          }
        }
      }

      // Method 4: If consent successful but still no tenant ID, try OAuth discovery
      if (adminConsent === "True" && !extractedTenantId) {
        extractedTenantId = await this.discoverTenantFromOAuth();
        if (extractedTenantId) {
          consentWorked = true;
          console.log("✅ Discovered tenant ID via OAuth:", extractedTenantId);
        }
      }

      // Presence of error= means consent failed server-side, even when
      // admin_consent=True is also present (e.g. AADSTS650051 transient SP race,
      // AADSTS90092 backend failure). Recover only with a validated tenant ID;
      // otherwise treat as a consent failure and offer retry.
      if (error) {
        if (extractedTenantId && consentWorked) {
          this.clientTenantId = extractedTenantId;
          sessionStorage.setItem("clientTenantId", extractedTenantId);
          console.log(
            "🎯 Recovering from consent error with validated tenant:",
            extractedTenantId,
          );
          this.promptGlobalAdminLogin();
        } else {
          this.handleConsentFailure(error, errorDescription);
        }
        return;
      }

      // If we have a tenant ID and consent worked, proceed
      if (extractedTenantId && consentWorked) {
        this.clientTenantId = extractedTenantId;
        sessionStorage.setItem("clientTenantId", extractedTenantId);
        console.log("🎯 Proceeding with tenant:", extractedTenantId);
        this.promptGlobalAdminLogin();
        return;
      }

      // If consent worked but no tenant ID, prompt user
      if (adminConsent === "True" && !extractedTenantId) {
        this.handleConsentWithoutTenantId();
        return;
      }

      // Check for previously stored tenant ID
      const savedTenantId = sessionStorage.getItem("clientTenantId");
      if (savedTenantId) {
        this.clientTenantId = savedTenantId;
        this.promptGlobalAdminLogin();
      }
    },

    async handleConsentWithoutTenantId() {
      console.log("Consent successful but tenant ID missing - asking user");

      const manualTenantId = prompt(
        "✅ Enterprise app created successfully!\n\n" +
          "Please provide your Tenant ID to continue:\n\n" +
          "(You can find this in Azure Portal > Azure Active Directory > Overview > Tenant ID)",
      );

      if (manualTenantId && manualTenantId.match(/^[a-f0-9-]{36}$/i)) {
        this.clientTenantId = manualTenantId;
        sessionStorage.setItem("clientTenantId", manualTenantId);
        console.log("✅ Manual tenant ID provided:", manualTenantId);
        this.promptGlobalAdminLogin();
      } else if (manualTenantId) {
        alert("Invalid Tenant ID format. Please try again.");
        this.handleConsentWithoutTenantId(); // Retry
      } else {
        alert(
          "Tenant ID is required to continue. Please try the onboarding process again.",
        );
      }
    },

    // Handle actual consent failures
    handleConsentFailure(error, errorDescription) {
      console.error("Admin consent failed:", { error, errorDescription });

      const retryChoice = confirm(
        `❌ Enterprise app creation failed.\n\n` +
          `Error: ${error}\n\n` +
          `Would you like to try again?\n\n` +
          `Click OK to retry, or Cancel to exit.`,
      );

      if (retryChoice) {
        console.log("🔄 Retrying admin consent...");
        // Add a small delay and retry
        setTimeout(() => {
          this.startAdminConsentFlow();
        }, 2000);
      } else {
        alert("Please contact support if the issue persists.");
      }
    },

    // Method to prompt login for global admin
    async promptGlobalAdminLogin() {
      try {
        const loginResponse = await msalOnboardInstance.loginPopup({
          scopes: [
            "User.Read",
            "Application.Read.All",
            "Application.ReadWrite.All",
            "RoleManagement.ReadWrite.Directory",
            "Directory.Read.All",
            "https://management.azure.com/user_impersonation",
          ],
        });

        // Guard: the signed-in account must belong to the tenant being onboarded.
        // A different tenant here means the wrong account/session was used
        // (e.g. an existing SSO session slipped through /common).
        const signedInTenantId =
          loginResponse.account && loginResponse.account.tenantId;
        if (
          this.clientTenantId &&
          signedInTenantId &&
          signedInTenantId.toLowerCase() !== this.clientTenantId.toLowerCase()
        ) {
          this.globalAdminToken = null;
          this.onboardAccount = null;
          alert(
            `Tenant mismatch: you signed in to tenant ${signedInTenantId}, ` +
              `but this onboarding is for tenant ${this.clientTenantId}. ` +
              `Please sign in with an admin account from the client tenant.`,
          );
          return;
        }

        this.onboardAccount = loginResponse.account;
        this.globalAdminToken = loginResponse.accessToken;

        // Auth complete after a consent round-trip - advance to role assignment
        if (this.onboardStep === 2) this.onboardStep = 3;
      } catch (err) {
        console.error("Login failed after admin consent:", err);
      }
    },

    // Method to assign all required roles in one click
    async assignRequiredRoles() {
      // Reset status messages
      this.readerRoleStatus = "";
      this.policyContributorRoleStatus = "";
      this.ContributorRoleStatus = "";
      this.teamsAdminRoleStatus = "";
      this.globalAdminRoleStatus = "";
      this.exchangeAdminRoleStatus = "";
      this.AuthPolicyAdminStatus = "";
      this.PrivAuthAdminStatus = "";
      this.allRolesAssigned = false;

      // Enterprise app name - hardcoded as specified
      const appName = "Aegis - Deploy - Monitor - Fix";

      try {
        const account =
          this.onboardAccount || msalOnboardInstance.getAllAccounts()[0];
        if (!account) throw new Error("No authenticated account found.");

        // Get Graph token for AAD role assignments
        let graphToken;
        try {
          graphToken = (
            await msalOnboardInstance.acquireTokenSilent({
              scopes: ["https://graph.microsoft.com/.default"],
              account,
            })
          ).accessToken;
        } catch (silentErr) {
          const popupResult = await msalOnboardInstance.acquireTokenPopup({
            scopes: ["https://graph.microsoft.com/.default"],
          });
          graphToken = popupResult.accessToken;
        }

        // Get ARM token for subscription role assignments
        let armToken;
        try {
          armToken = (
            await msalOnboardInstance.acquireTokenSilent({
              scopes: ["https://management.azure.com/.default"],
              account,
            })
          ).accessToken;
        } catch (silentErr) {
          const popupResult = await msalOnboardInstance.acquireTokenPopup({
            scopes: ["https://management.azure.com/.default"],
          });
          armToken = popupResult.accessToken;
        }

        // Get the subscription ID for "Options IT" or custom subscription
        const subRes = await fetch(
          "https://management.azure.com/subscriptions?api-version=2020-01-01",
          {
            headers: { Authorization: `Bearer ${armToken}` },
          },
        );

        const subs = await subRes.json();

        // Use Options IT or custom subscription name based on selection
        const subscriptionName =
          this.newHasOptionsCSP === "yes"
            ? "Options IT"
            : this.newCustomSubscriptionName;

        console.log("Looking for subscription with name:", subscriptionName);
        console.log(
          "Available subscriptions:",
          subs.value.map((s) => s.displayName),
        );

        const subscription = subs.value.find(
          (s) => s.displayName === subscriptionName,
        );

        if (!subscription) {
          // Show more detailed error message with available subscription names
          const availableNames = subs.value
            .map((s) => s.displayName)
            .join(", ");
          throw new Error(
            `Subscription "${subscriptionName}" not found. Available subscriptions: ${availableNames}`,
          );
        }

        const subscriptionId = subscription.subscriptionId;

        // Get Service Principal objectId by displayName
        const spRes = await fetch(
          `https://graph.microsoft.com/v1.0/servicePrincipals?$filter=displayName eq '${appName}'`,
          {
            headers: { Authorization: `Bearer ${graphToken}` },
          },
        );

        const spData = await spRes.json();
        const principalId = spData.value[0]?.id;
        if (!principalId)
          throw new Error(`Enterprise App "${appName}" not found`);

        const assignArmRoles = async (token) => {
          // 1. Assign Reader Role
          await this.assignSubscriptionRole(
            token,
            subscriptionId,
            principalId,
            "acdd72a7-3385-48ef-bd42-f606fba81ae7", // Reader role ID
            "Reader",
          );

          // 2.A Assign Resource Policy Contributor Role
          await this.assignSubscriptionRole(
            token,
            subscriptionId,
            principalId,
            "36243c78-bf99-498c-9df9-86d9f8d28608", // Resource Policy Contributor role ID
            "Resource Policy Contributor",
          );

          // 2.B Assign Contributor Role
          await this.assignSubscriptionRole(
            token,
            subscriptionId,
            principalId,
            "b24988ac-6180-42a0-ab88-20f7382dd24c", // Contributor role ID
            "Contributor",
          );
        };

        try {
          await assignArmRoles(armToken);
        } catch (roleError) {
          // Azure enforces MFA for resource management writes (aka.ms/MFAforAzure).
          // Re-acquire the ARM token with an MFA claims challenge and retry once.
          if (
            /RequestDisallowedByAzure|MFAforAzure|authenticating through MFA/i.test(
              (roleError && roleError.message) || "",
            )
          ) {
            console.warn(
              "ARM write rejected (MFA required) - re-acquiring token with MFA claims challenge",
            );
            const mfaResult = await msalOnboardInstance.acquireTokenPopup({
              scopes: ["https://management.azure.com/.default"],
              account,
              claims: JSON.stringify({
                access_token: {
                  acrs: {
                    essential: true,
                    value: "http://schemas.microsoft.com/claims/multipleauthn",
                  },
                },
              }),
            });
            await assignArmRoles(mfaResult.accessToken);
          } else {
            throw roleError;
          }
        }

        // 3. Assign Teams Administrator Role
        await this.assignDirectoryRole(
          graphToken,
          principalId,
          "69091246-20e8-4a56-aa4d-066075b2a7a8", // Teams Administrator role ID
          "Teams Administrator",
        );

        // 4. Assign Global Administrator Role
        await this.assignDirectoryRole(
          graphToken,
          principalId,
          "62e90394-69f5-4237-9190-012177145e10", // Global Administrator role ID
          "Global Administrator",
        );

        // 5. Assign Exchange Administrator Role
        await this.assignDirectoryRole(
          graphToken,
          principalId,
          "29232cdf-9323-42fd-ade2-1d097af3e4de", // Exchange Administrator role ID
          "Exchange Administrator",
        );

        // 6. Assign Authentication Policy Administrator Role
        await this.assignDirectoryRole(
          graphToken,
          principalId,
          "0526716b-113d-4c15-b2c8-68e3c22b9f80", // Authentication Policy Administrator role ID
          "Authentication Policy Administrator",
        );

        // 7. Assign Privileged Authentication Administrator Role
        await this.assignDirectoryRole(
          graphToken,
          principalId,
          "7be44c8a-adaf-4e2a-84d6-ab2649e08a13", //Privileged Authentication Administrator role ID
          "Privileged Authentication Administrator",
        );

        // Check if all roles assigned successfully
        const allSuccess =
          this.readerRoleStatus.startsWith("✅") &&
          this.policyContributorRoleStatus.startsWith("✅") &&
          this.ContributorRoleStatus.startsWith("✅") &&
          this.teamsAdminRoleStatus.startsWith("✅") &&
          this.globalAdminRoleStatus.startsWith("✅") &&
          this.exchangeAdminRoleStatus.startsWith("✅");
        this.AuthPolicyAdminStatus.startsWith("✅");
        this.PrivAuthAdminStatus.startsWith("✅");

        if (allSuccess) {
          this.allRolesAssigned = true;
        }
      } catch (err) {
        console.error("Error assigning roles:", err);
        alert(`Error assigning roles: ${err.message}`);
      }
    },

    // Helper method to assign subscription roles (ARM)
    async assignSubscriptionRole(
      armToken,
      subscriptionId,
      principalId,
      roleDefinitionId,
      roleName,
    ) {
      try {
        const assignmentId = crypto.randomUUID();
        const result = await fetch(
          `https://management.azure.com/subscriptions/${subscriptionId}/providers/Microsoft.Authorization/roleAssignments/${assignmentId}?api-version=2020-04-01-preview`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${armToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              properties: {
                roleDefinitionId: `/subscriptions/${subscriptionId}/providers/Microsoft.Authorization/roleDefinitions/${roleDefinitionId}`,
                principalId: principalId,
                principalType: "ServicePrincipal",
              },
            }),
          },
        );

        if (!result.ok) {
          const error = await result.json();
          throw new Error(
            error.error?.message || `Failed to assign ${roleName} role`,
          );
        }

        if (roleName === "Reader") {
          this.readerRoleStatus = `✅ ${roleName} role successfully assigned to "Aegis - Deploy - Monitor - Fix"`;
        } else if (roleName === "Resource Policy Contributor") {
          this.policyContributorRoleStatus = `✅ ${roleName} role successfully assigned to "Aegis - Deploy - Monitor - Fix"`;
        } else if (roleName === "Contributor") {
          this.ContributorRoleStatus = `✅ ${roleName} role successfully assigned to "Aegis - Deploy - Monitor - Fix"`;
        }

        return true;
      } catch (err) {
        if (roleName === "Reader") {
          this.readerRoleStatus = `❌ Error assigning ${roleName} role: ${err.message}`;
        } else if (roleName === "Resource Policy Contributor") {
          this.policyContributorRoleStatus = `❌ Error assigning ${roleName} role: ${err.message}`;
        } else if (roleName === "Contributor") {
          this.ContributorRoleStatus = `❌ Error assigning ${roleName} role: ${err.message}`;
        }
        console.error(`Error assigning ${roleName} role:`, err);
        return false;
      }
    },

    // Helper method to assign directory roles (Graph API)
    async assignDirectoryRole(graphToken, principalId, roleId, roleName) {
      try {
        // Get directory role template
        const roleTemplate = await fetch(
          `https://graph.microsoft.com/v1.0/directoryRoleTemplates/${roleId}`,
          {
            headers: { Authorization: `Bearer ${graphToken}` },
          },
        );

        if (!roleTemplate.ok) {
          throw new Error(`Failed to get ${roleName} role template`);
        }

        // Activate the role if it's not already activated
        let roleInfo;
        const activeRoles = await fetch(
          `https://graph.microsoft.com/v1.0/directoryRoles?$filter=roleTemplateId eq '${roleId}'`,
          {
            headers: { Authorization: `Bearer ${graphToken}` },
          },
        );

        const activeRolesData = await activeRoles.json();

        if (activeRolesData.value.length === 0) {
          // Activate the role
          const activateResult = await fetch(
            `https://graph.microsoft.com/v1.0/directoryRoles`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${graphToken}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                roleTemplateId: roleId,
              }),
            },
          );

          if (!activateResult.ok) {
            throw new Error(`Failed to activate ${roleName} role`);
          }

          roleInfo = await activateResult.json();
        } else {
          roleInfo = activeRolesData.value[0];
        }

        // Assign the service principal to the role
        const assignResult = await fetch(
          `https://graph.microsoft.com/v1.0/directoryRoles/${roleInfo.id}/members/$ref`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${graphToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              "@odata.id": `https://graph.microsoft.com/v1.0/servicePrincipals/${principalId}`,
            }),
          },
        );

        if (!assignResult.ok) {
          const error = await assignResult.text();
          throw new Error(`Failed to assign ${roleName} role: ${error}`);
        }

        // Update the appropriate status
        if (roleName === "Teams Administrator") {
          this.teamsAdminRoleStatus = `✅ ${roleName} role successfully assigned to "Aegis - Deploy - Monitor - Fix"`;
        } else if (roleName === "Global Administrator") {
          this.globalAdminRoleStatus = `✅ ${roleName} role successfully assigned to "Aegis - Deploy - Monitor - Fix"`;
        } else if (roleName === "Exchange Administrator") {
          this.exchangeAdminRoleStatus = `✅ ${roleName} role successfully assigned to "Aegis - Deploy - Monitor - Fix"`;
        } else if (roleName === "Authentication Policy Administrator") {
          this.AuthPolicyAdminStatus = `✅ ${roleName} role successfully assigned to "Aegis - Deploy - Monitor - Fix"`;
        } else if (roleName === "Privileged Authentication Administrator") {
          this.PrivAuthAdminStatus = `✅ ${roleName} role successfully assigned to "Aegis - Deploy - Monitor - Fix"`;
        }

        return true;
      } catch (err) {
        // Update the appropriate status
        if (roleName === "Teams Administrator") {
          this.teamsAdminRoleStatus = `❌ Error assigning ${roleName} role: ${err.message}`;
        } else if (roleName === "Global Administrator") {
          this.globalAdminRoleStatus = `❌ Error assigning ${roleName} role: ${err.message}`;
        } else if (roleName === "Exchange Administrator") {
          this.exchangeAdminRoleStatus = `❌ Error assigning ${roleName} role: ${err.message}`;
        } else if (roleName === "Authentication Policy Administrator") {
          this.AuthPolicyAdminStatus = `❌ Error assigning ${roleName} role: ${err.message}`;
        } else if (roleName === "Privileged Authentication Administrator") {
          this.PrivAuthAdminStatus = `❌ Error assigning ${roleName} role: ${err.message}`;
        }

        console.error(`Error assigning ${roleName} role:`, err);
        return false;
      }
    },

    // Method to trigger the onboarding pipeline with collected information
    async triggerOnboardPipeline() {
      try {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length) {
          const accessTokenRequest = {
            account: accounts[0],
            scopes: ["499b84ac-1321-427f-aa17-267ca6975798/user_impersonation"],
          };
          const response = await msalInstance.acquireTokenSilent(
            accessTokenRequest,
          );
          const accessToken = response.accessToken;

          // Determine Subscription Name for $DefaultSubscriptionForLogin
          const subscriptionForLogin =
            this.newHasOptionsCSP === "yes"
              ? "Options IT"
              : this.newCustomSubscriptionName;

          // Create pipeline parameters with data collected from the new form
          const projectId = "1b238677-4a8d-4243-827e-88a0558c085a";
          const pipelineBuildId = "178"; // Using new pipeline ID as original onboard tab  was 75

          // Trigger the pipeline and get the run ID
          this.onboardPipelineStatus = {
            text: "Triggering New onboarding pipeline...",
            type: "info",
          };

          const runId = await triggerNewOnboardBuild(
            accessToken,
            projectId,
            pipelineBuildId,
            this.newClientCode,
            this.newDomain,
            subscriptionForLogin,
            this.clientTenantId,
            this.enrollInPowerSupport,
            this.selectedRegion,
          );

          if (runId) {
            this.onboardPipelineStatus = {
              text: "Pipeline triggered successfully! Processing your request...",
              type: "success",
            };

            // Register tenant in Entra backend for Graph collection
            registerEntraTenant({
              tenantId: this.clientTenantId,
              displayName: this.newClientCode,
              primaryDomain: this.newDomain,
              regionCode: azureRegionToCspCode(this.selectedRegion),
              customerId: null,
              defaultCurrency: 'GBP',
              credential: {
                authMode: 'client-secret-kv',
                keyvaultRef: 'Aegis-Enterprise-App-KeyVault-Management-Secret'
              }
            }).catch(function() {});

            // Start polling the pipeline status
            this.pollPipelineStatus(
              accessToken,
              projectId,
              pipelineBuildId,
              runId,
              (statusText, statusType) => {
                this.onboardPipelineStatus = {
                  text: statusText,
                  type: statusType,
                };
                // Onboarding finished - clear stashed session state so the
                // next client doesn't resume this one's tenant/form data
                if (statusType === "success") {
                  this.clearOnboardingSession();
                }
              },
            );
          } else {
            this.onboardPipelineStatus = {
              text: "Failed to trigger the pipeline.",
              type: "error",
            };
          }
        } else {
          throw new Error("No accounts available for authentication.");
        }
      } catch (error) {
        console.error("Error during pipeline execution:", error);
        this.onboardPipelineStatus = {
          text: "Error: " + error.message,
          type: "error",
        };
      }
    },

    // Updated pollPipelineStatus with callback support
    async pollPipelineStatus(
      accessToken,
      projectId,
      pipelineId,
      runId,
      statusCallback = null,
    ) {
      const pollingInterval = 5000; // Poll every 5 seconds
      const successMessageDuration = 3000; // Show success message for 3 seconds before updating

      // Use callback for status updates if provided, otherwise use pipelineStatusMessage
      const updateStatus = (text, type) => {
        if (statusCallback) {
          statusCallback(text, type);
        } else {
          this.pipelineStatusMessage = { text, type };
        }
      };

      // Preserve the success message for a set duration
      setTimeout(() => {
        updateStatus("", ""); // Clear the success message
        checkStatus(); // Start polling after success message duration
      }, successMessageDuration);

      const checkStatus = async () => {
        try {
          console.log("Checking pipeline status...");
          const status = await getPipelineStatus(
            accessToken,
            projectId,
            pipelineId,
            runId,
          );
          console.log("Pipeline status response: ", status);

          // Format the message based on state
          let message;
          if (status.state === "inProgress") {
            message = "Pipeline status: In Progress";
          } else if (status.result) {
            message = `Pipeline status: ${status.state} - ${status.result}`;
          } else {
            message = `Pipeline status: ${status.state}`;
          }

          updateStatus(
            message,
            status.result === "succeeded" ? "success" : "info",
          );

          // Stop polling if the pipeline is no longer running
          if (status.state !== "inProgress") {
            return;
          }
          // Continue polling
          setTimeout(checkStatus, pollingInterval);
        } catch (error) {
          console.error("Error checking pipeline status:", error);
          updateStatus(
            "Error checking pipeline status: " + error.message,
            "error",
          );
        }
      };
    },

    // Original method implementations
    async fetchPipelines() {
      try {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length) {
          const accessTokenRequest = {
            account: accounts[0],
            scopes: ["499b84ac-1321-427f-aa17-267ca6975798/user_impersonation"],
          };
          const response = await msalInstance.acquireTokenSilent(
            accessTokenRequest,
          );
          const projectName = "Aegis";

          // Determine which folder filter to use based on active tab
          let folderFilter;
          if (this.activeTab === "offboard") {
            folderFilter = "Remove Baseline Tenant Configuration";
          } else {
            folderFilter = "Deploy Baseline Tenant Configuration";
          }

          this.pipelines = await fetchPipelinesWithFolderFilter(
            response.accessToken,
            projectName,
            folderFilter,
          );

          // Clear any previous pipeline selections when switching tabs
          if (this.activeTab === "offboard") {
            this.selectedOffboardPipelineId = null;
          } else {
            this.selectedPipelineId = null;
          }
        }
      } catch (error) {
        console.error("Error fetching pipelines:", error);
        // Optionally set an error message for the UI
        const messageProperty =
          this.activeTab === "offboard" ? "offboardMessage" : "message";
        this[messageProperty] = {
          text: "Failed to fetch pipelines. Please try again.",
          type: "error",
        };
      }
    },

    async handleSubmit() {
      if (!this.validateForm()) {
        this.formError = true;
        return;
      }

      try {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length) {
          const accessTokenRequest = {
            account: accounts[0],
            scopes: ["499b84ac-1321-427f-aa17-267ca6975798/user_impersonation"],
          };
          const response = await msalInstance.acquireTokenSilent(
            accessTokenRequest,
          );
          const accessToken = response.accessToken;

          // Determine Subscription Name for $DefaultSubscriptionForLogin
          const subscriptionForLogin =
            this.hasOptionsCSP === "yes"
              ? "Options IT"
              : this.customSubscriptionName;

          const formData = {
            clientCode: this.clientCode,
            domain: this.domain,
            username: "*****",
            password: "*****",
            DefaultSubscriptionForLogin: subscriptionForLogin, // Pass correct value
          };

          console.log("Form submitted:", formData);

          const projectId = "1b238677-4a8d-4243-827e-88a0558c085a";
          const pipelineBuildId = "75";

          // Trigger the pipeline and get the run ID
          const runId = await triggerOnboardBuild(
            accessToken,
            projectId,
            pipelineBuildId,
            this.clientCode,
            this.domain,
            this.username,
            this.password,
            subscriptionForLogin, // Pass it to the pipeline
          );

          if (runId) {
            this.message.text = "Pipeline triggered successfully!";
            this.message.type = "success";

            // Start polling the pipeline status
            this.pollPipelineStatus(
              accessToken,
              projectId,
              pipelineBuildId,
              runId,
            );

            // Clear the fields
            this.clientCode = "";
            this.domain = "";
            this.username = "";
            this.password = "";
            this.hasOptionsCSP = "yes";
            this.customSubscriptionName = "";
            this.clientCodeValidated = false;
            this.formError = false;
            this.invalidFields = [];
          } else {
            this.message.text = "Failed to trigger the pipeline.";
            this.message.type = "error";
          }
        } else {
          throw new Error("No accounts available for authentication.");
        }
      } catch (error) {
        console.error("Error during form submission:", error);
        this.message.text = "Error triggering build: " + error.message;
        this.message.type = "error";
      }
    },

    async deployFullBaseline() {
      if (!this.selectedPipelineId) return;

      try {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length) {
          const accessTokenRequest = {
            account: accounts[0],
            scopes: ["499b84ac-1321-427f-aa17-267ca6975798/user_impersonation"],
          };
          const response = await msalInstance.acquireTokenSilent(
            accessTokenRequest,
          );
          const accessToken = response.accessToken;

          // Retrieve the selected pipeline
          const selectedPipeline = this.pipelines.find(
            (p) => p.id === this.selectedPipelineId,
          );
          if (!selectedPipeline) {
            throw new Error("Selected pipeline not found.");
          }
          const projectId = selectedPipeline._links.self.href.split("/")[4];

          // Trigger the pipeline and get the run ID
          const runId = await runFullBaselinePipeline(
            accessToken,
            projectId,
            this.selectedPipelineId,
          );

          // Check if the pipeline run ID exists
          if (runId) {
            this.deploymentMessage.text = "Pipeline triggered successfully!";
            this.deploymentMessage.type = "success";

            // Start polling the pipeline status
            this.pollPipelineStatus(
              accessToken,
              projectId,
              this.selectedPipelineId,
              runId,
            );
          } else {
            this.deploymentMessage.text = "Failed to trigger the pipeline.";
            this.deploymentMessage.type = "error";
          }
        } else {
          throw new Error("No accounts available for authentication.");
        }
      } catch (error) {
        console.error("Error during full baseline deployment:", error);
        this.deploymentMessage = {
          text: "Error during full baseline deployment: " + error.message,
          type: "error",
        };
      }
    },

    async deploySelectedComponents() {
      if (!this.selectedPipelineId) return;

      try {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length) {
          const accessTokenRequest = {
            account: accounts[0],
            scopes: ["499b84ac-1321-427f-aa17-267ca6975798/user_impersonation"],
          };
          const response = await msalInstance.acquireTokenSilent(
            accessTokenRequest,
          );
          const accessToken = response.accessToken;

          // Retrieve the selected pipeline
          const selectedPipeline = this.pipelines.find(
            (p) => p.id === this.selectedPipelineId,
          );
          if (!selectedPipeline) {
            throw new Error("Selected pipeline not found.");
          }
          const projectId = selectedPipeline._links.self.href.split("/")[4];

          // Convert selectedComponents array into a JSON string for the pipeline run
          const componentsJson = JSON.stringify({
            components: this.selectedComponents,
          });

          // Trigger the pipeline and get the run ID
          const runId = await runCustomBaselinePipeline(
            accessToken,
            projectId,
            this.selectedPipelineId,
            componentsJson,
          );

          // Check if the pipeline run ID exists
          if (runId) {
            this.deploymentMessage.text =
              "Selected components have started deploying . . .";
            this.deploymentMessage.type = "success";

            // Add selected components to the deployedComponents array
            this.deployedComponents = [...this.selectedComponents];

            // Start polling the pipeline status
            this.pollPipelineStatus(
              accessToken,
              projectId,
              this.selectedPipelineId,
              runId,
            );
          } else {
            this.deploymentMessage.text = "Failed to trigger the pipeline.";
            this.deploymentMessage.type = "error";
          }
        } else {
          throw new Error("No accounts available for authentication.");
        }
      } catch (error) {
        console.error("Error during component deployment:", error);
        this.deploymentMessage = {
          text: "Error during component deployment: " + error.message,
          type: "error",
        };
      }
    },

    formatFolder(folder) {
      return folder.replace("\\", "").trim();
    },

    validateForm() {
      this.invalidFields = [];
      if (!this.clientCode) this.invalidFields.push("clientCode");
      if (!this.domain) this.invalidFields.push("domain");
      if (!this.username) this.invalidFields.push("username");
      if (!this.password) this.invalidFields.push("password");
      return this.invalidFields.length === 0;
    },

    selectAll(select) {
      if (select) {
        this.selectedComponents = [
          ...Object.values(this.configurationCategories).flat(),
        ];
      } else {
        this.selectedComponents = [];
      }
    },

    toggleCategory(category, select) {
      if (select) {
        this.selectedComponents = [
          ...new Set([
            ...this.selectedComponents,
            ...this.configurationCategories[category],
          ]),
        ];
      } else {
        this.selectedComponents = this.selectedComponents.filter(
          (item) => !this.configurationCategories[category].includes(item),
        );
      }
    },

    isConditionalAccessPair(option) {
      return (
        option === "Create Conditional Access - Policies" ||
        option === "Create Conditional Access - Named Locations"
      );
    },
  },

  // Watcher to refresh pipelines when the activeTab changes
  watch: {
    onboardStep(newStep) {
      sessionStorage.setItem("onboardStep", String(newStep));
    },
    activeTab(newTab, oldTab) {
      if (oldTab === 'custom') this.saveFilterState('custom');
      else if (oldTab === 'offboard') this.saveFilterState('offboard');
      if (newTab === 'custom') this.restoreFilterState('custom');
      else if (newTab === 'offboard') this.restoreFilterState('offboard');
    },
    selectedComponents(newVal, oldVal) {
      const policies = "Create Conditional Access - Policies";
      const locations = "Create Conditional Access - Named Locations";
      const tap = "Enable Temporary Access Pass Authentication method";
      const serviceAccounts = "Create Entra ID Service Accounts";
      const requiredGroups = "Create Required Entra ID Groups";

      const addedItem = newVal.find((item) => !oldVal.includes(item));
      const removedItem = oldVal.find((item) => !newVal.includes(item));

      // Conditional Access Policies <-> Named Locations pairing
      if (addedItem === policies && !newVal.includes(locations)) {
        this.selectedComponents.push(locations);
      } else if (addedItem === locations && !newVal.includes(policies)) {
        this.selectedComponents.push(policies);
      }

      // TAP dependencies: requires both Service Accounts and Required Groups
      if (addedItem === tap) {
        if (!newVal.includes(serviceAccounts)) {
          this.selectedComponents.push(serviceAccounts);
        }
        if (!newVal.includes(requiredGroups)) {
          this.selectedComponents.push(requiredGroups);
        }
      }

      // If user tries to remove dependencies while TAP is selected, prevent it
      if (newVal.includes(tap)) {
        if (removedItem === serviceAccounts) {
          this.selectedComponents.push(serviceAccounts);
          this.$nextTick(() => {
            alert(
              'Cannot remove "Create Entra ID Service Accounts" while TAP is enabled. This creates the JIT Admin account required for TAP.',
            );
          });
        }
        if (removedItem === requiredGroups) {
          this.selectedComponents.push(requiredGroups);
          this.$nextTick(() => {
            alert(
              'Cannot remove "Create Required Entra ID Groups" while TAP is enabled. This creates the JIT Admins TAP group.',
            );
          });
        }
      }

      // Do NOT auto-re-add if one is removed
    },
    activeModalTab(newTab) {
      if (newTab === 'ai' && this.openInfoModal && !this.aiExplaining[this.openInfoModal]) {
        if (this.aiExplanations[this.openInfoModal]) return;
        if (this.configDetails[this.openInfoModal]?.rawConfig) {
          this.explainConfigWithAI(this.openInfoModal);
        }
      }
    },
  },

  mounted() {
    this.fetchPipelines(); // Fetch pipelines when the component is mounted

    // Check for saved form data and restore it
    const savedClientCode = sessionStorage.getItem("newClientCode");
    const savedHasOptionsCSP = sessionStorage.getItem("newHasOptionsCSP");
    const savedCustomSubscriptionName = sessionStorage.getItem(
      "newCustomSubscriptionName",
    );
    const savedDomain = sessionStorage.getItem("newDomain");
    const savedEnrollInPowerSupport = sessionStorage.getItem(
      "enrollInPowerSupport",
    );
    const savedRegion = sessionStorage.getItem("selectedRegion");

    if (savedClientCode) {
      this.newClientCode = savedClientCode;
      this.validateNewClientCode();
    }

    if (savedHasOptionsCSP) {
      this.newHasOptionsCSP = savedHasOptionsCSP;
    }

    if (savedCustomSubscriptionName) {
      this.newCustomSubscriptionName = savedCustomSubscriptionName;
    }

    if (savedDomain) {
      this.newDomain = savedDomain;
    }

    if (savedEnrollInPowerSupport) {
      this.enrollInPowerSupport = savedEnrollInPowerSupport;
    }

    if (savedRegion) {
      this.selectedRegion = savedRegion;
    }

    // Resume wizard position across the admin-consent page redirect
    // (startAdminConsentFlow navigates the whole page away and back)
    const savedOnboardStep = parseInt(sessionStorage.getItem("onboardStep"), 10);
    if (savedOnboardStep >= 1 && savedOnboardStep <= 3) {
      this.onboardStep = savedOnboardStep;
    }

    // Use the enhanced URL parameter handler with comprehensive error handling
    this.handleUrlParameters();
  },
  beforeUnmount() {
    if (this.activeTab === 'custom') this.saveFilterState('custom');
    else if (this.activeTab === 'offboard') this.saveFilterState('offboard');
  },
};
</script>

<style scoped>
/* Typography Scale */
:scope {
  --text-xs: 0.75rem;
  --text-sm: 0.8125rem;
  --text-base: 0.875rem;
  --text-md: 0.9375rem;
  --text-lg: 1.0625rem;
  --text-xl: 1.125rem;
  --text-2xl: 1.25rem;
  --text-3xl: 1.5rem;
  --text-4xl: 1.875rem;
  --leading-tight: 1.25;
  --leading-normal: 1.5;
  --leading-relaxed: 1.625;
}

/* Icon System — SVG background-image based (no mask, reliable rendering) */
.icon-search { display: inline-block; width: 1em; height: 1em; vertical-align: -0.125em; background: currentColor; -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='11' cy='11' r='7' fill='none' stroke='white' stroke-width='2'/%3E%3Cline x1='21' y1='21' x2='16.65' y2='16.65' stroke='white' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E") center/contain no-repeat; mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='11' cy='11' r='7' fill='none' stroke='white' stroke-width='2'/%3E%3Cline x1='21' y1='21' x2='16.65' y2='16.65' stroke='white' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E") center/contain no-repeat; }
.icon-pdf { display: inline-block; width: 1em; height: 1em; vertical-align: -0.125em; background: currentColor; -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' fill='white'/%3E%3Cpolyline points='14 2 14 8 20 8' fill='none' stroke='white' stroke-width='2'/%3E%3Cline x1='12' y1='12' x2='12' y2='18' stroke='white' stroke-width='2'/%3E%3Cline x1='9' y1='15' x2='15' y2='15' stroke='white' stroke-width='2'/%3E%3C/svg%3E") center/contain no-repeat; mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' fill='white'/%3E%3Cpolyline points='14 2 14 8 20 8' fill='none' stroke='white' stroke-width='2'/%3E%3Cline x1='12' y1='12' x2='12' y2='18' stroke='white' stroke-width='2'/%3E%3Cline x1='9' y1='15' x2='15' y2='15' stroke='white' stroke-width='2'/%3E%3C/svg%3E") center/contain no-repeat; }
.icon-csv { display: inline-block; width: 1em; height: 1em; vertical-align: -0.125em; background: currentColor; -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' fill='white'/%3E%3Cpolyline points='14 2 14 8 20 8' fill='none' stroke='white' stroke-width='2'/%3E%3Cline x1='8' y1='13' x2='16' y2='13' stroke='white' stroke-width='2'/%3E%3Cline x1='8' y1='17' x2='16' y2='17' stroke='white' stroke-width='2'/%3E%3C/svg%3E") center/contain no-repeat; mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' fill='white'/%3E%3Cpolyline points='14 2 14 8 20 8' fill='none' stroke='white' stroke-width='2'/%3E%3Cline x1='8' y1='13' x2='16' y2='13' stroke='white' stroke-width='2'/%3E%3Cline x1='8' y1='17' x2='16' y2='17' stroke='white' stroke-width='2'/%3E%3C/svg%3E") center/contain no-repeat; }
.icon-warning { display: inline-block; width: 1em; height: 1em; vertical-align: -0.125em; background: currentColor; -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' fill='white'/%3E%3Cline x1='12' y1='9' x2='12' y2='13' stroke='black' stroke-width='2'/%3E%3Ccircle cx='12' cy='17' r='0.5' fill='black'/%3E%3C/svg%3E") center/contain no-repeat; mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' fill='white'/%3E%3Cline x1='12' y1='9' x2='12' y2='13' stroke='black' stroke-width='2'/%3E%3Ccircle cx='12' cy='17' r='0.5' fill='black'/%3E%3C/svg%3E") center/contain no-repeat; }
.icon-info { display: inline-block; width: 1em; height: 1em; vertical-align: -0.125em; background: currentColor; -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='12' r='10' fill='none' stroke='white' stroke-width='2'/%3E%3Cline x1='12' y1='16' x2='12' y2='12' stroke='white' stroke-width='2'/%3E%3Ccircle cx='12' cy='8' r='0.5' fill='white'/%3E%3C/svg%3E") center/contain no-repeat; mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='12' r='10' fill='none' stroke='white' stroke-width='2'/%3E%3Cline x1='12' y1='16' x2='12' y2='12' stroke='white' stroke-width='2'/%3E%3Ccircle cx='12' cy='8' r='0.5' fill='white'/%3E%3C/svg%3E") center/contain no-repeat; }
.icon-spinner { display: inline-block; width: 1em; height: 1em; vertical-align: -0.125em; border: 2px solid currentColor; border-right-color: transparent; border-radius: 50%; animation: spin 0.8s linear infinite; }
.icon-check { display: inline-block; width: 1em; height: 1em; vertical-align: -0.125em; background: currentColor; -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpolyline points='20 6 9 17 4 12' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center/contain no-repeat; mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpolyline points='20 6 9 17 4 12' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center/contain no-repeat; }
.icon-close { display: inline-block; width: 1em; height: 1em; vertical-align: -0.125em; background: currentColor; -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cline x1='18' y1='6' x2='6' y2='18' stroke='white' stroke-width='3' stroke-linecap='round'/%3E%3Cline x1='6' y1='6' x2='18' y2='18' stroke='white' stroke-width='3' stroke-linecap='round'/%3E%3C/svg%3E") center/contain no-repeat; mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cline x1='18' y1='6' x2='6' y2='18' stroke='white' stroke-width='3' stroke-linecap='round'/%3E%3Cline x1='6' y1='6' x2='18' y2='18' stroke='white' stroke-width='3' stroke-linecap='round'/%3E%3C/svg%3E") center/contain no-repeat; }
.icon-card-view { display: inline-block; width: 1em; height: 1em; vertical-align: -0.125em; background: currentColor; -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Crect x='3' y='3' width='7' height='7' rx='1' fill='white'/%3E%3Crect x='14' y='3' width='7' height='7' rx='1' fill='white'/%3E%3Crect x='14' y='14' width='7' height='7' rx='1' fill='white'/%3E%3Crect x='3' y='14' width='7' height='7' rx='1' fill='white'/%3E%3C/svg%3E") center/contain no-repeat; mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Crect x='3' y='3' width='7' height='7' rx='1' fill='white'/%3E%3Crect x='14' y='3' width='7' height='7' rx='1' fill='white'/%3E%3Crect x='14' y='14' width='7' height='7' rx='1' fill='white'/%3E%3Crect x='3' y='14' width='7' height='7' rx='1' fill='white'/%3E%3C/svg%3E") center/contain no-repeat; }
.icon-table-view { display: inline-block; width: 1em; height: 1em; vertical-align: -0.125em; background: currentColor; -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Crect x='3' y='3' width='18' height='18' rx='2' fill='none' stroke='white' stroke-width='2'/%3E%3Cline x1='3' y1='9' x2='21' y2='9' stroke='white' stroke-width='2'/%3E%3Cline x1='3' y1='15' x2='21' y2='15' stroke='white' stroke-width='2'/%3E%3C/svg%3E") center/contain no-repeat; mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Crect x='3' y='3' width='18' height='18' rx='2' fill='none' stroke='white' stroke-width='2'/%3E%3Cline x1='3' y1='9' x2='21' y2='9' stroke='white' stroke-width='2'/%3E%3Cline x1='3' y1='15' x2='21' y2='15' stroke='white' stroke-width='2'/%3E%3C/svg%3E") center/contain no-repeat; }
.icon-delete { display: inline-block; width: 1em; height: 1em; vertical-align: -0.125em; background: currentColor; -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M3 6h18' stroke='white' stroke-width='2' stroke-linecap='round'/%3E%3Cpath d='M8 6V4h8v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6' fill='none' stroke='white' stroke-width='2'/%3E%3C/svg%3E") center/contain no-repeat; mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M3 6h18' stroke='white' stroke-width='2' stroke-linecap='round'/%3E%3Cpath d='M8 6V4h8v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6' fill='none' stroke='white' stroke-width='2'/%3E%3C/svg%3E") center/contain no-repeat; }
.icon-copy { display: inline-block; width: 1em; height: 1em; vertical-align: -0.125em; background: currentColor; -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Crect x='9' y='9' width='13' height='13' rx='2' fill='none' stroke='white' stroke-width='2'/%3E%3Cpath d='M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1' fill='none' stroke='white' stroke-width='2'/%3E%3C/svg%3E") center/contain no-repeat; mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Crect x='9' y='9' width='13' height='13' rx='2' fill='none' stroke='white' stroke-width='2'/%3E%3Cpath d='M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1' fill='none' stroke='white' stroke-width='2'/%3E%3C/svg%3E") center/contain no-repeat; }
.icon-cleanup { display: inline-block; width: 1em; height: 1em; vertical-align: -0.125em; background: currentColor; -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M3 6h18' stroke='white' stroke-width='2'/%3E%3Cpath d='M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' fill='none' stroke='white' stroke-width='2'/%3E%3C/svg%3E") center/contain no-repeat; mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M3 6h18' stroke='white' stroke-width='2'/%3E%3Cpath d='M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' fill='none' stroke='white' stroke-width='2'/%3E%3C/svg%3E") center/contain no-repeat; }
.icon-time { display: inline-block; width: 1em; height: 1em; vertical-align: -0.125em; background: currentColor; -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='12' r='10' fill='none' stroke='white' stroke-width='2'/%3E%3Cpolyline points='12 6 12 12 16 14' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center/contain no-repeat; mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='12' r='10' fill='none' stroke='white' stroke-width='2'/%3E%3Cpolyline points='12 6 12 12 16 14' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center/contain no-repeat; }
.icon-danger { display: inline-block; width: 1em; height: 1em; vertical-align: -0.125em; background: currentColor; -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='12' r='10' fill='none' stroke='white' stroke-width='2'/%3E%3Cline x1='15' y1='9' x2='9' y2='15' stroke='white' stroke-width='2' stroke-linecap='round'/%3E%3Cline x1='9' y1='9' x2='15' y2='15' stroke='white' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E") center/contain no-repeat; mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='12' r='10' fill='none' stroke='white' stroke-width='2'/%3E%3Cline x1='15' y1='9' x2='9' y2='15' stroke='white' stroke-width='2' stroke-linecap='round'/%3E%3Cline x1='9' y1='9' x2='15' y2='15' stroke='white' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E") center/contain no-repeat; }

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@keyframes dangerPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

/* Main Container */
.deployment-container {
  padding: 1.5rem;
  background-color: #f8fafc;
  color: #1e293b;
  min-height: calc(100vh - 64px);
}

/* Main Content */
.main-content {
  background-color: #ffffff;
  padding: 2rem;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  max-width: 1200px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

/* Typography */
.baseline-text {
  color: #1e293b;
  font-weight: 500;
  margin-bottom: 1rem;
  line-height: 1.6;
}

h2.baseline-text {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
  font-size: var(--text-4xl);
  font-weight: 700;
  color: #1e293b;
  line-height: var(--leading-tight);
  letter-spacing: -0.02em;
}

p.baseline-text {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
  font-size: var(--text-base);
  font-weight: 400;
  color: #475569;
  line-height: var(--leading-relaxed);
  max-width: 65ch;
}

/* Forms */
.form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

label {
  color: #475569;
  font-size: 0.875rem;
  font-weight: 500;
  display: block;
}

/* Form Inputs */
.onboard-input,
select {
  width: 100%;
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  background-color: #ffffff;
  color: #1e293b;
  transition: all 0.2s;
  font-family: inherit;
}

.onboard-input:focus,
select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  background-color: #ffffff;
}

.onboard-input::placeholder {
  color: #94a3b8;
}

.onboard-input[readonly] {
  background-color: #f8fafc;
  cursor: not-allowed;
  opacity: 0.7;
}

.is-invalid {
  border-color: #ef4444 !important;
}

.is-invalid:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
}

/* Validation Messages */
.validation-messages {
  margin-top: 0.25rem;
}

.error-message {
  color: #ef4444;
  font-size: 0.75rem;
}

.success-message {
  color: #16a34a;
  font-size: 0.75rem;
}

/* Buttons */
.primary-button {
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  border: none;
  border-radius: 0.375rem;
  background-color: #3b82f6;
  color: white;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.primary-button:hover:not(:disabled) {
  background-color: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.primary-button:disabled {
  background-color: #94a3b8;
  cursor: not-allowed;
  opacity: 0.6;
  transform: none;
}

/* Tab Navigation */
.tab-header {
  display: flex;
  gap: 0.25rem;
  margin-bottom: 2rem;
  background-color: #f8fafc;
  padding: 0.25rem;
  border-radius: 0.75rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid #e2e8f0;
}

.tab-header button {
  padding: 0.75rem 1.5rem;
  background: none;
  border: none;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 0.5rem;
  white-space: nowrap;
  font-family: inherit;
}

.tab-header button:hover {
  color: #1e293b;
  background-color: #e2e8f0;
}

.tab-header button.active-tab {
  color: #fff;
  background-color: #3b82f6;
}

/* Tab Content Window */
.tab-content-window {
  background: #ffffff;
  padding: 1.5rem;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

/* Tabs */
.tabs {
  display: flex;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.25rem;
  margin-bottom: 1rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.tab {
  flex: 1;
  padding: 0.75rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: none;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  min-width: 120px;
  border-radius: 0.375rem;
  font-family: inherit;
}

.tab:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.tab.active {
  background: #3b82f6;
  color: white;
}

/* Tab Content */
.tab-content {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Config Cards */
.config-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.config-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.5rem;
  transition: all 0.2s;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.config-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
  transform: translateY(-100%);
  transition: transform 0.2s;
}

.config-card:hover::before {
  transform: translateY(0);
}

.config-card:hover {
  border-color: #cbd5e1;
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.config-card.selected {
  border-color: #3b82f6;
  background: #eff6ff;
}

.config-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.config-checkbox {
  margin-top: 0.2rem;
  transform: scale(1.2);
  flex-shrink: 0;
  cursor: pointer;
}

.config-info {
  flex: 1;
}

.config-title {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.4;
}

.config-description {
  color: #64748b;
  font-size: 0.8125rem;
  line-height: 1.5;
  margin-bottom: 0.75rem;
}

.config-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.impact-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.impact-high {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.impact-medium {
  background: rgba(251, 146, 60, 0.1);
  color: #f97316;
}

.impact-low {
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
}

.time-estimate,
.dependency-indicator {
  color: #94a3b8;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
}

.info-button-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  padding: 0.375rem;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: #64748b;
  width: 2rem;
  height: 2rem;
}

.info-button-card:hover:not(:disabled) {
  background: #eff6ff;
  border-color: #3b82f6;
  color: #3b82f6;
}

.info-button-card:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.info-icon-svg {
  display: block;
}

.spinner {
  animation: spin 1s linear infinite;
  display: inline-block;
}

/* Action Bar */
.action-bar {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

.selection-summary {
  color: #64748b;
  font-size: 0.875rem;
}

.selection-count {
  font-weight: 600;
  color: #3b82f6;
}

.action-buttons {
  display: flex;
  gap: 0.75rem;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  font-family: inherit;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.btn-primary:disabled {
  background: #94a3b8;
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-secondary {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover {
  background: #e2e8f0;
  transform: translateY(-1px);
}

.btn-danger {
  background-color: #ef4444;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background-color: #dc2626;
  transform: translateY(-1px);
}

/* Deployed Components */
.deployed-components-section {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-top: 2rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

.deployed-components-section h3 {
  color: #1e293b;
  font-size: 1.125rem;
  margin-bottom: 1rem;
}

.deployed-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 0.5rem;
}

.deployed-item {
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  border: 1px solid rgba(34, 197, 94, 0.2);
}

/* Off-board specific */
.config-card-offboard {
  border-left: 3px solid #ef4444;
}

.removed-components {
  background-color: rgba(239, 68, 68, 0.05);
  border-color: rgba(239, 68, 68, 0.2);
}

.removed-item {
  background-color: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

/* Warning Banner */
.warning-banner {
  background-color: rgba(251, 146, 60, 0.1);
  border: 1px solid rgba(251, 146, 60, 0.2);
  border-radius: 0.5rem;
  padding: 1rem;
  margin: 1.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.warning-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
  color: #f97316;
}

.warning-content {
  color: #f97316;
  font-size: 0.875rem;
}

.warning-content strong {
  color: #ea580c;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content,
.confirmation-modal {
  background: #ffffff;
  border-radius: 0.75rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 80vw;
  max-height: 90vh;
  width: 100%;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.confirmation-modal {
  max-width: 500px;
  padding: 1.5rem;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  background-color: #f8fafc;
}

.modal-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.375rem;
  color: #94a3b8;
  transition: all 0.2s;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover {
  background-color: #e2e8f0;
  color: #475569;
}

.modal-tabs {
  display: flex;
  border-bottom: 1px solid #e2e8f0;
  background-color: #f8fafc;
}

.modal-tabs button {
  padding: 0.75rem 1.5rem;
  border: none;
  background: none;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
  color: #64748b;
  font-weight: 500;
}

.modal-tabs button.active {
  border-bottom-color: #3b82f6;
  color: #3b82f6;
}

.modal-tabs button:hover {
  background-color: #e2e8f0;
  color: #1e293b;
}

.modal-body {
  padding: 1.5rem;
  max-height: 60vh;
  overflow-y: auto;
  background-color: #ffffff;
}

.loading-state,
.error-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #64748b;
}

.error-state {
  color: #ef4444;
}

.tab-content-modal {
  animation: fadeIn 0.2s ease-in;
}

.overview-section {
  margin-bottom: 1.5rem;
}

.overview-section h3 {
  font-size: 1.125rem;
  font-weight: 500;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.overview-section p {
  color: #64748b;
  line-height: 1.6;
}

.overview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.overview-item h4 {
  font-weight: 500;
  color: #94a3b8;
  margin-bottom: 0.25rem;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.8125rem;
  font-weight: 500;
  background-color: #f1f5f9;
  color: #3b82f6;
  border: 1px solid #e2e8f0;
}

.type-badge {
  background-color: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  border-color: rgba(59, 130, 246, 0.2);
}

.path-code {
  font-family: "Monaco", "Menlo", monospace;
  background-color: #f1f5f9;
  padding: 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  border: 1px solid #e2e8f0;
  display: block;
  word-break: break-all;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s;
}

.path-code:hover {
  background-color: #e2e8f0;
  color: #1e293b;
}

.settings-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.setting-item {
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem;
  background-color: #f8fafc;
}

.setting-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
}

.setting-header h4 {
  font-weight: 500;
  color: #1e293b;
}

.setting-value {
  text-align: right;
}

.setting-value .value {
  font-family: "Monaco", "Menlo", monospace;
  background-color: #f1f5f9;
  padding: 0.25rem 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  display: block;
  color: #3b82f6;
}

.setting-value .property {
  color: #94a3b8;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  display: block;
}

.setting-description {
  color: #64748b;
  font-size: 0.8125rem;
  line-height: 1.4;
}

.raw-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.raw-header h3 {
  font-size: 1.125rem;
  font-weight: 500;
  color: #1e293b;
}

.copy-button {
  padding: 0.5rem 0.75rem;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.8125rem;
  transition: all 0.2s;
  color: #475569;
}

.copy-button:hover {
  background-color: #e2e8f0;
  transform: translateY(-1px);
  color: #1e293b;
}

.raw-header-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.raw-expand-btn {
  padding: 0.375rem 0.625rem;
  background-color: transparent;
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 0.25rem;
  cursor: pointer;
  font-size: 0.75rem;
  color: #8b9dc3;
  transition: all 0.15s;
  font-family: inherit;
}

.raw-expand-btn:hover {
  background-color: rgba(255,255,255,0.08);
  color: #c4d4ef;
}

.console-json-viewer {
  background-color: #1a1f2e;
  border: 1px solid #2d3548;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  max-height: 400px;
  overflow: auto;
  font-family: "Monaco", "Menlo", "Consolas", "Courier New", monospace;
  font-size: 0.8125rem;
  line-height: 1.65;
}

.json-tree-row {
  display: flex;
  align-items: flex-start;
  white-space: nowrap;
  min-height: 1.5rem;
}

.json-toggle {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 2px;
  margin-top: 1px;
}

.json-toggle-spacer {
  visibility: hidden;
}

.json-toggle-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-size: 8px;
  color: #6b7d99;
  line-height: 1;
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  transition: color 0.1s;
  font-family: inherit;
}

.json-toggle-btn:hover {
  color: #c4d4ef;
  background-color: rgba(255,255,255,0.05);
}

.json-toggle-btn:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 1px;
}

.json-key {
  color: #79c0ff;
  margin-right: 0.25rem;
  flex-shrink: 0;
}

.json-bracket {
  color: #c4d4ef;
  flex-shrink: 0;
}

.json-value {
  flex-shrink: 0;
}

.json-string {
  color: #a5d6a7;
}

.json-number {
  color: #ffa657;
}

.json-boolean {
  color: #ff7b72;
}

.json-null {
  color: #8b949e;
  font-style: italic;
}

.json-preview {
  color: #8b9dc3;
  font-style: italic;
  flex-shrink: 0;
}

.json-closing {
  margin-left: 0.25rem;
}

.components-to-remove {
  background-color: #fef2f2;
  border-radius: 0.5rem;
  padding: 1rem;
  margin: 1rem 0;
  border: 1px solid #fecaca;
}

.components-to-remove h4 {
  margin-top: 0;
  margin-bottom: 0.75rem;
  color: #ef4444;
}

.components-to-remove ul {
  margin: 0;
  padding-left: 1.25rem;
  max-height: 200px;
  overflow-y: auto;
}

.components-to-remove li {
  margin: 0.25rem 0;
  color: #dc2626;
}

.modal-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
}

.confirmation-modal h3 {
  margin-top: 0;
  color: #ef4444;
  font-size: 1.5rem;
  margin-bottom: 1rem;
}

.confirmation-modal p {
  color: #64748b;
  margin: 1rem 0;
}

/* Scrollbar Styling */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Messages */
.message {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  border: 1px solid;
}

.message.success {
  background-color: rgba(34, 197, 94, 0.1);
  color: #16a34a;
  border-color: rgba(34, 197, 94, 0.2);
}

.message.error {
  background-color: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border-color: rgba(239, 68, 68, 0.2);
}

.message.status-info {
  background-color: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  border-color: rgba(59, 130, 246, 0.2);
}

/* Pipeline Container */
.pipeline-container {
  background-color: #ffffff;
  padding: 1.5rem;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  margin-top: 2rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

.pipeline-container label {
  margin-bottom: 0.5rem;
  display: block;
  color: #475569;
}

.pipeline-container select {
  margin-bottom: 1rem;
}

/* Pipeline Selection */
.pipeline-selection {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

/* Subscription Input */
.subscription-input {
  margin-top: 1rem;
  padding: 1rem;
  background-color: #f8fafc;
  border-radius: 0.375rem;
  border: 1px solid #e2e8f0;
}

.subscription-input label {
  margin-bottom: 0.5rem;
  color: #475569;
}

/* Role Status Container */
.role-status-container {
  background-color: #f8fafc;
  border-radius: 0.5rem;
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
}

.role-status {
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  border-left: 3px solid transparent;
  transition: all 0.2s;
}

.role-status.text-green-600 {
  background-color: rgba(34, 197, 94, 0.1);
  border-left-color: #16a34a;
  color: #16a34a;
}

.role-status.text-red-600 {
  background-color: rgba(239, 68, 68, 0.1);
  border-left-color: #ef4444;
  color: #ef4444;
}

/* Success/Error Text */
.text-green-600 {
  color: #16a34a;
  background-color: rgba(34, 197, 94, 0.1);
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  border: 1px solid rgba(34, 197, 94, 0.2);
}

.text-red-600 {
  color: #ef4444;
}

/* Margin Utilities */
.mt-6 {
  margin-top: 1.5rem;
}
.mt-4 {
  margin-top: 1rem;
}
.mt-3 {
  margin-top: 0.75rem;
}
.mt-2 {
  margin-top: 0.5rem;
}
.mb-4 {
  margin-bottom: 1rem;
}
.mb-2 {
  margin-bottom: 0.5rem;
}

/* Custom Deployment Section */
.custom-deployment-container {
  max-width: 100%;
}

/* Pipeline Section */
.pipeline-section {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

.section-header {
  margin-bottom: 1rem;
}

.section-header h3 {
  color: #1e293b;
  font-size: var(--text-xl);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  letter-spacing: -0.01em;
}

.section-header h3::before {
  font-size: 1.25rem;
}

.pipeline-dropdown {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  background: #ffffff;
  color: #1e293b;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.pipeline-dropdown:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Quick Actions */
.quick-actions {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.quick-action {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

.quick-action:hover {
  transform: translateY(-2px);
  border-color: #3b82f6;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.quick-action-icon {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.quick-action-title {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #1e293b;
}

.quick-action-desc {
  color: #64748b;
  font-size: 0.875rem;
}

/* Service Tabs */
.service-tabs {
  display: flex;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 0.25rem;
  margin-bottom: 2rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.service-tab {
  flex: 1;
  padding: 0.75rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: none;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  min-width: 120px;
  border-radius: 0.5rem;
  font-family: inherit;
}

.service-tab:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.service-tab.active {
  background: #3b82f6;
  color: white;
}

/* Configuration Grid */
.config-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .deployment-container {
    padding: 1rem;
  }

  .tab-content-window {
    padding: 1rem;
  }

  .config-grid {
    grid-template-columns: 1fr;
  }

  .overview-grid {
    grid-template-columns: 1fr;
  }

  .quick-actions {
    grid-template-columns: 1fr;
  }

  .action-bar {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }

  .action-buttons {
    justify-content: center;
  }

  .modal-content {
    max-width: 95vw;
  }
}

/* Full width fields */
.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group.medium-width {
  grid-column: span 2;
}

/* Form grid wrapper */
.form-grid-wrapper {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem 1.5rem;
  margin-bottom: 1.5rem;
}

/* Improve label styling */
.global-admin-section label {
  color: #475569;
  font-weight: 500;
  margin-bottom: 0.375rem;
  font-size: 0.8125rem;
  min-height: 2.5rem;
  display: flex;
  align-items: flex-end;
}

/* Better input field styling */
.global-admin-section .onboard-input {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  transition: all 0.2s;
  height: 2.5rem;
}

.global-admin-section .onboard-input:focus {
  background-color: #ffffff;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.global-admin-section select {
  height: 2.5rem;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.75rem center;
  background-repeat: no-repeat;
  background-size: 1.25rem;
  padding-right: 2.5rem;
}

/* Placeholder text styling */
.global-admin-section .onboard-input::placeholder {
  color: #94a3b8;
  font-style: italic;
}

/* Readonly field styling */
.global-admin-section .onboard-input[readonly] {
  background-color: #f8fafc;
  opacity: 0.7;
  cursor: not-allowed;
  color: #94a3b8;
}

/* Primary button enhancement */
.global-admin-section .primary-button {
  background-color: #3b82f6;
  color: white;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 0.375rem;
  transition: all 0.2s;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.global-admin-section .primary-button:hover {
  background-color: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.global-admin-section .primary-button:active {
  transform: translateY(0);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Popup Warning */
.popup-warning {
  background-color: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.popup-warning .warning-icon {
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.popup-warning .warning-text strong {
  color: #92400e;
  font-weight: 600;
  display: block;
  margin-bottom: 0.25rem;
}

.popup-warning .warning-text p {
  color: #78350f;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
}

.admin-note {
  color: #475569;
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

/* Export Section */
.export-section {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

.export-section .section-header h3::before {
  font-size: 1.25rem;
  margin-right: 0.5rem;
}

.export-description {
  color: #64748b;
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

.export-buttons {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.btn-export {
  padding: 0.625rem 1.25rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-export:hover {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.btn-export:active {
  transform: translateY(0);
}

/* Export Options */
.export-options {
  margin-bottom: 1rem;
  padding: 1rem;
  background-color: #f8fafc;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
}

.export-option-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: #475569;
  font-size: 0.875rem;
}

.export-option-checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.export-note {
  margin-top: 0.5rem;
  margin-left: 1.625rem;
  color: #f97316;
  font-size: 0.8125rem;
  font-style: italic;
}

.export-progress {
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 0.375rem;
  color: #3b82f6;
  font-size: 0.875rem;
  text-align: center;
}

.btn-export:disabled {
  background-color: #94a3b8;
  cursor: not-allowed;
  opacity: 0.6;
}

/* AI Explanation Styles */
.ai-container {
  padding: 0.5rem;
}

.ai-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #eff6ff 0%, #faf5ff 100%);
  border: 1px solid #e0e7ff;
  border-radius: 0.75rem;
}

.ai-header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.ai-header-icon {
  color: #6366f1;
  flex-shrink: 0;
}

.ai-header h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  line-height: 1.3;
}

.ai-header-sub {
  font-size: 0.75rem;
  color: #6366f1;
  margin: 0.125rem 0 0 0;
  font-weight: 500;
}

.ai-explain-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.125rem;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(99, 102, 241, 0.3);
}

.ai-explain-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  box-shadow: 0 2px 6px rgba(99, 102, 241, 0.4);
  transform: translateY(-1px);
}

.ai-explain-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ai-loading {
  padding: 2rem 1.5rem;
}

.ai-loading-label {
  text-align: center;
  color: #6366f1;
  font-size: 0.875rem;
  font-weight: 500;
  margin-top: 1.5rem;
}

.ai-skeleton {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.skeleton-block {
  background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s ease-in-out infinite;
  border-radius: 0.375rem;
  height: 0.875rem;
}

.skeleton-title {
  height: 1.5rem;
  width: 45%;
  border-radius: 0.5rem;
}

.skeleton-heading {
  height: 1.125rem;
  width: 35%;
  margin-top: 0.5rem;
}

.skeleton-text {
  width: 100%;
}

.skeleton-text-short {
  width: 65%;
}

.skeleton-text-medium {
  width: 80%;
}

@keyframes skeleton-shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.ai-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2.5rem 2rem;
  text-align: center;
  color: #dc2626;
  font-size: 0.9375rem;
}

.ai-retry-btn {
  padding: 0.5rem 1.25rem;
  background: #fff;
  color: #dc2626;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.ai-retry-btn:hover {
  background: #fef2f2;
  border-color: #fca5a5;
}

.ai-explanation {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.ai-cached-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  margin: 1rem 1.25rem 0;
  padding: 0.375rem 0.75rem;
  background: #f0fdf4;
  color: #065f46;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 0.5rem;
  border: 1px solid #bbf7d0;
}

.ai-explanation-body {
  padding: 1rem 1.25rem 0.5rem;
  color: #1e293b;
  font-size: 0.9375rem;
  line-height: 1.7;
}

.ai-explanation-body :deep(h2) {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin: 1.25rem 0 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.ai-explanation-body :deep(h2:first-child) {
  margin-top: 0;
}

.ai-explanation-body :deep(h3) {
  font-size: 1.0625rem;
  font-weight: 600;
  color: #334155;
  margin: 1rem 0 0.5rem;
}

.ai-explanation-body :deep(h4) {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #475569;
  margin: 0.75rem 0 0.375rem;
}

.ai-explanation-body :deep(p) {
  margin: 0 0 0.75rem;
  color: #334155;
}

.ai-explanation-body :deep(ul),
.ai-explanation-body :deep(ol) {
  margin: 0.5rem 0 1rem;
  padding-left: 1.5rem;
}

.ai-explanation-body :deep(li) {
  margin-bottom: 0.375rem;
  color: #334155;
}

.ai-explanation-body :deep(strong) {
  color: #0f172a;
  font-weight: 600;
}

.ai-explanation-body :deep(code) {
  background: #f1f5f9;
  color: #6366f1;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.875em;
  font-family: 'SF Mono', 'Fira Code', monospace;
}

.ai-actions {
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-top: 1px solid #f1f5f9;
  background: #fafbfc;
}

.ai-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.4375rem 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: #fff;
}

.ai-copy-btn {
  color: #475569;
}

.ai-copy-btn:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #1e293b;
}

.ai-regenerate-btn {
  color: #6366f1;
  border-color: #e0e7ff;
}

.ai-regenerate-btn:hover {
  background: #eef2ff;
  border-color: #c7d2fe;
  color: #4f46e5;
}

.ai-initial-state {
  text-align: center;
  padding: 2.5rem 2rem;
}

.ai-initial-icon {
  margin-bottom: 1.25rem;
  color: #6366f1;
  opacity: 0.85;
}

.ai-initial-state h4 {
  font-size: 1.0625rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.5rem;
}

.ai-initial-state > p {
  color: #64748b;
  font-size: 0.875rem;
  line-height: 1.6;
  margin: 0 auto 1.5rem;
  max-width: 380px;
}

.ai-initial-features {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.625rem;
  margin: 0 auto;
  max-width: 260px;
}

.ai-feature-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 0.8125rem;
  color: #475569;
}

.modal-tabs button.ai-tab {
  color: #16a34a;
  font-weight: 600;
}

.modal-tabs button.ai-tab:hover {
  background-color: #f0fdf4;
  color: #15803d;
}

.modal-tabs button.ai-tab.active {
  border-bottom-color: #16a34a;
  color: #16a34a;
  background-color: #f0fdf4;
}

/* Quick Action Presets */
.quick-action-greenfield {
  border-color: rgba(34, 197, 94, 0.3);
  position: relative;
}
.quick-action-greenfield:hover {
  border-color: #22c55e;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.15);
}
.quick-action-brownfield {
  border-color: rgba(217, 119, 6, 0.3);
  position: relative;
}
.quick-action-brownfield:hover {
  border-color: #d97706;
  box-shadow: 0 4px 12px rgba(217, 119, 6, 0.15);
}
.quick-action-badge {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  font-size: var(--text-xs);
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.badge-green {
  background: rgba(34, 197, 94, 0.15);
  color: #15803d;
}
.badge-amber {
  background: rgba(217, 119, 6, 0.15);
  color: #92400e;
}
.quick-action-count {
  display: inline-block;
  margin-top: 0.5rem;
  font-size: var(--text-xs);
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
}
.quick-actions.two-column {
  grid-template-columns: repeat(2, 1fr);
}

/* Category Status Indicators */
.category-status {
  margin-bottom: 1rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  font-size: var(--text-sm);
  display: flex;
  align-items: center;
  gap: 0.375rem;
}
.category-status-all {
  color: #16a34a;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
.category-status-partial {
  color: #64748b;
}
.category-status-offboard {
  background: rgba(239, 68, 68, 0.05);
  border: 1px solid rgba(239, 68, 68, 0.1);
}

/* Admin Access Card */
.admin-access-card {
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;
  align-items: flex-start;
}
.admin-access-icon {
  color: #3b82f6;
  flex-shrink: 0;
}
.admin-access-content strong {
  display: block;
  color: #1e40af;
  font-weight: 600;
  margin-bottom: 0.25rem;
  font-size: var(--text-sm);
}
.admin-access-content p {
  color: #3b82f6;
  font-size: var(--text-sm);
  margin: 0;
  line-height: var(--leading-normal);
}

/* Auth CTA Card */
.auth-cta-card {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #bfdbfe;
  border-radius: 0.75rem;
  overflow: hidden;
  margin-bottom: 1.5rem;
}
.auth-cta-header {
  display: flex;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  align-items: center;
  color: #1e40af;
  border-bottom: 1px solid #bfdbfe;
}
.auth-cta-header strong {
  display: block;
  font-size: var(--text-md);
  color: #1e40af;
}
.auth-cta-header p {
  margin: 0;
  font-size: var(--text-sm);
  color: #3b82f6;
}
.auth-cta-body {
  padding: 1.5rem;
}
.auth-success {
  margin: 0.75rem 0;
  padding: 0.5rem 0.75rem;
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: 0.375rem;
  color: #16a34a;
  font-size: var(--text-sm);
  display: flex;
  align-items: center;
  gap: 0.375rem;
}
.auth-cta-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: var(--text-base);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}
.auth-cta-button:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}
.auth-cta-button:disabled {
  background: #94a3b8;
  cursor: not-allowed;
  opacity: 0.6;
}
.auth-warning {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 0.375rem;
  color: #92400e;
  font-size: var(--text-sm);
  margin-bottom: 1rem;
}

/* Role Status */
.role-status-success {
  background: rgba(34, 197, 94, 0.1);
  border-left: 3px solid #16a34a;
  color: #16a34a;
}
.role-status-error {
  background: rgba(239, 68, 68, 0.1);
  border-left: 3px solid #ef4444;
  color: #ef4444;
}

/* Focus Visible */
.tab-header button:focus-visible,
.service-tab:focus-visible,
.impact-filter-btn:focus-visible,
.view-toggle-btn:focus-visible,
.search-clear:focus-visible,
.modal-tabs button:focus-visible,
.close-button:focus-visible,
.btn:focus-visible,
.primary-button:focus-visible,
.info-button-card:focus-visible,
.quick-action:focus-visible,
.btn-export:focus-visible,
.wizard-step.clickable:focus-visible,
.banner-dismiss:focus-visible,
.auth-cta-button:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* Modal sizing */
.modal-content {
  max-width: 1100px;
  max-height: 90vh;
}

/* Settings Row */
.settings-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 2rem;
}
.settings-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
  margin-bottom: 0;
}

/* Pipeline Offboard */
.pipeline-section-offboard {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

/* Pipeline section (wrapped in settings-row) */
.settings-card.pipeline-section {
  box-shadow: none;
  margin-bottom: 0;
}
.settings-card.export-section {
  box-shadow: none;
  margin-bottom: 0;
}

/* Remove dead ::before */
.section-header h3::before {
  content: none;
}

/* Service tabs gradient fade */
.service-tabs::after {
  content: '';
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 3rem;
  background: linear-gradient(to right, transparent, #f8fafc);
  pointer-events: none;
}
.service-tabs {
  position: relative;
}

/* Banner Dismiss */
.banner-dismiss {
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: #92400e;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  flex-shrink: 0;
  line-height: 1;
  transition: all 0.15s;
}
.banner-dismiss:hover {
  background: rgba(251, 191, 36, 0.3);
  color: #78350f;
}

/* Search and Filter Bar */
.search-filter-bar {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  align-items: center;
  flex-wrap: wrap;
}
.search-input-wrapper {
  position: relative;
  flex: 1;
  min-width: 200px;
}
.search-icon {
  position: absolute;
  left: 0.625rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
}
.search-input-field {
  width: 100%;
  padding: 0.5rem 2rem 0.5rem 2rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  font-size: var(--text-sm);
  font-family: inherit;
  background: #ffffff;
  color: #1e293b;
  transition: border-color 0.2s;
}
.search-input-field:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}
.search-clear {
  position: absolute;
  right: 0.375rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  padding: 0.25rem;
  border-radius: 0.25rem;
}
.search-clear:hover {
  color: #475569;
  background: #e2e8f0;
}
.filter-toggle-group {
  display: flex;
  gap: 0.125rem;
  background: #f1f5f9;
  border-radius: 0.375rem;
  padding: 0.125rem;
}
.impact-filter-btn {
  padding: 0.375rem 0.625rem;
  border: none;
  background: none;
  cursor: pointer;
  font-size: var(--text-xs);
  font-weight: 500;
  color: #64748b;
  border-radius: 0.25rem;
  transition: all 0.15s;
  font-family: inherit;
}
.impact-filter-btn:hover {
  color: #1e293b;
}
.impact-filter-btn.active {
  background: #ffffff;
  color: #1e293b;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.view-toggle-group {
  display: flex;
  gap: 0;
  background: #f1f5f9;
  border-radius: 0.375rem;
  padding: 0.125rem;
}
.view-toggle-btn {
  padding: 0.375rem 0.5rem;
  border: none;
  background: none;
  cursor: pointer;
  color: #94a3b8;
  border-radius: 0.25rem;
  transition: all 0.15s;
}
.view-toggle-btn:hover {
  color: #1e293b;
}
.view-toggle-btn.active {
  background: #ffffff;
  color: #3b82f6;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

/* Config Table */
.config-table-container {
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  overflow: hidden;
  margin-bottom: 2rem;
  background: #ffffff;
}
.config-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-sm);
}
.config-table thead {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}
.config-table th {
  padding: 0.625rem 0.75rem;
  text-align: left;
  font-weight: 600;
  color: #475569;
  font-size: var(--text-xs);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.table-col-checkbox {
  width: 3rem;
}
.config-table td {
  padding: 0.625rem 0.75rem;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
}
.config-table tbody tr:hover {
  background: #f8fafc;
}
.config-table .selected-row {
  background: #eff6ff;
}
.table-config-name {
  font-weight: 600;
  color: #1e293b;
}
.table-config-desc {
  font-size: var(--text-xs);
  color: #94a3b8;
  margin-top: 0.125rem;
}
.config-empty-state {
  padding: 3rem 1.5rem;
  text-align: center;
}
.empty-state-icon {
  margin-bottom: 0.75rem;
}
.empty-state-title {
  font-weight: 600;
  color: #475569;
  font-size: var(--text-base);
  margin-bottom: 0.25rem;
}
.empty-state-desc {
  color: #94a3b8;
  font-size: var(--text-sm);
}

/* Wizard Styles */
.wizard-panel {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}
.wizard-panel-title {
  font-size: var(--text-lg);
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
}
.wizard-steps-summary {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}
.wizard-step-summary {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  transition: all 0.2s;
}
.wizard-step-summary:hover {
  background: #f8fafc;
  border-color: #3b82f6;
}
.wizard-step-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #3b82f6;
  color: white;
  font-weight: 600;
  font-size: var(--text-sm);
  flex-shrink: 0;
}
.wizard-step-summary strong {
  display: block;
  color: #1e293b;
  font-size: var(--text-sm);
}
.wizard-step-summary p {
  margin: 0;
  color: #64748b;
  font-size: var(--text-xs);
}
.wizard-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
  gap: 0.75rem;
}

/* Animations */
.modal-overlay {
  animation: modalFadeIn 0.2s ease-out;
}
.modal-content {
  animation: modalSlideUp 0.25s ease-out;
}
.config-card {
  animation: cardFadeIn 0.3s ease-out both;
}

@keyframes modalFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes modalSlideUp {
  from { opacity: 0; transform: translateY(20px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes cardFadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>
